import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { fetchAllPages } from './paginate';
import { nativeStopRiderTracking } from '@/lib/bhooklyNative';
import type {
  HRAdvance,
  HRCandidate,
  HREmployee,
  HREmployeeInput,
  HRHoliday,
  HRImportRow,
  HRLeaveBalances,
  HRLeaveEntry,
  HRLeaveType,
  HRMarkEntry,
  HRMonth,
  HROverview,
  HRPayout,
  HRPayoutMethod,
  HRRate,
  HRRecoveryPlan,
  HRRegisterDay,
  HRRule,
  HRRunDetail,
  HRRunListItem,
  HRSettings,
  HRStatement,
  HRYearEndRow,
} from '@/types/hr';
import type {
  PrepRecipe,
  PrepRecipePayload,
  PrepIngredientOption,
  ProductionBatch,
  ProductionBatchStatus,
  CompleteBatchResult,
  ProductionBatchMovement,
  DemandLineInput,
  DemandPreview,
  KitchenPrepItem,
} from '@/types/kitchenFlowPrep';
import type {
  DispatchBoard,
  HandinRequest,
  HandinResult,
  RiderCash,
  RiderCollectRequest,
  RiderMe,
  RiderProgressRequest,
  RiderLocationPoint,
  TrailPoint,
} from '@/types/rider';
import type { RecipeCard, RecipeCardIndex, RecipeMethod, RecipeMethodPayload } from '@/lib/recipeCard/recipeCardTypes';
import type { CateringLineStatus, CateringTicket } from '@/lib/cateringKds/cateringKdsTypes';
import type {
  APIResponse,
  PaginatedResponse,
  LoginRequest,
  LoginResponse,
  User,
  Product,
  ProductCostingHealth,
  ProductRecipe,
  OptionRecipe,
  ProductOptionOverride,
  StockItemUsage,
  Category,
  Tag,
  ModifierGroup,
  ModifierOption,
  ModifierOptionGroupRule,
  ResolvedModifierGroup,
  ProductModifierConfig,
  ModifierCategoryPriceImpact,
  ModifierAttachments,
  DiningTable,
  Order,
  OnlineOrder,
  ActiveOnlineOrder,
  OnlineOrderEditRequest,
  OnlineOrderEditPreview,
  OnlineOrderingStatus,
  SetOnlineOrderingStatusRequest,
  CancelOnlineOrderRequest,
  CancelOnlineOrderResult,
  StationKOT,
  OfflineReplayRequest,
  UnreconciledOfflineItem,
  Payment,
  CreateOrderRequest,
  OpenCounterTableTabRequest,
  UpdateOrderStatusRequest,
  ProcessPaymentRequest,
  PaymentSummary,
  DashboardStats,
  TableStatus,
  OrderFilters,
  UpdateCounterOrderGuestRequest,
  UpdateCounterOrderServiceRequest,
  ProductFilters,
  TableFilters,
  StockCategory,
  StockItem,
  StockItemBarcode,
  BarcodeMatch,
  BarcodeExternalInfo,
  StockLotsResponse,
  ExpirySettings,
  WriteOffLotResult,
  UndatedLot,
  StockImportResult,
  PurchaseOrderAttachment,
  StockMovement,
  InventoryActivityEntry,
  StockAlert,
  ExpiringBatchAlert,
  UserBrief,
  StockSummary,
  AdvancedStockReport,
  StockReconciliationReport,
  Supplier,
  PurchaseOrderSummary,
  PurchaseOrderDetail,
  PoReceiveResult,
  QuickPurchaseResult,
  VendorBill,
  VendorLedgerEntry,
  Expense,
  DailyClosing,
  PnLReport,
  CurrentDayStatus,
  AgingOpenOrdersResult,
  TenderReconResult,
  ExpenseSummary,
  ExpenseIntelligenceReport,
  ExpenseCategoryDefinition,
  KitchenStation,
  VoidLogEntry,
  VoidLogSummary,
  CreateOrderItemModifier,
  FireKOTResponse,
  RelayedPrintJob,
  PrintJob,
  PrintJobAudit,
  PrintJobStatusResponse,
  PricingSettings,
  OnlineSubMethodConfig,
  CounterServer,
  OverviewReport,
  DailySalesRow,
  HourlySalesReport,
  ItemSalesRow,
  TableSalesRow,
  PartySizeRow,
  OrdersBrowserResponse,
  InvoiceSearchRow,
  RefundDailyRow,
  RefundLogRow,
  ReportsExportId,
  OrdersBrowserParams,
  TaxSummaryReport,
  StaffPerformanceReport,
  DashboardOverview,
  DashboardPeriod,
  LivePulse,
  DashboardActivityPage,
  SalesTimeseries,
  DashboardTopItem,
  PaymentMixSlice,
  OrderTypeMixSlice,
  DashboardAlert,
  FiscalConfigPublic,
  FiscalTestConnectionResult,
  FiscalDiSn002SandboxValidateResult,
  BusinessDay,
  CashDrawerMovement,
  ShiftClosing,
  DayCloseAuditEntry,
  FiscalNote,
  CreateFiscalNoteRequest,
  IssueCreditNoteRequest,
  FiscalAuditEvent,
  FiscalJob,
  PRAUnresolvedClaim,
  ZReportPayload,
  MarketingQRListResponse,
  MarketingQRTimeseriesResponse,
  UpdateMarketingQRRequest,
  MarketingOverviewResponse,
  VendorSalesReport,
  VendorStockReport,
  VendorSettlement,
  ServiceChargeOverride,
  CogsBasis,
  CogsBasisState,
} from '@/types';
import type {
  ParLevelRow,
  OrderSuggestion,
  FromSuggestionsLine,
  FromSuggestionsResult,
  PriceHistoryRow,
  PriceAlert,
  VendorItem,
  VendorItemInput,
  PettyCashReport,
} from '@/types/kitchenFlowPurchasing';
import type { EquipmentClash, ProductionPlan, PlanInput, PlanPreview, PlanAction } from '@/lib/inventoryFlow/planTypes';
import type { WasteEntry, WasteInput, WasteLog } from '@/lib/inventoryFlow/wasteTypes';
import type { WrapUpInput, WrapUpSheet } from '@/lib/inventoryFlow/wrapUpTypes';
import type { MarketPurchase, MarketPurchaseInput, StockCashOutPurchase } from '@/lib/inventoryFlow/marketTypes';
import type {
  ApproveReceiptExpensesInput,
  ApproveReceiptStockInput,
  ReceiptDuplicate,
  ReceiptScan,
  ReceiptScanList,
  ReceiptScanOptions,
} from '@/lib/receiptScan/receiptScanTypes';
import type { PriceChangesReport } from '@/lib/receiptScan/priceChanges';
import type { ItemMergeLogRow, MergeBulkSummary, MergeInput, MergeResult } from '@/lib/inventoryFlow/mergeTypes';
import type { PrebillWire } from '@/lib/printCustomerReceipt';
import type { MenuImportResult } from '@/lib/inventoryFlow/menuImportTypes';
import type { KitchenBasic } from '@/lib/inventoryFlow/basicsTypes';
import type { KitchenToday } from '@/lib/inventoryFlow/todayTypes';
import type {
  BookingAction, BookingInput, BookingUpdateResult, CateringBooking, CateringPaymentsReport, PaymentInput, PlanRevision,
} from '@/lib/inventoryFlow/cateringTypes';
import { markBackendReachable, noteRequestFailure } from '@/lib/offline/reachability';
import type {
  CreateRequisitionBody,
  FlowItem,
  FlowLocation,
  Requisition,
  RequisitionSummary,
  ShortfallGroup,
} from '@/lib/inventoryFlow/types';
// Kitchen flow Stage 3 — owner visibility.
import type {
  AvTReport,
  CountSession,
  DocumentTimeline,
  FillRateReport,
  FlowSummary,
  PurchasingReport,
  StockByLocationReport,
  TransitVarianceReport,
  YieldReport,
} from '@/lib/inventoryFlow/visibilityTypes';

/**
 * F-RPT-N30 — serialise Orders browser facets into the backend's query
 * string. Shared by the list call and the CSV export so both always carry
 * exactly the same filters. Empty / default values are omitted.
 */
export function ordersBrowserQueryParams(p: OrdersBrowserParams): Record<string, string> {
  const out: Record<string, string> = {};
  if (p.date) out.date = p.date;
  else if (p.from && p.to) {
    out.from = p.from;
    out.to = p.to;
  }
  if (p.search && p.search.trim() !== '') out.search = p.search.trim();
  if (p.order_types && p.order_types.length > 0) out.order_types = p.order_types.join(',');
  if (p.payment_method) out.payment_method = p.payment_method;
  if (p.payment_method === 'online' && p.payment_sub_method) out.payment_sub_method = p.payment_sub_method;
  if (p.status) out.status = p.status;
  if (p.pra_filter && p.pra_filter !== 'all') out.pra_filter = p.pra_filter;
  if (p.fiscal_filter && p.fiscal_filter !== 'all') out.fiscal_filter = p.fiscal_filter;
  if (p.sort) out.sort = p.sort;
  if (p.dir) out.dir = p.dir;
  if (p.page && p.page > 1) out.page = String(p.page);
  if (p.per_page) out.per_page = String(p.per_page);
  if (p.include_empty_cancels) out.include_empty_cancels = '1';
  if (p.focus) out.focus = p.focus;
  return out;
}

/**
 * Error thrown by every {@link APIClient.request} on non-2xx responses.
 * Carries the parsed backend payload so callers can branch on machine-
 * readable codes (e.g. `previous_day_open`) without scraping the
 * human-readable message.
 */
/**
 * The text a person sees for an API error. A bare machine code in `error`
 * (`tab_bill_already_paid`) is dropped when there is a message — callers branch
 * on `ApiClientError.code` instead; any other detail is kept after the message.
 */
export function apiErrorMessage(message?: string, error?: string): string {
  const parts = [message, error].filter(Boolean) as string[];
  if (message && error && /^[a-z][a-z0-9_]*$/.test(error)) return message;
  return parts.join(' — ');
}

export class ApiClientError extends Error {
  /** Machine-readable error code from the backend payload (`error` field). */
  code?: string;
  /** HTTP status code from the response. */
  status?: number;
  /** Optional list of orders blocking the operation (table reassign, etc.). */
  blockingOrders?: Array<{ id?: string; order_number?: string; status?: string }>;
  /**
   * Tender keys the backend refused to close without, on `tender_count_required`
   * (F-CTRL-N33) — e.g. `['card']`. Lets the close wizard reopen on that exact
   * step instead of stranding the operator on a Finish button that keeps
   * failing. Read from `data.data`, since the envelope invariant puts payloads
   * under `Data`.
   */
  missingTenders?: string[];
  /**
   * Per-product before/after prices returned with
   * `modifier_category_price_impact` (F-MOD-N5), read from `data.data.impact`.
   *
   * This rides on the ERROR rather than a success body because the backend
   * refuses the write with 409 and `request()` throws on any non-2xx — so a
   * caller checking `res.error` would never see it. The whole point of that
   * 409 is to put a customer-visible price change in front of the owner, so
   * losing it here would silently delete the safeguard.
   */
  priceImpact?: unknown;
  /**
   * The error response's `Data` payload, verbatim. Machine-usable context that
   * rides on refusals — e.g. `book_quantity_changed` (409, stale count
   * preview) carries the fresh `book_quantity` here so the count dialog can
   * re-render its variance preview without another fetch.
   */
  payload?: unknown;
  /**
   * True when no HTTP response was received — i.e. a transport failure (no
   * connection) OR a timeout after the request may have reached the server.
   * Callers use this to keep a staged order instead of discarding it.
   */
  isNetworkError?: boolean;
  /**
   * Raw axios error code when available. `ERR_NETWORK` = request never left the
   * device (safe to re-place); `ECONNABORTED` = timeout, the server may have
   * received it (ambiguous — warn before re-placing to avoid a duplicate).
   */
  axiosCode?: string;
  constructor(message: string) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/** Axios requests use `/auth/...`, `/admin/...` — base must include `/api/v1` from Gin's router.Group. */
export function resolveApiBaseUrl(): string {
  const raw =
    typeof import.meta.env?.VITE_API_URL === 'string' && import.meta.env.VITE_API_URL.trim() !== ''
      ? import.meta.env.VITE_API_URL.trim()
      : 'http://localhost:8080/api/v1'
  const base = raw.replace(/\/+$/, '')
  if (/\/api\/v1$/i.test(base)) return base
  return `${base}/api/v1`
}

class APIClient {
  private client: AxiosInstance;

  constructor() {
    const apiUrl = resolveApiBaseUrl();
    console.log('🔧 API Client baseURL:', apiUrl);
    console.log('🔧 Environment VITE_API_URL:', import.meta.env?.VITE_API_URL);
    
    this.client = axios.create({
      baseURL: apiUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('pos_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          config.headers['X-POS-JWT'] = token;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const payload = error.response?.data as { error?: string } | undefined;
          const errCode = typeof payload?.error === 'string' ? payload.error : '';
          // Proxies may strip Authorization — not an expired session.
          if (errCode !== 'missing_auth_header') {
            localStorage.removeItem('pos_token');
            localStorage.removeItem('pos_user');
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Helper method to handle API responses
  private async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.request(config);
      // F-FE-N30: a real response proves the backend is up. This is the single
      // HTTP choke point (invariant #9), so it is the only place that needs to
      // know — every caller inherits an accurate signal for free.
      markBackendReachable();
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as
          | {
              message?: string;
              error?: string;
              blocking_orders?: Array<{ id?: string; order_number?: string; status?: string }>;
              data?: { missing_tenders?: string[]; impact?: unknown };
            }
          | undefined;
        let msg = apiErrorMessage(data?.message, data?.error) || error.message;
        if (data?.blocking_orders?.length) {
          const detail = data.blocking_orders
            .map((o) => `${o.order_number ?? o.id ?? '?'} (${o.status ?? 'unknown'})`)
            .join(', ');
          msg = `${msg}. Blocking: ${detail}`;
        }
        // Surface structured fields on the Error so callers (counter UI,
        // day-close page) can branch on machine-readable codes like
        // `previous_day_open` without parsing the message string.
        const err = new ApiClientError(msg);
        err.code = data?.error;
        err.status = error.response?.status;
        err.blockingOrders = data?.blocking_orders;
        err.missingTenders = data?.data?.missing_tenders;
        err.priceImpact = data?.data?.impact;
        err.payload = data?.data;
        // No response => transport failure (offline) or a post-send timeout.
        // Preserve the axios code so callers can tell "never sent" (ERR_NETWORK)
        // from "sent, outcome unknown" (ECONNABORTED) — the duplicate-order trap.
        err.isNetworkError = !error.response;
        err.axiosCode = error.code;
        // F-FE-N30: decide whether this failure means the BACKEND is gone, as
        // opposed to a server bug or a business-rule rejection. Narrow on
        // purpose — see noteRequestFailure.
        noteRequestFailure({
          isNetworkError: err.isNetworkError,
          status: err.status,
          code: err.code,
        });
        throw err;
      }
      throw error;
    }
  }

  /**
   * Backend exposes POST /counter/orders and /admin/orders — not POST /orders.
   * Order creation must use the route group that matches the logged-in role.
   */
  private getStoredUserRole(): string {
    try {
      const raw = localStorage.getItem('pos_user');
      if (raw) {
        const u = JSON.parse(raw) as { role?: string };
        if (u.role) return u.role;
      }
    } catch {
      /* ignore */
    }
    return 'counter';
  }

  private getOrderCreatePath(): string {
    const role = this.getStoredUserRole();
    if (role === 'admin') return '/admin/orders';
    if (role === 'counter' || role === 'manager') return '/counter/orders';
    return '/counter/orders';
  }

  private getProcessPaymentPath(orderId: string): string {
    const role = this.getStoredUserRole();
    if (role === 'admin') return `/admin/orders/${orderId}/payments`;
    if (role === 'counter' || role === 'manager') return `/counter/orders/${orderId}/payments`;
    return `/counter/orders/${orderId}/payments`;
  }

  // Authentication endpoints
  async login(credentials: LoginRequest): Promise<APIResponse<LoginResponse>> {
    return this.request({
      method: 'POST',
      url: '/auth/login',
      data: credentials,
    });
  }

  async logout(): Promise<APIResponse> {
    return this.request({
      method: 'POST',
      url: '/auth/logout',
    });
  }

  async getCurrentUser(): Promise<APIResponse<User>> {
    return this.request({
      method: 'GET',
      url: '/auth/me',
    });
  }

  /**
   * Starts a password-reset flow. The backend always responds 200 with a
   * generic message — we never signal whether the email exists. Rate
   * limiting lives on the server.
   */
  async forgotPassword(email: string): Promise<APIResponse> {
    return this.request({
      method: 'POST',
      url: '/auth/forgot-password',
      data: { email },
    });
  }

  /**
   * Completes a password reset using the one-time token embedded in the
   * email link. `newPassword` must be at least 8 characters; the backend
   * enforces the same rule.
   */
  async resetPassword(token: string, newPassword: string): Promise<APIResponse> {
    return this.request({
      method: 'POST',
      url: '/auth/reset-password',
      data: { token, new_password: newPassword },
    });
  }

  /**
   * Rotates the currently-signed-in user's password. Requires current
   * password as anti-session-hijack guard. Does NOT invalidate existing
   * JWTs — the caller keeps their session, which is fine at our scale.
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<APIResponse> {
    return this.request({
      method: 'POST',
      url: '/auth/change-password',
      data: { current_password: currentPassword, new_password: newPassword },
    });
  }

  /**
   * Persists the caller's UI theme choice. Validated server-side against
   * the enum {light | dark | high-contrast | system}. The frontend updates
   * localStorage + DOM optimistically; failure here just leaves the DB out
   * of sync with the device (preference UX trumps consistency).
   */
  async updateThemePreference(
    theme: 'light' | 'dark' | 'high-contrast'
  ): Promise<APIResponse<{ theme_preference: string }>> {
    return this.request({
      method: 'PATCH',
      url: '/auth/me/theme',
      data: { theme_preference: theme },
    });
  }

  // Product endpoints
  async getProducts(filters?: ProductFilters): Promise<PaginatedResponse<Product[]>> {
    return this.request({
      method: 'GET',
      url: '/products',
      params: filters,
    });
  }

  /**
   * Fetch the ENTIRE product catalogue by walking every page of /products.
   *
   * /products is paginated (default 50, hard-capped at 100 per page server-side),
   * so a single getProducts() call silently returns only the first page. The POS
   * counter's "All" tab needs the whole menu — both to render the grid and so the
   * client-side search box can match products that live past page 1. Calling the
   * paginated endpoint once made the search box appear broken: any product beyond
   * the first 50 (e.g. "crochet flowers" on a 193-item tenant) was never in the
   * array being filtered, so it "didn't exist".
   *
   * The walk itself lives in paginate.ts (F-FE-N114): pages after the first are
   * fetched together and each page retries on its own, because a sequential
   * all-or-nothing loop left KIMMUN's till with no menu the evening its link
   * kept dropping.
   */
  async getAllProducts(
    filters?: Omit<ProductFilters, 'page' | 'per_page'>
  ): Promise<Product[]> {
    return fetchAllPages((page) => this.getProducts({ ...filters, page, per_page: 100 }));
  }

  async getProduct(id: string): Promise<APIResponse<Product>> {
    return this.request({
      method: 'GET',
      url: `/products/${id}`,
    });
  }

  async getCategories(activeOnly = true): Promise<APIResponse<Category[]>> {
    return this.request({
      method: 'GET',
      url: '/categories',
      params: { active_only: activeOnly },
    });
  }

  async getProductsByCategory(
    categoryId: string,
    availableOnly = true
  ): Promise<PaginatedResponse<Product[]>> {
    return this.request({
      method: 'GET',
      url: `/categories/${categoryId}/products`,
      params: { available_only: availableOnly },
    });
  }

  // Table endpoints
  async getTables(filters?: TableFilters): Promise<APIResponse<DiningTable[]>> {
    return this.request({
      method: 'GET',
      url: '/tables',
      params: filters,
    });
  }

  async getTable(id: string): Promise<APIResponse<DiningTable>> {
    return this.request({
      method: 'GET',
      url: `/tables/${id}`,
    });
  }

  async getTablesByLocation(): Promise<APIResponse<any[]>> {
    return this.request({
      method: 'GET',
      url: '/tables/by-location',
    });
  }

  async getTableStatus(): Promise<APIResponse<TableStatus>> {
    return this.request({
      method: 'GET',
      url: '/tables/status',
    });
  }

  // Order endpoints
  async getOrders(filters?: OrderFilters): Promise<PaginatedResponse<Order[]>> {
    return this.request({
      method: 'GET',
      url: '/orders',
      params: filters,
    });
  }

  async createOrder(order: CreateOrderRequest): Promise<APIResponse<Order>> {
    return this.request({
      method: 'POST',
      url: this.getOrderCreatePath(),
      data: order,
    });
  }

  /**
   * Connectivity probe (F-FE-N71). Cheap and side-effect-free by design — the
   * server answers it without touching Postgres — so `lib/offline/probe.ts` can
   * poll it every 15s while the app believes the backend is unreachable.
   *
   * The response is ignored by callers on purpose: any 2xx through this client
   * calls markBackendReachable() at the choke point below, so recovery happens
   * through exactly the same path as any other successful request.
   */
  async ping(): Promise<APIResponse> {
    return this.request({ method: 'GET', url: '/counter/ping' });
  }

  // reserveOrderNumbers / releaseOrderNumbers are GONE (F-CTRL-N52).
  //
  // Offline slips no longer print a pre-reserved order number — the server
  // allocates the number when the order arrives. The two endpoints still exist
  // server-side because pre-N52 terminals keep calling them until they restart,
  // but nothing in this bundle may: reserving here is what burnt 20 numbers per
  // till per day out of the invoice sequence, and what left a terminal unable to
  // sell during the outage the feature exists for.

  /**
   * Post an order taken while this terminal was offline (F-CTRL-N34).
   *
   * Retry-safe: `op_id` is stored on the order behind a unique index, so a
   * repeat call returns `offline_order_duplicate` rather than creating a
   * second order for food the kitchen already made.
   */
  async replayOfflineOrder(
    op: OfflineReplayRequest,
    // `fired` is false when the order synced but could not be marked fired
    // (F-CTRL-N54) — the sale is safe, but that order still needs one tap at the
    // counter or its next fire reprints food the kitchen already made.
    // `total_amount` is the SERVER's recomputed total (F-CTRL-N55). A terminal
    // that took payment during the outage settles at this figure, not the one it
    // computed from its cached menu — the offline device records what it
    // collected, it does not decide what the order is worth.
  ): Promise<
    APIResponse<{
      order_id: string
      order_number: string
      fired?: boolean
      total_amount?: number
      /**
       * Counter print jobs the replay queued (F-FE-N124) — non-empty only when
       * this client asked to relay. Optional-for-old-backends, like the fire
       * response's field: a backend that predates N124 returns nothing here.
       */
      relayed_print_jobs?: RelayedPrintJob[]
    }>
  > {
    return this.request({
      method: 'POST',
      url: '/counter/offline-orders/replay',
      data: op,
    });
  }

  /**
   * Hand a permanently-failed offline operation to the reconciliation queue
   * (F-CTRL-N35). Idempotent on op_id, so a retried report cannot show the
   * manager the same food twice.
   */
  async reportUnreconciledOffline(item: {
    op_id: string;
    order_number: string;
    target_order_id: string | null;
    captured_at: number;
    failure_code: string;
    lines: unknown;
    /**
     * F-CTRL-N55: both sides of an offline payment that did not reconcile —
     * what the cashier collected, and what the server billed. A manager needs
     * both or the row is unanswerable.
     */
    collected_amount?: number | null;
    settled_amount?: number | null;
  }): Promise<APIResponse<{ id: string }>> {
    return this.request({
      method: 'POST',
      url: '/counter/offline-orders/unreconciled',
      data: item,
    });
  }

  /** Open items awaiting a manager's bill-or-write-off decision. */
  async listUnreconciledOffline(): Promise<APIResponse<{ items: UnreconciledOfflineItem[] }>> {
    return this.request({
      method: 'GET',
      url: '/counter/offline-orders/unreconciled',
    });
  }

  /**
   * Record the decision. Manager/admin only; a note is required either way.
   *
   * The path is `/admin`-prefixed on purpose: reporting and listing live on the
   * counter group, but the decision is manager-gated (routes.go,
   * adminManagerPOSWrite). Dropping the prefix matches no route at all, so Gin
   * answers a bare 404 with no APIResponse body and the manager sees
   * "Request failed with status code 404" while Day Close stays blocked.
   */
  async resolveUnreconciledOffline(
    id: string,
    action: 'billed' | 'written_off',
    note: string,
  ): Promise<APIResponse> {
    return this.request({
      method: 'POST',
      url: `/admin/offline-orders/unreconciled/${id}/resolve`,
      data: { action, note },
    });
  }

  async getOrder(id: string): Promise<APIResponse<Order>> {
    return this.request({
      method: 'GET',
      url: `/orders/${id}`,
    });
  }

  async updateOrderStatus(
    id: string,
    status: UpdateOrderStatusRequest['status'],
    notes?: string
  ): Promise<APIResponse<Order>> {
    const statusUpdate: UpdateOrderStatusRequest = { status, notes };
    return this.request({
      method: 'PATCH',
      url: `/orders/${id}/status`,
      data: statusUpdate,
    });
  }

  // Payment endpoints
  // ── Delivery riders (F-DLV-N1). Backend 404s feature_not_enabled unless
  // FEATURE_RIDER_APP=1; screens gate on useRiderAppEnabled first.
  async getRiderMe(): Promise<APIResponse<RiderMe>> {
    return this.request({ method: 'GET', url: '/rider/me' });
  }

  async getRiderMyCash(): Promise<APIResponse<RiderCash>> {
    return this.request({ method: 'GET', url: '/rider/me/cash' });
  }

  async setRiderShift(on: boolean): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: '/rider/shift', data: { on } });
  }

  async updateRiderAssignment(assignmentId: string, body: RiderProgressRequest): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: `/rider/assignments/${assignmentId}/progress`, data: body });
  }

  async riderCollect(assignmentId: string, body: RiderCollectRequest): Promise<APIResponse<Payment>> {
    return this.request({ method: 'POST', url: `/rider/assignments/${assignmentId}/collect`, data: body });
  }

  // F-DLV-N2: live location + push alerts.
  async postRiderLocation(points: RiderLocationPoint[], battery?: number | null): Promise<APIResponse<{ kept: number }>> {
    return this.request({ method: 'POST', url: '/rider/location', data: { points, battery: battery ?? null } });
  }

  async getRiderPushKey(): Promise<APIResponse<{ public_key: string }>> {
    return this.request({ method: 'GET', url: '/rider/push/key' });
  }

  async riderPushSubscribe(sub: PushSubscriptionJSON): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: '/rider/push/subscribe', data: sub });
  }

  async riderPushUnsubscribe(endpoint: string): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: '/rider/push/unsubscribe', data: { endpoint } });
  }

  async getRiderTrail(riderId: string, hours = 12): Promise<APIResponse<{ points: TrailPoint[] }>> {
    return this.request({ method: 'GET', url: `/dispatch/riders/${riderId}/trail`, params: { hours } });
  }

  // F-DLV-N3: drop pins for counter / phone delivery orders.
  async setOrderPin(orderId: string, pin: { lat: number; lng: number } | null): Promise<APIResponse<void>> {
    return this.request({ method: 'PUT', url: `/dispatch/orders/${orderId}/pin`, data: pin ?? { clear: true } });
  }

  async resolveMapLink(url: string): Promise<APIResponse<{ lat: number; lng: number }>> {
    return this.request({ method: 'POST', url: '/dispatch/map-link', data: { url } });
  }

  // ── Staff & Payroll (F-HR-N1..N6). Backend 404s feature_not_enabled
  // unless FEATURE_HR_PAYROLL=1; the page gates on useHRPayrollEnabled first.
  async hrOverview(): Promise<APIResponse<HROverview>> {
    return this.request({ method: 'GET', url: '/hr/overview' });
  }

  async hrGetSettings(): Promise<APIResponse<HRSettings>> {
    return this.request({ method: 'GET', url: '/hr/settings' });
  }

  async hrUpdateSettings(body: HRSettings): Promise<APIResponse<HRSettings>> {
    return this.request({ method: 'PUT', url: '/hr/settings', data: body });
  }

  async hrListEmployees(status: 'active' | 'left' | 'all' = 'active'): Promise<APIResponse<HREmployee[]>> {
    return this.request({ method: 'GET', url: '/hr/employees', params: { status } });
  }

  async hrGetEmployee(id: string): Promise<APIResponse<{ employee: HREmployee; rates?: HRRate[] }>> {
    return this.request({ method: 'GET', url: `/hr/employees/${id}` });
  }

  async hrCreateEmployee(body: HREmployeeInput): Promise<APIResponse<HREmployee>> {
    return this.request({ method: 'POST', url: '/hr/employees', data: body });
  }

  async hrUpdateEmployee(id: string, body: HREmployeeInput): Promise<APIResponse<HREmployee>> {
    return this.request({ method: 'PATCH', url: `/hr/employees/${id}`, data: body });
  }

  async hrAddRate(id: string, body: { amount: number; effective_from: string; reason?: string }): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: `/hr/employees/${id}/rates`, data: body });
  }

  async hrMarkLeft(id: string, exitDate: string): Promise<APIResponse<{ encashed_days: number }>> {
    return this.request({ method: 'POST', url: `/hr/employees/${id}/left`, data: { exit_date: exitDate } });
  }

  async hrRejoin(id: string): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: `/hr/employees/${id}/rejoin` });
  }

  async hrCandidates(): Promise<APIResponse<HRCandidate[]>> {
    return this.request({ method: 'GET', url: '/hr/candidates' });
  }

  async hrImport(body: { as_of: string; dry_run?: boolean; rows: HRImportRow[] }): Promise<APIResponse<{ rows: number }>> {
    return this.request({ method: 'POST', url: '/hr/import', data: body });
  }

  async hrGetDay(date: string): Promise<APIResponse<HRRegisterDay>> {
    return this.request({ method: 'GET', url: '/hr/attendance/day', params: { date } });
  }

  async hrMarkDay(body: { date: string; reason?: string; entries: HRMarkEntry[] }): Promise<APIResponse<{ changed: number }>> {
    return this.request({ method: 'PUT', url: '/hr/attendance/day', data: body });
  }

  async hrMarkAllPresent(date: string): Promise<APIResponse<{ changed: number }>> {
    return this.request({ method: 'POST', url: '/hr/attendance/day/mark-all', data: { date } });
  }

  async hrGetMonth(month: string): Promise<APIResponse<HRMonth>> {
    return this.request({ method: 'GET', url: '/hr/attendance/month', params: { month } });
  }

  async hrListHolidays(year: number): Promise<APIResponse<HRHoliday[]>> {
    return this.request({ method: 'GET', url: '/hr/holidays', params: { year } });
  }

  async hrAddHoliday(body: HRHoliday): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: '/hr/holidays', data: body });
  }

  async hrDeleteHoliday(date: string): Promise<APIResponse<void>> {
    return this.request({ method: 'DELETE', url: `/hr/holidays/${date}` });
  }

  async hrListLeaveTypes(all = false): Promise<APIResponse<HRLeaveType[]>> {
    return this.request({ method: 'GET', url: '/hr/leave-types', params: all ? { all: 1 } : undefined });
  }

  async hrCreateLeaveType(body: Partial<HRLeaveType>): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/hr/leave-types', data: body });
  }

  async hrUpdateLeaveType(id: string, body: Partial<HRLeaveType>): Promise<APIResponse<void>> {
    return this.request({ method: 'PATCH', url: `/hr/leave-types/${id}`, data: body });
  }

  async hrLeaveBalances(asOf?: string): Promise<APIResponse<HRLeaveBalances>> {
    return this.request({ method: 'GET', url: '/hr/leave/balances', params: asOf ? { as_of: asOf } : undefined });
  }

  async hrListLeaveEntries(employeeId?: string): Promise<APIResponse<HRLeaveEntry[]>> {
    return this.request({ method: 'GET', url: '/hr/leave/entries', params: employeeId ? { employee_id: employeeId } : undefined });
  }

  async hrRecordLeave(body: {
    employee_id: string
    leave_type_id: string
    from: string
    to?: string
    half_day?: boolean
    note?: string
    allow_over?: boolean
  }): Promise<APIResponse<{ id: string; days: number }>> {
    return this.request({ method: 'POST', url: '/hr/leave/entries', data: body });
  }

  async hrCancelLeave(id: string): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: `/hr/leave/entries/${id}/cancel` });
  }

  async hrAdjustLeave(body: { employee_id: string; leave_type_id: string; days: number; reason: string }): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: '/hr/leave/adjustments', data: body });
  }

  async hrPreviewYearEnd(): Promise<APIResponse<HRYearEndRow[]>> {
    return this.request({ method: 'GET', url: '/hr/leave/year-end' });
  }

  async hrCloseYearEnd(): Promise<APIResponse<{ rows: number }>> {
    return this.request({ method: 'POST', url: '/hr/leave/year-end' });
  }

  async hrListAdvances(status: 'open' | 'all' = 'open', employeeId?: string): Promise<APIResponse<HRAdvance[]>> {
    return this.request({ method: 'GET', url: '/hr/advances', params: { status, ...(employeeId ? { employee_id: employeeId } : {}) } });
  }

  async hrGiveAdvance(body: {
    employee_id: string
    amount: number
    given_on?: string
    reason?: string
    recovery_plan: HRRecoveryPlan
    installments?: number
    monthly_amount?: number
    method: HRPayoutMethod
    reference?: string
  }): Promise<APIResponse<HRAdvance>> {
    return this.request({ method: 'POST', url: '/hr/advances', data: body });
  }

  async hrWriteOffAdvance(id: string, note: string): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: `/hr/advances/${id}/write-off`, data: { note } });
  }

  async hrListPayouts(params: { employee_id?: string; run_id?: string }): Promise<APIResponse<HRPayout[]>> {
    return this.request({ method: 'GET', url: '/hr/payouts', params });
  }

  async hrPay(body: {
    employee_id: string
    run_id?: string
    amount: number
    paid_on?: string
    method?: HRPayoutMethod
    reference?: string
    note?: string
  }): Promise<APIResponse<HRPayout>> {
    return this.request({ method: 'POST', url: '/hr/payouts', data: body });
  }

  async hrReversePayout(id: string, reason: string): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: `/hr/payouts/${id}/reverse`, data: { reason } });
  }

  async hrStatement(id: string): Promise<APIResponse<HRStatement>> {
    return this.request({ method: 'GET', url: `/hr/employees/${id}/statement` });
  }

  async hrListRules(): Promise<APIResponse<HRRule[]>> {
    return this.request({ method: 'GET', url: '/hr/rules' });
  }

  async hrCreateRule(body: Omit<HRRule, 'id' | 'active'> & { active?: boolean }): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/hr/rules', data: body });
  }

  async hrUpdateRule(id: string, body: Partial<HRRule>): Promise<APIResponse<void>> {
    return this.request({ method: 'PATCH', url: `/hr/rules/${id}`, data: body });
  }

  async hrListRuns(): Promise<APIResponse<HRRunListItem[]>> {
    return this.request({ method: 'GET', url: '/hr/payroll/runs' });
  }

  async hrCreateRun(month: string): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/hr/payroll/runs', data: { month } });
  }

  async hrGetRun(id: string): Promise<APIResponse<HRRunDetail>> {
    return this.request({ method: 'GET', url: `/hr/payroll/runs/${id}` });
  }

  async hrAddRunItem(id: string, body: { employee_id: string; kind: 'bonus' | 'deduction'; amount: number; reason: string }): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: `/hr/payroll/runs/${id}/items`, data: body });
  }

  async hrDecideRunItem(id: string, itemId: string, body: { state: 'kept' | 'removed' | 'suggested'; note?: string }): Promise<APIResponse<void>> {
    return this.request({ method: 'PATCH', url: `/hr/payroll/runs/${id}/items/${itemId}`, data: body });
  }

  async hrKeepAll(id: string): Promise<APIResponse<{ kept: number }>> {
    return this.request({ method: 'POST', url: `/hr/payroll/runs/${id}/keep-all` });
  }

  async hrSubmitRun(id: string): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: `/hr/payroll/runs/${id}/submit` });
  }

  async hrReturnRun(id: string, note: string): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: `/hr/payroll/runs/${id}/return`, data: { note } });
  }

  async hrApproveRun(id: string): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: `/hr/payroll/runs/${id}/approve` });
  }

  async hrReopenRun(id: string, reason: string): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: `/hr/payroll/runs/${id}/reopen`, data: { reason } });
  }

  async hrPayAll(id: string, body: { paid_on?: string; method?: HRPayoutMethod | ''; reference?: string }): Promise<APIResponse<{ paid: number; total: number; skipped: number }>> {
    return this.request({ method: 'POST', url: `/hr/payroll/runs/${id}/pay-all`, data: body });
  }

  async getDispatchBoard(): Promise<APIResponse<DispatchBoard>> {
    return this.request({ method: 'GET', url: '/dispatch/board' });
  }

  async assignRider(orderId: string, riderId: string): Promise<APIResponse<{ assignment_id: string }>> {
    return this.request({ method: 'POST', url: `/dispatch/orders/${orderId}/assign`, data: { rider_id: riderId } });
  }

  async unassignRider(orderId: string): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: `/dispatch/orders/${orderId}/unassign` });
  }

  async markDeliveredWithoutRider(orderId: string): Promise<APIResponse<void>> {
    return this.request({ method: 'POST', url: `/dispatch/orders/${orderId}/delivered` });
  }

  async getDispatchRiderCash(riderId: string): Promise<APIResponse<RiderCash>> {
    return this.request({ method: 'GET', url: `/dispatch/riders/${riderId}/cash` });
  }

  async riderHandin(riderId: string, body: HandinRequest): Promise<APIResponse<HandinResult>> {
    return this.request({ method: 'POST', url: `/dispatch/riders/${riderId}/handin`, data: body });
  }

  async processPayment(orderId: string, payment: ProcessPaymentRequest): Promise<APIResponse<Payment>> {
    return this.request({
      method: 'POST',
      url: this.getProcessPaymentPath(orderId),
      data: payment,
    });
  }

  async getPayments(orderId: string): Promise<APIResponse<Payment[]>> {
    return this.request({
      method: 'GET',
      url: `/orders/${orderId}/payments`,
    });
  }

  async getPaymentSummary(orderId: string): Promise<APIResponse<PaymentSummary>> {
    return this.request({
      method: 'GET',
      url: `/orders/${orderId}/payment-summary`,
    });
  }

  // Dashboard endpoints
  // ─── Legacy snapshot (kept for backward compatibility) ─────────────────
  /**
   * @deprecated Use {@link getDashboardOverview} + {@link getDashboardLive}
   * which return typed payloads with prior-period comparisons.
   */
  async getDashboardStats(): Promise<APIResponse<DashboardStats>> {
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/stats',
    });
  }

  // ─── Dashboard v2 ─────────────────────────────────────────────────────
  // Typed, business-timezone-aware payloads with prior-period comparisons.
  // Powers the redesigned admin dashboard. See backend/internal/handlers/dashboard.go.

  /** Builds the params object for any dashboard v2 endpoint. */
  private dashboardParams(period: DashboardPeriod, from?: string, to?: string) {
    const params: Record<string, string> = { period };
    if (period === 'custom') {
      if (from) params.from = from;
      if (to) params.to = to;
    }
    return params;
  }

  async getDashboardOverview(
    period: DashboardPeriod,
    from?: string,
    to?: string,
  ): Promise<APIResponse<DashboardOverview>> {
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/overview',
      params: this.dashboardParams(period, from, to),
    });
  }

  async getDashboardLive(): Promise<APIResponse<LivePulse>> {
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/live',
    });
  }

  /** F-RPT-N44: the current business day's activity, newest first; `before` pages back. */
  async getDashboardActivity(before?: string, limit?: number): Promise<APIResponse<DashboardActivityPage>> {
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/activity',
      params: { ...(before ? { before } : {}), ...(limit ? { limit } : {}) },
    });
  }

  async getDashboardSalesTimeseries(
    period: DashboardPeriod,
    from?: string,
    to?: string,
  ): Promise<APIResponse<SalesTimeseries>> {
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/sales-timeseries',
      params: this.dashboardParams(period, from, to),
    });
  }

  async getDashboardTopItems(
    period: DashboardPeriod,
    opts: { limit?: number; from?: string; to?: string } = {},
  ): Promise<APIResponse<DashboardTopItem[]>> {
    const params = this.dashboardParams(period, opts.from, opts.to);
    if (opts.limit) (params as Record<string, string | number>).limit = opts.limit;
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/top-items',
      params,
    });
  }

  async getDashboardPaymentMix(
    period: DashboardPeriod,
    from?: string,
    to?: string,
  ): Promise<APIResponse<PaymentMixSlice[]>> {
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/payment-mix',
      params: this.dashboardParams(period, from, to),
    });
  }

  async getDashboardOrderTypeMix(
    period: DashboardPeriod,
    from?: string,
    to?: string,
  ): Promise<APIResponse<OrderTypeMixSlice[]>> {
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/order-type-mix',
      params: this.dashboardParams(period, from, to),
    });
  }

  async getDashboardAlerts(): Promise<APIResponse<DashboardAlert[]>> {
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/alerts',
    });
  }
  // The SSE stream URL is built inside the useDashboardStream hook (see
  // src/lib/dashboardStream.ts), mirroring the kitchen-stream convention.

  // F-RPT-01: legacy getSalesReport / getOrdersReport / getIncomeReport
  // client methods + their backend endpoints are gone. Use the Reports v2
  // methods below or getDashboardSalesTimeseries instead — both bucket
  // orders by business_day_id so they match Day Close and the dashboard.

  // ─── Reports v2 ──────────────────────────────────────────────────────────
  // All v2 endpoints share a `from`/`to` ISO YYYY-MM-DD contract. The UI
  // displays DD-MM-YYYY but always sends ISO on the wire.

  async getReportsOverview(from: string, to: string): Promise<APIResponse<OverviewReport>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/overview',
      params: { from, to },
    });
  }

  // ─── Marketing Suite landing tab ────────────────────────────────────────
  // Single aggregated endpoint feeding the /admin/marketing/overview page
  // (KPIs + customer mix + engagement timeseries + top performers +
  // needs-attention + occasions + recent activity + channel status).

  async getMarketingOverview(
    from: string,
    to: string,
  ): Promise<APIResponse<MarketingOverviewResponse>> {
    return this.request({
      method: 'GET',
      url: '/admin/marketing/overview',
      params: { from, to },
    });
  }

  async getDailySalesReport(from: string, to: string): Promise<APIResponse<DailySalesRow[]>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/sales/daily',
      params: { from, to },
    });
  }

  async getHourlySalesReport(from: string, to: string): Promise<APIResponse<HourlySalesReport>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/sales/hourly',
      params: { from, to },
    });
  }

  async getItemSalesReport(
    from: string,
    to: string,
    opts: {
      search?: string;
      category_id?: string;
      tag_id?: string;
      group_by?: 'item' | 'category' | 'tag';
      sort?: 'qty' | 'gross' | 'net' | 'margin' | 'margin_pct';
      limit?: number;
      /** order_type ids to keep (dine_in / takeout / delivery / custom). Empty = all. */
      order_types?: string[];
      /** Orthogonal provenance axis — an online delivery matches both filters. */
      order_source?: 'pos' | 'server' | 'online';
    } = {},
  ): Promise<APIResponse<ItemSalesRow[]>> {
    const params: Record<string, string | number> = { from, to };
    if (opts.search && opts.search.trim() !== '') params.search = opts.search.trim();
    if (opts.category_id) params.category_id = opts.category_id;
    if (opts.tag_id) params.tag_id = opts.tag_id;
    if (opts.group_by) params.group_by = opts.group_by;
    if (opts.order_types && opts.order_types.length > 0) params.order_types = opts.order_types.join(',');
    if (opts.order_source) params.order_source = opts.order_source;
    if (opts.sort) params.sort = opts.sort;
    if (opts.limit) params.limit = opts.limit;
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/items',
      params,
    });
  }

  /** Active tags for the report's Tag group/filter dropdown (accountant-safe). */
  async getReportTags(): Promise<APIResponse<Tag[]>> {
    return this.request({ method: 'GET', url: '/admin/reports/v2/tags' });
  }

  async getTableSalesReport(from: string, to: string): Promise<APIResponse<TableSalesRow[]>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/tables',
      params: { from, to },
    });
  }

  async getPartySizeReport(from: string, to: string): Promise<APIResponse<PartySizeRow[]>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/party-size',
      params: { from, to },
    });
  }

  /**
   * Returns a lightweight list of orders for one business day, including the
   * computed PRA late-print eligibility per order. Used by the Reports →
   * Reports Daily Sales tab (drill into a day).
   *
   * `date` accepts ISO YYYY-MM-DD or DD-MM-YYYY. `praFilter` defaults to 'all'.
   */
  async getOrdersBrowser(
    params: OrdersBrowserParams,
  ): Promise<APIResponse<OrdersBrowserResponse>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/orders',
      params: ordersBrowserQueryParams(params),
    });
  }

  /**
   * Cross-day invoice search for the Reports → Daily Sales box. Matches order
   * number, PRA invoice number, FBR IRN, or customer name across all business
   * days. Returns up to 100 matches; the UI deep-links into each match's day.
   */
  async searchInvoices(
    q: string,
  ): Promise<APIResponse<{ results: InvoiceSearchRow[] }>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/orders/search',
      params: { q: q.trim() },
    });
  }

  /** Refund Logs per-day rollup. Reports → Refund Logs tab. */
  async getRefundsDaily(from: string, to: string): Promise<APIResponse<RefundDailyRow[]>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/refunds/daily',
      params: { from, to },
    });
  }

  async getTaxSummary(from: string, to: string): Promise<APIResponse<TaxSummaryReport>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/tax',
      params: { from, to },
    });
  }

  async getStaffPerformance(from: string, to: string): Promise<APIResponse<StaffPerformanceReport>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/staff',
      params: { from, to },
    });
  }

  /** Credit notes issued on a single day. Reports → Refund Logs drill-in. */
  async getRefundsByDay(
    day: string,
    opts: {
      search?: string;
      status?: 'all' | 'synced' | 'pending' | 'failed';
    } = {},
  ): Promise<APIResponse<RefundLogRow[]>> {
    const params: Record<string, string> = { day };
    if (opts.search && opts.search.trim() !== '') params.search = opts.search.trim();
    if (opts.status && opts.status !== 'all') params.status = opts.status;
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/refunds/by-day',
      params,
    });
  }

  /**
   * Downloads a report as a CSV file in the user's browser. The filename is
   * the backend's Content-Disposition value — a slug of the restaurant_name
   * setting plus the DD-MM-YYYY range (e.g. "biskits_items_15-06-2026_…csv").
   * That header is only readable cross-origin because the API exposes it via
   * CORS (ExposeHeaders in backend/main.go); the brand-neutral fallback below
   * is a last resort if the header is ever missing.
   */
  async exportReportCsv(
    report: ReportsExportId,
    from: string,
    to: string,
    extra: Record<string, string> = {},
  ): Promise<void> {
    const response = await this.client.get('/admin/reports/v2/export', {
      params: { report, from, to, format: 'csv', ...extra },
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Try to honour the server-provided filename; fall back to a sensible name.
    const cd = response.headers['content-disposition'] as string | undefined;
    const match = cd && /filename="?([^"]+)"?/i.exec(cd);
    a.download = match ? match[1] : `report_${report}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Per-report formatted .xlsx (the Excel counterpart of exportReportCsv).
   * `report` picks the workbook: overview (Summary/Payment/Order-type sheets),
   * items (Items/Categories/Tags), daily_sales, hourly, tables, party_size,
   * refunds, or period_pack (all reports bundled into one workbook). `extra`
   * passes through Items filters (search/category_id/tag_id).
   */
  async exportReportXlsx(
    report: string,
    from: string,
    to: string,
    extra: Record<string, string> = {},
  ): Promise<void> {
    const response = await this.client.get('/admin/reports/v2/xlsx-export', {
      params: { report, from, to, ...extra },
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = response.headers['content-disposition'] as string | undefined;
    const match = cd && /filename="?([^"]+)"?/i.exec(cd);
    a.download = match ? match[1] : `report_${report}_${from}_to_${to}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Order-wise + line-wise audit workbook (.xlsx) over a full date range: an
   * Orders sheet (one row per order with the full money + fiscal trail) and a
   * Line Items sheet (one row per item sold). Genuinely auditable — unlike the
   * per-day summary CSV or the on-screen-table PDF.
   */
  async exportOrdersAuditXlsx(from: string, to: string): Promise<void> {
    const response = await this.client.get('/admin/reports/v2/orders/audit-export', {
      params: { from, to },
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = response.headers['content-disposition'] as string | undefined;
    const match = cd && /filename="?([^"]+)"?/i.exec(cd);
    a.download = match ? match[1] : `orders_audit_${from}_to_${to}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  // Kitchen endpoints
  async getKitchenOrders(
    statusOrOptions?: string | { status?: string; station_id?: string; include_stale?: boolean },
  ): Promise<APIResponse<Order[]>> {
    const opts = typeof statusOrOptions === 'string' ? { status: statusOrOptions } : statusOrOptions ?? {}
    const params: Record<string, string> = {}
    if (opts.status && opts.status !== 'all') params.status = opts.status
    if (opts.station_id) params.station_id = opts.station_id
    if (opts.include_stale) params.include_stale = 'true'
    return this.request({
      method: 'GET',
      url: '/kitchen/orders',
      params,
    });
  }

  async updateOrderItemStatus(orderId: string, itemId: string, status: string): Promise<APIResponse> {
    return this.request({
      method: 'PATCH',
      url: `/kitchen/orders/${orderId}/items/${itemId}/status`,
      data: { status },
    });
  }

  /** Kitchen bump: order ready for pickup, removes from active KDS */
  async kitchenBumpOrder(orderId: string, stationId?: string): Promise<
    APIResponse<{
      order_id: string
      completion_seconds?: number
      kitchen_bumped_at?: string
      table_id?: string | null
      ready_for_pickup: boolean
      station_id?: string
      // true when only this station was bumped and the order is still live on
      // other stations' boards (per-station bump).
      partial?: boolean
    }>
  > {
    // A station_id scopes the bump to that station's lines only; omit it to bump
    // the whole order (the "All" board).
    const params = stationId ? { station_id: stationId } : undefined
    return this.request({
      method: 'POST',
      url: `/kitchen/orders/${orderId}/bump`,
      params,
    });
  }

  /** Recall a bumped order back to the line (within kitchen.recall_window_seconds). */
  async recallOrder(orderId: string): Promise<APIResponse<{ order_id: string; status: string }>> {
    return this.request({
      method: 'POST',
      url: `/kitchen/orders/${orderId}/recall`,
    });
  }

  /**
   * KDS pass hand-off ("Picked up") — F-KDS-N5.
   *
   * Kitchen-scoped: this is the ONLY served transition a kitchen-role station
   * can perform. Do not switch this back to updateOrderStatus(id, 'served') —
   * that hits PATCH /orders/:id/status, which is counter/manager/admin only
   * (F-CTRL-05, since it also cancels + completes), so it 403'd on every
   * kitchen-role KDS.
   */
  async markOrderServed(orderId: string): Promise<APIResponse<{ order_id: string; status: string }>> {
    return this.request({
      method: 'POST',
      url: `/kitchen/orders/${orderId}/served`,
    });
  }

  /** Kitchen-scoped read-only stations list (works for kitchen and admin). */
  async getKitchenStations(): Promise<APIResponse<KitchenStation[]>> {
    return this.request({ method: 'GET', url: '/kitchen/stations' });
  }

  /**
   * Station routing for the counter, for offline KOT building (F-FE-N76).
   *
   * A separate call from getKitchenStations() because that one hits
   * /kitchen/stations, which is gated on RequireRoles({kitchen, admin}) AND
   * RequireKDSEnabled. Offline capture ran it on every counter terminal, where
   * it 403'd for any `counter` login, and 403'd for everyone on a tenant with
   * KDS switched off — which is most printer-only venues, i.e. exactly the
   * tenants offline capture is built for. The failure was swallowed, so the
   * cashier only learned about it as "Kitchen routing has not been cached yet"
   * at the moment the connection died.
   */
  async getCounterStations(): Promise<APIResponse<KitchenStation[]>> {
    return this.request({ method: 'GET', url: '/counter/stations' });
  }

  /** Recently bumped orders (for the KDS recall strip). */
  async getRecentBumpedOrders(limit = 5): Promise<
    APIResponse<
      Array<{
        id: string
        order_number: string
        order_type: string
        customer_name?: string
        table_number?: string | null
        kitchen_bumped_at?: string | null
      }>
    >
  > {
    return this.request({
      method: 'GET',
      url: '/kitchen/recent-bumped',
      params: { limit },
    });
  }

  /**
   * Today's finished tickets (served/completed) for the KDS "Completed" lane —
   * a view-only history, business-day scoped on the server. Optionally scope to
   * the active station filter. Reuses the same Order shape as getKitchenOrders
   * so the card renderer works unchanged.
   */
  async getKitchenCompletedOrders(
    opts: { limit?: number; station_id?: string } = {},
  ): Promise<APIResponse<Order[]>> {
    const params: Record<string, string | number> = {}
    if (opts.limit) params.limit = opts.limit
    if (opts.station_id) params.station_id = opts.station_id
    return this.request({
      method: 'GET',
      url: '/kitchen/completed',
      params,
    });
  }

  /** Rolling-24h KDS performance metrics for the board stats strip. */
  async getKitchenStats(): Promise<
    APIResponse<{
      window_hours: number
      bumped_count: number
      avg_prep_seconds: number
      on_time_count: number
      on_time_pct: number
      target_minutes: number
    }>
  > {
    return this.request({ method: 'GET', url: '/kitchen/stats' });
  }

  // Server Interface order creation. Dedicated endpoint (not the role-derived
  // /counter|/admin/orders): it is dine-in only and stamps order_source =
  // 'server' so reports distinguish waiter-fired orders from counter orders.
  // Admins are inside the /counter role group, so this URL works for every role.
  async createServerOrder(order: CreateOrderRequest): Promise<APIResponse<Order>> {
    return this.request({
      method: 'POST',
      url: '/counter/server-orders',
      data: order,
    });
  }

  async createCounterOrder(order: CreateOrderRequest): Promise<APIResponse<Order>> {
    return this.request({
      method: 'POST',
      url: '/counter/orders',
      data: order,
    });
  }

  // Counter payment processing
  async processCounterPayment(orderId: string, payment: ProcessPaymentRequest): Promise<APIResponse<Payment>> {
    return this.request({
      method: 'POST',
      url: `/counter/orders/${orderId}/payments`,
      data: payment,
    });
  }

  // ── Online ordering — staff inbox ────────────────────────────────────────
  // Incoming online orders sit in the online_orders staging table until staff
  // accept (which creates the real fiscal order via the shared core) or reject.
  async getOnlineOrders(): Promise<APIResponse<OnlineOrder[]>> {
    return this.request({
      method: 'GET',
      url: '/counter/online-orders',
    });
  }

  // Accepted online orders whose real order is still live, for the dispatch
  // board's In-Kitchen → Ready → Out-for-delivery → Awaiting-settlement lanes.
  async getActiveOnlineOrders(): Promise<APIResponse<ActiveOnlineOrder[]>> {
    return this.request({
      method: 'GET',
      url: '/counter/online-orders/active',
    });
  }

  // Actioned online orders (accepted/rejected), newest-first + paginated, for
  // the Accepted/Rejected history tabs. Mirrors getVoidLog's param shape.
  async getOnlineOrderHistory(params: {
    status: 'accepted' | 'rejected';
    page?: number;
    per_page?: number;
    from?: string;
    to?: string;
    q?: string;
  }): Promise<PaginatedResponse<OnlineOrder[]>> {
    return this.request({
      method: 'GET',
      url: '/counter/online-orders/history',
      params,
    });
  }

  // F-ONL-N5: readyInMinutes is the promise the customer is shown; omitted,
  // the backend uses its own suggestion.
  async acceptOnlineOrder(
    id: string,
    readyInMinutes?: number,
  ): Promise<APIResponse<{ order: Order; kots: StationKOT[]; fire_failed?: boolean; fire_failed_reason?: string; promo_failed?: boolean; promo_code?: string; ready_in_minutes?: number; promised_at?: string }>> {
    return this.request({
      method: 'POST',
      url: `/counter/online-orders/${id}/accept`,
      data: readyInMinutes ? { ready_in_minutes: readyInMinutes } : {},
    });
  }

  // F-ONL-N3: pause / busy. Counter staff may set it — it lives on the inbox
  // route, not the admin settings one.
  async getOnlineOrderingStatus(): Promise<APIResponse<OnlineOrderingStatus>> {
    return this.request({ method: 'GET', url: '/counter/online-orders/ordering-status' });
  }

  async setOnlineOrderingStatus(body: SetOnlineOrderingStatusRequest): Promise<APIResponse<OnlineOrderingStatus>> {
    return this.request({ method: 'PUT', url: '/counter/online-orders/ordering-status', data: body });
  }

  // F-ONL-N6: cancel an accepted online order (customer cancelled, delivery
  // failed). id is the ONLINE order id; the manager PIN rides in the body.
  async cancelOnlineOrder(id: string, body: CancelOnlineOrderRequest): Promise<APIResponse<CancelOnlineOrderResult>> {
    return this.request({ method: 'POST', url: `/counter/online-orders/${id}/cancel`, data: body });
  }

  // F-FE-N193: remove items from a placed order and reprice it. dry_run
  // returns the new totals + warnings without saving. A below-minimum edit
  // without confirm_below_minimum fails with code below_minimum_confirm and
  // the preview on err.payload.
  async editOnlineOrder(id: string, body: OnlineOrderEditRequest): Promise<APIResponse<OnlineOrderEditPreview>> {
    return this.request({
      method: 'POST',
      url: `/counter/online-orders/${id}/edit`,
      data: body,
    });
  }

  async rejectOnlineOrder(id: string, reason?: string): Promise<APIResponse<null>> {
    return this.request({
      method: 'POST',
      url: `/counter/online-orders/${id}/reject`,
      data: reason ? { reason } : {},
    });
  }

  /**
   * Marks an order as having had its optional PRA (Punjab Revenue Authority)
   * tax invoice slip printed. Safe to call multiple times — the backend
   * refreshes the `pra_invoice_printed_at` timestamp and preserves the
   * existing `pra_invoice_number` when the caller doesn't supply a new one.
   *
   * `invoiceNumber` is optional so the current rollout (which prints the slip
   * with a blank number field) can still log the print event; once a real
   * PRA-issued number is wired up, pass it here.
   */
  async markPraInvoicePrinted(
    orderId: string,
    invoiceNumber?: string,
  ): Promise<APIResponse<null>> {
    const body: { pra_invoice_number?: string } = {};
    if (invoiceNumber && invoiceNumber.trim() !== '') {
      body.pra_invoice_number = invoiceNumber.trim();
    }
    return this.request({
      method: 'POST',
      url: `/counter/orders/${orderId}/pra-invoice`,
      data: body,
    });
  }

  async getCounterServers(q?: string): Promise<APIResponse<CounterServer[]>> {
    return this.request({
      method: 'GET',
      url: '/counter/servers',
      params: q ? { q } : undefined,
    });
  }

  /** Latest open order for a table (pending…served) — for counter add-ons to occupied tables. */
  async getActiveOrderForTable(tableId: string): Promise<APIResponse<Order>> {
    return this.request({
      method: 'GET',
      url: `/counter/tables/${tableId}/active-order`,
    });
  }

  /** Open dine-in tab: assigns order number, table_opened_at, empty line items. */
  async openCounterTableTab(body: OpenCounterTableTabRequest): Promise<APIResponse<Order>> {
    return this.request({
      method: 'POST',
      url: '/counter/table-tabs',
      data: body,
    });
  }

  /** Abandon tab before kitchen fire; releases order number for reuse. */
  async cancelCounterOpenTab(orderId: string): Promise<APIResponse<null>> {
    return this.request({
      method: 'POST',
      url: `/counter/orders/${orderId}/cancel-open-tab`,
    });
  }

  /** Reassign active dine-in order to another table. */
  async reassignCounterOrderTable(
    orderId: string,
    body: { table_id: string; notes?: string }
  ): Promise<APIResponse<Order>> {
    return this.request({
      method: 'PATCH',
      url: `/counter/orders/${orderId}/table`,
      data: body,
    });
  }

  /**
   * Customer Directory list — full filter/sort/paginate surface for the
   * /admin/customers page. Supersedes the legacy getAdminCustomers helper.
   */
  async listCustomers(
    params?: import('@/types').CustomerListParams
  ): Promise<APIResponse<import('@/types').CustomerListResponse>> {
    return this.request({
      method: 'GET',
      url: '/admin/customers',
      params,
    });
  }

  /**
   * Manual customer add (marketing edition front door). created=false in the
   * response means the backend's match ladder found an existing row — show
   * "matched existing customer", don't celebrate a duplicate.
   */
  async createAdminCustomer(body: {
    display_name: string;
    phone?: string;
    email?: string;
    birthday?: string;
    whatsapp_opt_in?: boolean;
  }): Promise<APIResponse<{ id: string; created: boolean }>> {
    return this.request({ method: 'POST', url: '/admin/customers', data: body });
  }

  /**
   * CSV guest-book import. Always dry_run first: the same loop runs and rolls
   * back, returning honest {created, matched_updated, skipped} counts.
   */
  async importAdminCustomers(body: {
    csv_text: string;
    mode: 'dry_run' | 'commit';
  }): Promise<
    APIResponse<{
      mode: string;
      created: number;
      matched_updated: number;
      skipped: Array<{ row: number; reason: string }>;
      total_rows: number;
    }>
  > {
    return this.request({ method: 'POST', url: '/admin/customers/import', data: body });
  }

  async getCustomer(
    id: string
  ): Promise<APIResponse<import('@/types').CustomerDetail>> {
    return this.request({ method: 'GET', url: `/admin/customers/${id}` });
  }

  async getCustomerOrderHistory(
    id: string,
    limit?: number
  ): Promise<APIResponse<import('@/types').CustomerOrderHistoryItem[]>> {
    return this.request({
      method: 'GET',
      url: `/admin/customers/${id}/orders`,
      params: limit ? { limit } : undefined,
    });
  }

  async getCustomerLoyaltyHistory(
    id: string
  ): Promise<APIResponse<import('@/types').CustomerLoyaltyHistory>> {
    return this.request({
      method: 'GET',
      url: `/admin/customers/${id}/loyalty-history`,
    });
  }

  /** Manual punch grant (admin/manager + PIN step-up). 1–5 punches, reason
   *  required; lands in the ledger as an attributed source='manual' row. */
  async adjustCustomerLoyalty(
    id: string,
    data: { punches: number; reason: string; pin: string }
  ): Promise<
    APIResponse<{ granted: number; balance: number; threshold: number; adjusted_by: string }>
  > {
    return this.request({
      method: 'POST',
      url: `/admin/customers/${id}/loyalty-adjust`,
      data,
    });
  }

  async updateCustomerNotes(
    id: string,
    notes: string
  ): Promise<APIResponse<null>> {
    return this.request({
      method: 'PUT',
      url: `/admin/customers/${id}/notes`,
      data: { notes },
    });
  }

  /**
   * Inline-edit the core profile fields (name, phone, email, birthday).
   * On success returns the refreshed CustomerDetail. On a phone/email
   * collision the backend returns 409 with `error: 'phone_conflict' |
   * 'email_conflict'` and `data` carrying CustomerUpdateConflict — the
   * caller is expected to read both fields.
   */
  async updateCustomer(
    id: string,
    patch: import('@/types').UpdateCustomerRequest
  ): Promise<APIResponse<import('@/types').CustomerDetail | import('@/types').CustomerUpdateConflict>> {
    return this.request({
      method: 'PATCH',
      url: `/admin/customers/${id}`,
      data: patch,
    });
  }

  // ── Customer address book (F-FE-N30) — same customer_addresses table the
  // storefront account edits, so admin and customer always see the same list.

  async listCustomerAddresses(
    customerId: string
  ): Promise<APIResponse<import('@/types').CustomerAddress[]>> {
    return this.request({
      method: 'GET',
      url: `/admin/customers/${customerId}/addresses`,
    });
  }

  async addCustomerAddress(
    customerId: string,
    body: import('@/types').CustomerAddressRequest
  ): Promise<APIResponse<null>> {
    return this.request({
      method: 'POST',
      url: `/admin/customers/${customerId}/addresses`,
      data: body,
    });
  }

  async updateCustomerAddress(
    customerId: string,
    addressId: string,
    body: import('@/types').CustomerAddressRequest
  ): Promise<APIResponse<null>> {
    return this.request({
      method: 'PUT',
      url: `/admin/customers/${customerId}/addresses/${addressId}`,
      data: body,
    });
  }

  async deleteCustomerAddress(
    customerId: string,
    addressId: string
  ): Promise<APIResponse<null>> {
    return this.request({
      method: 'DELETE',
      url: `/admin/customers/${customerId}/addresses/${addressId}`,
    });
  }

  async setCustomerDefaultAddress(
    customerId: string,
    addressId: string
  ): Promise<APIResponse<null>> {
    return this.request({
      method: 'POST',
      url: `/admin/customers/${customerId}/addresses/${addressId}/default`,
    });
  }

  /** Tile grid for the Segments tab — one row per static segment with its count. */
  async listCustomerSegments(): Promise<
    APIResponse<import('@/types').CustomerSegmentSummary[]>
  > {
    return this.request({ method: 'GET', url: '/admin/customers/segments' });
  }

  /** Drill into a specific segment; returns up to `limit` (default 500) customer rows. */
  async getCustomerSegment(
    key: import('@/types').SegmentKey,
    limit?: number
  ): Promise<APIResponse<import('@/types').CustomerSegmentDetail>> {
    return this.request({
      method: 'GET',
      url: `/admin/customers/segments/${key}`,
      params: limit ? { limit } : undefined,
    });
  }

  /**
   * Build the auth-aware URL for a CSV export. Triggers a browser download
   * via axios blob fetch — bypassing window.open because the request needs
   * the Authorization header that EventSource/window.open can't carry.
   */
  async downloadCustomerSegmentCSV(
    key: import('@/types').SegmentKey
  ): Promise<Blob> {
    const res = await this.client.get(
      `/admin/customers/segments/${key}`,
      { params: { export: 'csv' }, responseType: 'blob' }
    );
    return res.data;
  }

  /** Likely-duplicate suggestions for a given customer (up to 8). */
  async suggestCustomerDuplicates(
    id: string
  ): Promise<APIResponse<import('@/types').CustomerDuplicateCandidate[]>> {
    return this.request({
      method: 'GET',
      url: `/admin/customers/${id}/duplicates`,
    });
  }

  /** Fold the source customer into the target (audit-logged, soft-delete). */
  async mergeCustomers(
    sourceID: string,
    targetID: string
  ): Promise<APIResponse<{ source_id: string; target_id: string }>> {
    return this.request({
      method: 'POST',
      url: `/admin/customers/${sourceID}/merge`,
      data: { target_id: targetID },
    });
  }

  // ── Promo codes ─────────────────────────────────────────────────────
  async listPromoCodes(params?: {
    include_archived?: boolean;
    include_receipt_codes?: boolean;
  }): Promise<APIResponse<import('@/types').PromoCode[]>> {
    return this.request({ method: 'GET', url: '/admin/marketing/promo-codes', params });
  }

  async createPromoCode(
    data: import('@/types').CreatePromoCodeRequest
  ): Promise<APIResponse<import('@/types').PromoCode>> {
    return this.request({ method: 'POST', url: '/admin/marketing/promo-codes', data });
  }

  async updatePromoCode(
    id: string,
    data: import('@/types').UpdatePromoCodeRequest
  ): Promise<APIResponse<import('@/types').PromoCode>> {
    return this.request({ method: 'PUT', url: `/admin/marketing/promo-codes/${id}`, data });
  }

  async archivePromoCode(id: string): Promise<APIResponse<null>> {
    return this.request({ method: 'POST', url: `/admin/marketing/promo-codes/${id}/archive` });
  }

  /** Counter — current promo redemption state for an open order. */
  async getAppliedPromo(
    orderId: string
  ): Promise<APIResponse<import('@/types').AppliedPromoState>> {
    return this.request({
      method: 'GET',
      url: `/counter/orders/${orderId}/applied-promo`,
    });
  }

  /** Counter — apply a typed promo code. Returns 409 with reason on conflict. */
  async applyPromoToOrder(
    orderId: string,
    code: string
  ): Promise<APIResponse<import('@/types').AppliedPromoResult>> {
    return this.request({
      method: 'POST',
      url: `/counter/orders/${orderId}/apply-promo`,
      data: { code },
    });
  }

  /** Counter — remove the applied promo. Idempotent. */
  async removePromoFromOrder(orderId: string): Promise<APIResponse<{ applied: boolean }>> {
    return this.request({
      method: 'DELETE',
      url: `/counter/orders/${orderId}/apply-promo`,
    });
  }

  // ── Bank card promos ────────────────────────────────────────────────
  async listCardPromos(
    includeArchived = false
  ): Promise<APIResponse<import('@/types').CardPromo[]>> {
    return this.request({
      method: 'GET',
      url: `/admin/card-promos${includeArchived ? '?include_archived=true' : ''}`,
    });
  }

  async createCardPromo(
    data: import('@/types').CreateCardPromoRequest
  ): Promise<APIResponse<import('@/types').CardPromo>> {
    return this.request({ method: 'POST', url: '/admin/card-promos', data });
  }

  async updateCardPromo(
    id: string,
    data: import('@/types').UpdateCardPromoRequest
  ): Promise<APIResponse<import('@/types').CardPromo>> {
    return this.request({ method: 'PUT', url: `/admin/card-promos/${id}`, data });
  }

  async archiveCardPromo(id: string): Promise<APIResponse<null>> {
    return this.request({ method: 'POST', url: `/admin/card-promos/${id}/archive` });
  }

  /** Counter — card promos valid right now for this order (quick-pick chips). */
  async getEligibleCardPromos(
    orderId: string
  ): Promise<APIResponse<import('@/types').EligibleCardPromo[]>> {
    return this.request({
      method: 'GET',
      url: `/counter/orders/${orderId}/eligible-card-promos`,
    });
  }

  // ---- Held carts — the counter's "Hold" button (F-CTRL-N72) ----------------
  // A parked, un-placed cart. Not an order: no number, KOT, stock or fiscal row.

  async listHeldCarts(): Promise<APIResponse<import('@/components/counter/heldCarts').HeldCart[]>> {
    return this.request({ method: 'GET', url: '/counter/held-carts' });
  }

  async createHeldCart(
    body: import('@/components/counter/heldCarts').CreateHeldCartRequest
  ): Promise<APIResponse<import('@/components/counter/heldCarts').HeldCart>> {
    return this.request({ method: 'POST', url: '/counter/held-carts', data: body });
  }

  async renameHeldCart(
    id: string,
    label: string
  ): Promise<APIResponse<import('@/components/counter/heldCarts').HeldCart>> {
    return this.request({ method: 'PATCH', url: `/counter/held-carts/${id}`, data: { label } });
  }

  /** Claims the cart for this till. A second till gets held_cart_already_closed. */
  async resumeHeldCart(id: string): Promise<APIResponse<import('@/components/counter/heldCarts').HeldCart>> {
    return this.request({ method: 'POST', url: `/counter/held-carts/${id}/resume` });
  }

  async discardHeldCart(id: string): Promise<APIResponse<import('@/components/counter/heldCarts').HeldCart>> {
    return this.request({ method: 'DELETE', url: `/counter/held-carts/${id}` });
  }

  /** Counter — current card-promo redemption state for an open order. */
  async getAppliedCardPromo(
    orderId: string
  ): Promise<APIResponse<import('@/types').AppliedCardPromoState>> {
    return this.request({
      method: 'GET',
      url: `/counter/orders/${orderId}/applied-card-promo`,
    });
  }

  /** Counter — apply a picked card promo. Returns 409 with reason on conflict. */
  async applyCardPromoToOrder(
    orderId: string,
    cardPromoId: string
  ): Promise<APIResponse<import('@/types').AppliedCardPromoState>> {
    return this.request({
      method: 'POST',
      url: `/counter/orders/${orderId}/apply-card-promo`,
      data: { card_promo_id: cardPromoId },
    });
  }

  /** Counter — remove the applied card promo. Idempotent. */
  async removeCardPromoFromOrder(
    orderId: string
  ): Promise<APIResponse<{ applied: boolean }>> {
    return this.request({
      method: 'DELETE',
      url: `/counter/orders/${orderId}/apply-card-promo`,
    });
  }

  /** Reports — per-bank card-promo reimbursement claim report. */
  async getCardPromoReport(
    from: string,
    to: string
  ): Promise<APIResponse<import('@/types').CardPromoClaimReport>> {
    return this.request({
      method: 'GET',
      url: `/admin/reports/v2/card-promos?from=${from}&to=${to}`,
    });
  }

  // ── Loyalty programme ───────────────────────────────────────────────
  async getLoyaltyConfig(): Promise<APIResponse<import('@/types').LoyaltyConfig>> {
    return this.request({ method: 'GET', url: '/admin/marketing/loyalty/config' });
  }

  async updateLoyaltyConfig(
    data: import('@/types').LoyaltyConfig
  ): Promise<APIResponse<import('@/types').LoyaltyConfig>> {
    return this.request({ method: 'PUT', url: '/admin/marketing/loyalty/config', data });
  }

  /** Product ids that earn stamps. An EMPTY array means "all products earn" —
   *  that is the default and is distinct from "no products earn". */
  async getLoyaltyEligibleProducts(): Promise<APIResponse<string[]>> {
    return this.request({ method: 'GET', url: '/admin/marketing/loyalty/eligible-products' });
  }

  /** Replaces the whole eligible-product set. Sending [] clears it, which
   *  re-enables earning on everything. */
  async setLoyaltyEligibleProducts(
    productIds: string[]
  ): Promise<APIResponse<{ product_count: number }>> {
    return this.request({
      method: 'PUT',
      url: '/admin/marketing/loyalty/eligible-products',
      data: { product_ids: productIds },
    });
  }

  async getLoyaltyStats(all = false): Promise<APIResponse<import('@/types').LoyaltyStats>> {
    return this.request({
      method: 'GET',
      url: '/admin/marketing/loyalty/stats',
      params: all ? { balances: 'all' } : undefined,
    });
  }

  async getCustomerLoyaltyStatus(
    customerId: string
  ): Promise<APIResponse<import('@/types').LoyaltyStatus>> {
    return this.request({
      method: 'GET',
      url: `/counter/customers/${customerId}/loyalty-status`,
    });
  }

  async getWalletStats(): Promise<APIResponse<import('@/types').WalletStats>> {
    return this.request({
      method: 'GET',
      url: `/admin/marketing/loyalty/wallet/stats`,
    });
  }

  /** Force-push the restaurant's card branding to Google, restyling every
   *  card already saved on a customer's phone. Saving the loyalty config does
   *  this automatically; this is the manual retry. */
  async syncGoogleWalletClass(): Promise<
    APIResponse<import('@/types').GoogleClassSyncStatus>
  > {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/loyalty/wallet/google/sync`,
    });
  }

  /** The current wallet broadcast, for the admin composer. */
  async getWalletBroadcast(): Promise<APIResponse<import('@/types').WalletBroadcast>> {
    return this.request({
      method: 'GET',
      url: `/admin/marketing/loyalty/wallet/broadcast`,
    });
  }

  /** Push one marketing message onto every installed wallet card (Google
   *  class notification + Apple pass update whose OFFER-field change lands
   *  on the lock screen). Admin-only; highest blast radius in the product. */
  /** An EMPTY message is an explicit clear — it removes the offer from every
   *  card. `expiresAt` is a YYYY-MM-DD date; the backend retires the message
   *  from newly-issued passes past it and a worker sweeps installed cards. */
  async sendWalletBroadcast(
    message: string,
    expiresAt?: string
  ): Promise<APIResponse<import('@/types').WalletBroadcastResult>> {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/loyalty/wallet/broadcast`,
      data: {
        message,
        // Send end-of-day in the browser's zone so "ends on the 5th" means
        // through the 5th, not from its first second.
        expires_at: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : undefined,
      },
    });
  }

  async resolveLoyaltyCard(
    token: string
  ): Promise<APIResponse<import('@/types').ResolvedLoyaltyCard>> {
    return this.request({
      method: 'POST',
      url: `/counter/loyalty/resolve-card`,
      data: { token },
    });
  }

  /**
   * Record an order-free visit from the staff scan flow (marketing edition —
   * the tenant's real POS isn't ours, so the wallet-card scan IS the visit
   * sensor). Optional self-reported bill amount. 409 visit_cooldown when the
   * guest earned a tap/visit punch moments ago.
   */
  async recordLoyaltyVisit(body: {
    card_token: string;
    amount?: number;
    note?: string;
  }): Promise<
    APIResponse<{
      enabled: boolean;
      customer_id?: string;
      punches?: number;
      threshold?: number;
      eligible?: boolean;
      reward_label?: string;
    }>
  > {
    return this.request({
      method: 'POST',
      url: `/counter/loyalty/record-visit`,
      data: body,
    });
  }

  /** Staff offer redemption: read-only preview for the confirm screen. */
  async getOfferPreview(code: string): Promise<
    APIResponse<{
      code: string;
      description: string;
      discount_type: string;
      discount_value: number;
      guest_name: string;
      guest_phone: string;
    }>
  > {
    return this.request({
      method: 'GET',
      url: `/counter/promo/preview`,
      params: { code },
    });
  }

  /** Redeem a campaign offer at the counter (order-free; writes the
   *  redemption ledger + a visit punch for the bound guest). */
  async redeemOffer(body: {
    code: string;
    amount?: number;
  }): Promise<APIResponse<{ code: string; discount_applied: number }>> {
    return this.request({ method: 'POST', url: `/counter/promo/redeem`, data: body });
  }

  /** Campaign funnel: sent → clicked → redeemed → revenue (order-coupled
   *  AND staff redemptions, minted codes included). */
  async getCampaignFunnel(id: string): Promise<
    APIResponse<{ sent: number; clicked: number; redeemed: number; revenue_pkr: number }>
  > {
    return this.request({
      method: 'GET',
      url: `/admin/marketing/campaigns/${id}/funnel`,
    });
  }

  /** Whether the staff-phone /scan surface is enabled for this venue
   *  (loyalty on AND staff_phone_scan_enabled). Counter-accessible probe —
   *  the full loyalty config stays admin-only. loyalty_enabled is the
   *  programme switch on its own (the till's phone nudge keys off it). */
  async getPhoneScanEnabled(): Promise<APIResponse<{ enabled: boolean; loyalty_enabled?: boolean }>> {
    return this.request({
      method: 'GET',
      url: `/counter/loyalty/phone-scan-enabled`,
    });
  }

  async redeemLoyaltyReward(
    orderId: string
  ): Promise<APIResponse<import('@/types').LoyaltyRedeemResult>> {
    return this.request({
      method: 'POST',
      url: `/counter/orders/${orderId}/redeem-loyalty`,
    });
  }

  async removeLoyaltyRedemption(orderId: string): Promise<APIResponse<{ applied: boolean }>> {
    return this.request({
      method: 'DELETE',
      url: `/counter/orders/${orderId}/redeem-loyalty`,
    });
  }

  async getOrderLoyaltyClaim(
    orderId: string,
  ): Promise<APIResponse<import('@/types').LoyaltyClaimQR>> {
    return this.request({
      method: 'GET',
      url: `/counter/orders/${orderId}/loyalty-claim`,
    });
  }

  async getOrderThankYouPromo(
    orderId: string,
  ): Promise<APIResponse<import('@/types').OrderThankYouCode>> {
    return this.request({
      method: 'GET',
      url: `/counter/orders/${orderId}/thank-you-promo`,
    });
  }

  /** Counter — per-order guest-feedback invite for the receipt QR. Returns
   *  enabled:false (never an error) when the tenant has the receipt QR off or
   *  the guest rated us inside the cooldown. */
  async getOrderReviewInvite(orderId: string): Promise<
    APIResponse<{
      enabled: boolean;
      token?: string;
      url?: string;
      is_scannable: boolean;
      already_rated: boolean;
      with_loyalty?: boolean;
      headline?: string;
      subline?: string;
    }>
  > {
    return this.request({
      method: 'GET',
      url: `/counter/orders/${orderId}/review-invite`,
    });
  }

  // ── Recovery coupon — manager-issued comeback code for service recovery ──
  /** Counter — fetch tier presets, override cap and reasons for the
   *  recovery-coupon action (and whether it's enabled at all). */
  async getRecoveryCouponConfig(): Promise<APIResponse<import('@/types').RecoveryCouponConfig>> {
    return this.request({ method: 'GET', url: '/counter/recovery-coupon-config' });
  }

  /** Counter — issue a recovery coupon. Body carries the manager PIN
   *  (verified server-side); 403 on a bad PIN, 400 needs_customer when no
   *  customer is captured on the order. */
  async issueRecoveryCoupon(
    orderId: string,
    data: { pin: string; tier: string; reason: string; override_value?: number },
  ): Promise<APIResponse<import('@/types').IssuedRecoveryCoupon>> {
    return this.request({
      method: 'POST',
      url: `/counter/orders/${orderId}/recovery-coupon`,
      data,
    });
  }

  /**
   * Fetch the printable loyalty poster as raw HTML. Same shape as
   * `downloadCustomerSegmentCSV` — we go through axios so the JWT
   * Authorization header is attached. The caller wraps the response
   * in a blob URL and opens it in a new tab (window.open with the raw
   * /api/v1/... URL can't carry the Bearer token, so it 401s).
   *
   * `layout` selects the format:
   *   • 'a4'   — full-page wall/counter poster (default)
   *   • 'tent' — A5 table tent (foldable, both faces upright)
   *   • 'card' — postcard-sized counter card (148×100 mm)
   */
  async fetchLoyaltyPosterHTML(
    layout: 'a4' | 'tent' | 'card' = 'a4',
  ): Promise<string> {
    const res = await this.client.get('/admin/marketing/loyalty/poster', {
      params: { layout },
      responseType: 'text',
      transformResponse: [(d) => d], // disable JSON.parse
    });
    return String(res.data ?? '');
  }

  // ── Public links (customer-facing base URL) ────────────────────────

  // F-FE-N195 — Website → App: installs, app users, share of orders from the app.
  async getStorefrontAppStats(days = 30): Promise<APIResponse<import('@/types').StorefrontAppStats>> {
    return this.request({ method: 'GET', url: '/admin/storefront-app/stats', params: { days } });
  }

  async getPublicLinks(): Promise<APIResponse<import('@/types').PublicLinksConfig>> {
    return this.request({ method: 'GET', url: '/admin/settings/public-links' });
  }

  async updatePublicLinks(
    publicBaseURL: string,
  ): Promise<APIResponse<import('@/types').PublicLinksConfig>> {
    return this.request({
      method: 'PUT',
      url: '/admin/settings/public-links',
      data: { public_base_url: publicBaseURL },
    });
  }

  async searchCustomersQuick(
    q: string,
  ): Promise<APIResponse<{ items: import('@/types').CustomerQuickLookup[] }>> {
    return this.request({
      method: 'GET',
      url: `/counter/customers/search`,
      params: { q },
    });
  }

  // ── Campaigns ──────────────────────────────────────────────────────
  async listCampaigns(): Promise<APIResponse<import('@/types').Campaign[]>> {
    return this.request({ method: 'GET', url: '/admin/marketing/campaigns' });
  }

  async getCampaign(id: string): Promise<APIResponse<import('@/types').Campaign>> {
    return this.request({ method: 'GET', url: `/admin/marketing/campaigns/${id}` });
  }

  async createCampaign(
    data: import('@/types').CreateCampaignRequest
  ): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/admin/marketing/campaigns', data });
  }

  async updateCampaign(
    id: string,
    data: import('@/types').UpdateCampaignRequest
  ): Promise<APIResponse<null>> {
    return this.request({ method: 'PUT', url: `/admin/marketing/campaigns/${id}`, data });
  }

  async getCampaignRecipients(
    id: string,
    channel?: import('@/types').CampaignChannel
  ): Promise<APIResponse<import('@/types').CampaignRecipientsResponse>> {
    return this.request({
      method: 'GET',
      url: `/admin/marketing/campaigns/${id}/recipients`,
      params: channel ? { channel } : undefined,
    });
  }

  async dispatchCampaign(
    id: string,
    data: { channel: 'email' | 'whatsapp_api'; recipient_ids?: string[] }
  ): Promise<APIResponse<{ channel: string; queued: number; skipped: number }>> {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/campaigns/${id}/dispatch`,
      data,
    });
  }

  /** Code-shipped WhatsApp template registry (one Bhookly WABA, centrally
   *  approved) — composers never hardcode template names/param shapes. */
  async getWATemplates(): Promise<
    APIResponse<{ templates: import('@/types').WATemplate[] }>
  > {
    return this.request({ method: 'GET', url: '/admin/marketing/wa-templates' });
  }

  /** Autopilot automations — seeded rows, toggle/configure only. */
  async listAutomations(): Promise<
    APIResponse<{
      automations: Array<{
        key: string;
        enabled: boolean;
        channel: 'email' | 'whatsapp_api';
        wa_template_name?: string;
        wa_template_params: string[];
        email_subject: string;
        email_body: string;
        config: Record<string, unknown>;
        sent_last_30d: number;
      }>;
    }>
  > {
    return this.request({ method: 'GET', url: '/admin/marketing/automations' });
  }

  async updateAutomation(
    key: string,
    body: Record<string, unknown>
  ): Promise<APIResponse<null>> {
    return this.request({
      method: 'PUT',
      url: `/admin/marketing/automations/${key}`,
      data: body,
    });
  }

  /** Occasion campaign presets (code-shipped Pakistani-calendar catalog). */
  async listCampaignPresets(): Promise<
    APIResponse<{
      presets: Array<{
        key: string;
        title: string;
        description: string;
        occasion: string;
        suggested_window: string;
        segment_slug: string;
        channel_set: string[];
        wa_template_name: string;
        wa_template_params: string[];
        email_subject: string;
        email_body: string;
      }>;
    }>
  > {
    return this.request({ method: 'GET', url: '/admin/marketing/campaign-presets' });
  }

  /** Copy a preset into a draft campaign; returns the new campaign id. */
  async useCampaignPreset(key: string): Promise<APIResponse<{ id: string }>> {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/campaign-presets/${key}/use`,
    });
  }

  /** Review booster settings + private-feedback inbox. */
  // ── Gift cards (Wave 2, ledger-only) ────────────────────────────────

  async listGiftCards(
    status?: 'active' | 'spent' | 'expired' | 'void' | 'all',
  ): Promise<APIResponse<import('@/types').GiftCardListResponse>> {
    return this.request({
      method: 'GET',
      url: '/admin/marketing/gift-cards',
      params: status ? { status } : undefined,
    });
  }

  async getGiftCard(id: string): Promise<APIResponse<import('@/types').GiftCard>> {
    return this.request({ method: 'GET', url: `/admin/marketing/gift-cards/${id}` });
  }

  async issueGiftCard(body: {
    amount: number;
    customer_id?: string;
    note?: string;
    expires_at?: string;
  }): Promise<APIResponse<{ id: string; code: string; amount: number; balance: number }>> {
    return this.request({ method: 'POST', url: '/admin/marketing/gift-cards', data: body });
  }

  async voidGiftCard(id: string): Promise<APIResponse<unknown>> {
    return this.request({ method: 'POST', url: `/admin/marketing/gift-cards/${id}/void` });
  }

  /** Compensating ledger entry: correct a mis-keyed redemption, or top a
   *  card up. Positive delta adds value, negative removes it. A reason is
   *  required — it lands in the card's permanent history. */
  async adjustGiftCard(
    id: string,
    body: { delta: number; reason: string },
  ): Promise<APIResponse<{ delta: number; balance: number }>> {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/gift-cards/${id}/adjust`,
      data: body,
    });
  }

  /** Attach (or, with an empty id, detach) the customer a card belongs to.
   *  Without a customer there is nobody to send the code to. */
  async attachGiftCardCustomer(id: string, customerId: string): Promise<APIResponse<unknown>> {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/gift-cards/${id}/attach`,
      data: { customer_id: customerId },
    });
  }

  async sendGiftCard(id: string): Promise<APIResponse<{ channel: string }>> {
    return this.request({ method: 'POST', url: `/admin/marketing/gift-cards/${id}/send` });
  }

  /** Staff-side, on the counterLoyalty group — works on the marketing
   *  edition, where there is no checkout of ours to attach to. */
  async previewGiftCard(code: string): Promise<
    APIResponse<{
      id: string;
      code: string;
      balance: number;
      customer_name: string;
      redeemable: boolean;
      blocker: string;
    }>
  > {
    return this.request({
      method: 'GET',
      url: '/counter/loyalty/gift-card/preview',
      params: { code },
    });
  }

  async redeemGiftCard(body: {
    code: string;
    amount: number;
    note?: string;
  }): Promise<
    APIResponse<{ redeemed: number; balance: number; code: string; exhausted: boolean }>
  > {
    return this.request({
      method: 'POST',
      url: '/counter/loyalty/gift-card/redeem',
      data: body,
    });
  }

  /**
   * F-MM-N24: settle part (or all) of a bill from a gift card balance.
   *
   * Deliberately a separate method from processCounterPayment even though it
   * hits the same endpoint: this is the one tender whose response carries the
   * remaining card balance, and typing it apart keeps that shape honest
   * instead of widening every payment response to an optional field nobody
   * else fills. The card debit and the payment row are one transaction
   * server-side, so a failure here has taken nothing off the card.
   */
  async payWithGiftCard(
    orderId: string,
    body: { code: string; amount: number; rate_intent?: 'cash' | 'card' | 'online'; split_guest_no?: number }
  ): Promise<APIResponse<import('@/types').GiftCardPaymentResult>> {
    return this.request({
      method: 'POST',
      url: `/counter/orders/${orderId}/payments`,
      data: {
        payment_method: 'gift_card',
        amount: body.amount,
        gift_card_code: body.code,
        rate_intent: body.rate_intent,
        split_guest_no: body.split_guest_no,
      },
    });
  }

  // ── House accounts (F-MM-N30): staff meal allowances + customer tabs ──

  /** `purpose: 'settle'` lists every TAB with money owed (any status, switch
   *  or not) for Tab payment; the default lists active accounts that may be
   *  charged. */
  async searchCounterHouseAccounts(params: {
    q?: string;
    kind?: import('@/types').HouseAccountKind;
    purpose?: 'settle';
  }): Promise<APIResponse<import('@/types').HouseAccount[]>> {
    return this.request({ method: 'GET', url: '/counter/house-accounts', params });
  }

  async getCounterHouseAccount(
    id: string,
  ): Promise<APIResponse<import('@/types').CounterHouseAccountDetail>> {
    return this.request({ method: 'GET', url: `/counter/house-accounts/${id}` });
  }

  /** Charges the WHOLE open bill to an account. The server reprices it (staff
   *  meals carry no tax or service charge; tabs bill at the account's rate),
   *  completes the order and files it if it is a tab. */
  async chargeOrderToAccount(
    orderId: string,
    body: { account_id: string; manager_pin?: string; note?: string },
  ): Promise<APIResponse<import('@/types').ChargeToAccountResult>> {
    return this.request({ method: 'POST', url: `/counter/orders/${orderId}/charge-account`, data: body });
  }

  /** Reverses a whole staff meal (allowance back, order cancelled, void-logged).
   *  Nothing fiscal — a staff meal is never filed. Manager PIN required. */
  async undoStaffMeal(
    orderId: string,
    body: import('@/types').UndoStaffMealRequest,
  ): Promise<APIResponse<import('@/types').UndoStaffMealResult>> {
    return this.request({ method: 'POST', url: `/counter/orders/${orderId}/undo-staff-meal`, data: body });
  }

  async recordTabSettlement(
    accountId: string,
    body: {
      amount: number;
      method: 'cash' | 'card' | 'online';
      payment_sub_method?: string;
      reference_number?: string;
      note?: string;
    },
  ): Promise<APIResponse<import('@/types').TabSettlementResult>> {
    return this.request({ method: 'POST', url: `/counter/house-accounts/${accountId}/settlements`, data: body });
  }

  async listHouseAccounts(params?: {
    kind?: import('@/types').HouseAccountKind;
    status?: import('@/types').HouseAccountStatus;
    customer_id?: string;
  }): Promise<APIResponse<import('@/types').HouseAccount[]>> {
    return this.request({ method: 'GET', url: '/admin/house-accounts', params });
  }

  async createHouseAccount(body: {
    /** A tab needs one; a staff allowance takes user_id or holder_name instead (F-MM-N31). */
    customer_id?: string;
    kind: import('@/types').HouseAccountKind;
    user_id?: string;
    holder_name?: string;
    holder_phone?: string;
    monthly_allowance?: number;
    credit_limit?: number;
    tab_rate_intent?: import('@/types').TabRateIntent;
    note?: string;
  }): Promise<APIResponse<import('@/types').HouseAccount>> {
    return this.request({ method: 'POST', url: '/admin/house-accounts', data: body });
  }

  async updateHouseAccount(
    id: string,
    body: {
      status?: import('@/types').HouseAccountStatus;
      user_id?: string;
      holder_name?: string;
      holder_phone?: string;
      monthly_allowance?: number;
      credit_limit?: number;
      tab_rate_intent?: import('@/types').TabRateIntent;
      note?: string;
    },
  ): Promise<APIResponse<import('@/types').HouseAccount>> {
    return this.request({ method: 'PATCH', url: `/admin/house-accounts/${id}`, data: body });
  }

  async getHouseAccountStatement(
    id: string,
    params?: { from?: string; to?: string },
  ): Promise<APIResponse<import('@/types').HouseAccountStatement>> {
    return this.request({ method: 'GET', url: `/admin/house-accounts/${id}/statement`, params });
  }

  async getStaffMonthReport(
    month: string,
  ): Promise<APIResponse<{ month: string; rows: import('@/types').StaffMonthRow[] }>> {
    return this.request({ method: 'GET', url: '/admin/house-accounts/reports/staff-month', params: { month } });
  }

  async exportStaffMonthCsv(month: string): Promise<void> {
    await this.downloadAttachment('/admin/house-accounts/reports/staff-month', { month, format: 'csv' }, `staff-meals-${month}.csv`);
  }

  async getTabAgingReport(): Promise<APIResponse<import('@/types').TabAgingRow[]>> {
    return this.request({ method: 'GET', url: '/admin/house-accounts/reports/tab-aging' });
  }

  async exportTabAgingCsv(): Promise<void> {
    await this.downloadAttachment('/admin/house-accounts/reports/tab-aging', { format: 'csv' }, 'tab-aging.csv');
  }

  async getReviewSettings(): Promise<APIResponse<import('@/types').ReviewSettings>> {
    return this.request({ method: 'GET', url: '/admin/marketing/review-settings' });
  }

  async updateReviewSettings(
    body: import('@/types').ReviewSettings,
  ): Promise<APIResponse<unknown>> {
    return this.request({
      method: 'PUT',
      url: '/admin/marketing/review-settings',
      data: body,
    });
  }

  /** The guest-recovery worklist. Filters are all optional; omitting them
   *  returns the most recent page across every state. */
  async listReviewFeedback(params?: {
    state?: import('@/types').RecoveryState;
    rating_max?: number;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }): Promise<APIResponse<{ feedback: import('@/types').GuestFeedbackRow[] }>> {
    return this.request({
      method: 'GET',
      url: '/admin/marketing/review-feedback',
      params,
    });
  }

  /** Issue the apology + customer-bound comeback coupon, and flip the row to
   *  'recovered'. This endpoint is the ONLY writer of that state — which is
   *  what keeps the recovered-revenue figure a measurement rather than
   *  something a dropdown can assert. */
  async recoverGuestFeedback(
    id: string,
    data: {
      tier: string;
      reason: string;
      override_value?: number;
      send_message: boolean;
    },
  ): Promise<APIResponse<import('@/types').RecoveredFeedback>> {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/review-feedback/${id}/recover`,
      data,
    });
  }

  /** Hand-set state. Only 'acknowledged' and 'closed' are accepted. */
  async updateGuestFeedbackState(
    id: string,
    state: 'acknowledged' | 'closed',
  ): Promise<APIResponse<{ state: string }>> {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/review-feedback/${id}/state`,
      data: { state },
    });
  }

  /** Cheap counter-rail poll: are there unhappy guests awaiting a reply, and
   *  did any arrive in the last two hours (i.e. may still be in the room). */
  async getRecoveryPulse(): Promise<
    APIResponse<{ waiting: number; recent_2h: number }>
  > {
    return this.request({ method: 'GET', url: '/admin/marketing/recovery-pulse' });
  }

  async getRecoverySummary(
    from: string,
    to: string,
  ): Promise<APIResponse<import('@/types').RecoverySummary>> {
    return this.request({
      method: 'GET',
      url: '/admin/marketing/recovery-summary',
      params: { from, to },
    });
  }

  // ── Reputation Suite (public Google reviews) ─────────────────────────

  async getReputationSettings(): Promise<
    APIResponse<{
      settings: import('@/types').ReputationSettings;
      google: import('@/types').ReputationGoogleStatus;
    }>
  > {
    return this.request({ method: 'GET', url: '/admin/marketing/reputation-settings' });
  }

  /** Starts the Google Business Profile OAuth round-trip; open the
   *  returned auth_url in a new tab. */
  async startGoogleReputationConnect(): Promise<APIResponse<{ auth_url: string }>> {
    return this.request({
      method: 'POST',
      url: '/admin/marketing/reputation/google/connect',
    });
  }

  /** The listings offered by the last connect (when the account manages
   *  more than one), plus the current selection. */
  async listGoogleReputationLocations(): Promise<
    APIResponse<{
      candidates: import('@/types').ReputationGoogleLocation[];
      selected: string;
      selected_title: string;
    }>
  > {
    return this.request({
      method: 'GET',
      url: '/admin/marketing/reputation/google/locations',
    });
  }

  async selectGoogleReputationLocation(
    account: string,
    location: string,
  ): Promise<APIResponse<unknown>> {
    return this.request({
      method: 'POST',
      url: '/admin/marketing/reputation/google/select',
      data: { account, location },
    });
  }

  async disconnectGoogleReputation(): Promise<APIResponse<unknown>> {
    return this.request({
      method: 'POST',
      url: '/admin/marketing/reputation/google/disconnect',
    });
  }

  async updateReputationSettings(
    body: import('@/types').ReputationSettings,
  ): Promise<APIResponse<unknown>> {
    return this.request({
      method: 'PUT',
      url: '/admin/marketing/reputation-settings',
      data: body,
    });
  }

  /** Re-queues today's poll for immediate pickup — the refresh button.
   *  409 reputation_not_configured until monitoring is enabled with a
   *  place query. */
  async triggerReputationPoll(): Promise<APIResponse<unknown>> {
    return this.request({ method: 'POST', url: '/admin/marketing/reputation/poll-now' });
  }

  /** The reputation inbox: public reviews pulled by the daily poller. */
  async listExternalReviews(params?: {
    state?: import('@/types').RecoveryState;
    rating_max?: number;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }): Promise<
    APIResponse<{
      reviews: import('@/types').ExternalReviewRow[];
      stats: import('@/types').ExternalReviewStats;
    }>
  > {
    return this.request({
      method: 'GET',
      url: '/admin/marketing/external-reviews',
      params,
    });
  }

  /** Hand-set state. Only 'acknowledged' and 'closed' are accepted —
   *  'recovered' is written when a coupon is actually issued. */
  async updateExternalReviewState(
    id: string,
    state: 'acknowledged' | 'closed',
  ): Promise<APIResponse<{ state: string }>> {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/external-reviews/${id}/state`,
      data: { state },
    });
  }

  /** Fuzzy display-name candidates for a review's author. Suggestions
   *  only — nothing is linked until the owner confirms via matchExternalReview. */
  async getExternalReviewMatchSuggestions(
    id: string,
  ): Promise<APIResponse<{ suggestions: import('@/types').ReviewMatchSuggestion[] }>> {
    return this.request({
      method: 'GET',
      url: `/admin/marketing/external-reviews/${id}/match-suggestions`,
    });
  }

  /** Confirm (or clear, with an empty customer_id) the author↔customer link. */
  async matchExternalReview(
    id: string,
    customerId: string,
  ): Promise<APIResponse<unknown>> {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/external-reviews/${id}/match`,
      data: { customer_id: customerId },
    });
  }

  /** Recover a matched public-review author — same coupon machinery and
   *  tier config as guest-feedback recovery, same single writer of
   *  state='recovered'. 409 customer_match_required until a match is confirmed. */
  async recoverExternalReview(
    id: string,
    data: {
      tier: string;
      reason: string;
      override_value?: number;
      send_message: boolean;
    },
  ): Promise<APIResponse<import('@/types').RecoveredFeedback>> {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/external-reviews/${id}/recover`,
      data,
    });
  }

  /** Weekly owner digest settings (schedule + destination). */
  async getDigestSettings(): Promise<
    APIResponse<{
      enabled: boolean;
      owner_phone: string;
      owner_email: string;
      weekday: number;
      hour: number;
    }>
  > {
    return this.request({ method: 'GET', url: '/admin/marketing/digest-settings' });
  }

  async updateDigestSettings(body: {
    enabled: boolean;
    owner_phone: string;
    owner_email: string;
    weekday: number;
    hour: number;
  }): Promise<APIResponse<unknown>> {
    return this.request({
      method: 'PUT',
      url: '/admin/marketing/digest-settings',
      data: body,
    });
  }

  /** Monthly WhatsApp send allowance (used/limit/remaining, resets_at). */
  async getWAAllowance(): Promise<
    APIResponse<{
      limit: number;
      used: number;
      sent: number;
      pending: number;
      remaining: number;
      resets_at: string;
    }>
  > {
    return this.request({ method: 'GET', url: '/admin/marketing/wa-allowance' });
  }

  async logCampaignWhatsAppClick(
    id: string,
    customerIds: string[]
  ): Promise<APIResponse<{ logged: number }>> {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/campaigns/${id}/whatsapp-click`,
      data: { customer_ids: customerIds },
    });
  }

  async previewCampaign(
    id: string
  ): Promise<APIResponse<import('@/types').CampaignPreviewResponse>> {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/campaigns/${id}/preview`,
    });
  }

  async getCounterPricing(): Promise<APIResponse<PricingSettings>> {
    return this.request({ method: 'GET', url: '/counter/pricing' });
  }

  /**
   * F-MM-N28 — what each tender WILL charge for an open order, priced by the
   * checkout's own recompute. The only source an initial bill may print from.
   */
  async getOrderPrebill(orderId: string): Promise<APIResponse<PrebillWire>> {
    return this.request({ method: 'GET', url: `/counter/orders/${orderId}/prebill` });
  }

  async updateCheckoutIntent(
    orderId: string,
    body: { checkout_payment_method: 'cash' | 'card' | 'online' }
  ): Promise<APIResponse<Order>> {
    return this.request({
      method: 'PATCH',
      url: `/counter/orders/${orderId}/checkout-intent`,
      data: body,
    });
  }

  /** Split bill (F-CTRL-N14): persist the split session. Paid markers are
   *  server-owned — this only sends the client-writable shape. */
  async setOrderSplitState(
    orderId: string,
    state: import('@/types').OrderSplitStateInput
  ): Promise<APIResponse<Order>> {
    return this.request({
      method: 'PUT',
      url: `/counter/orders/${orderId}/split`,
      data: state,
    });
  }

  /** Discard the split session (rejected once any guest has paid). */
  async clearOrderSplitState(orderId: string): Promise<APIResponse<Order>> {
    return this.request({
      method: 'DELETE',
      url: `/counter/orders/${orderId}/split`,
    });
  }

  async applyOrderDiscount(
    orderId: string,
    body: { discount_amount?: number; discount_percent?: number }
  ): Promise<APIResponse<Order>> {
    return this.request({
      method: 'PATCH',
      url: `/counter/orders/${orderId}/discount`,
      data: body,
    });
  }

  async applyOrderItemDiscount(
    orderId: string,
    itemId: string,
    body: { discount_amount?: number; discount_percent?: number | null; pin?: string }
  ): Promise<APIResponse<Order>> {
    return this.request({
      method: 'PATCH',
      url: `/counter/orders/${orderId}/items/${itemId}/discount`,
      data: body,
    });
  }

  /**
   * Set or clear the per-order delivery fee override. Pass a number (>= 0, 0
   * allowed for free delivery) to override; pass null to clear the override and
   * revert to the settings default. Server recomputes totals and returns the
   * updated order.
   */
  async setOrderDeliveryFee(
    orderId: string,
    fee: number | null
  ): Promise<APIResponse<Order>> {
    return this.request({
      method: 'PATCH',
      url: `/counter/orders/${orderId}/delivery-fee`,
      data: { delivery_fee: fee },
    });
  }

  /**
   * Set or clear the per-order service charge override. Pass
   * `{ kind: 'rate', value }` (fraction, 0..1) to charge a different
   * percentage on this order, `{ kind: 'amount', value }` (>= 0, 0 = waived)
   * to charge a fixed figure with no percentage printed, or null to clear the
   * override and revert to the settings rate. Server recomputes totals
   * (service, tax, grand total) and returns the updated order.
   */
  async setOrderServiceCharge(
    orderId: string,
    override: ServiceChargeOverride | null
  ): Promise<APIResponse<Order>> {
    const body =
      override == null
        ? {}
        : override.kind === 'rate'
          ? { rate: override.value }
          : { amount: override.value };
    return this.request({
      method: 'PATCH',
      url: `/counter/orders/${orderId}/service-charge`,
      data: body,
    });
  }

  async updateCounterOrderGuest(
    orderId: string,
    body: UpdateCounterOrderGuestRequest
  ): Promise<APIResponse<Order>> {
    return this.request({
      method: 'PATCH',
      url: `/counter/orders/${orderId}/guest`,
      data: body,
    });
  }

  async updateCounterOrderService(
    orderId: string,
    body: UpdateCounterOrderServiceRequest
  ): Promise<APIResponse<Order>> {
    return this.request({
      method: 'PATCH',
      url: `/counter/orders/${orderId}/service`,
      data: body,
    });
  }

  // User management endpoints (Admin only)
  async getUsers(params?: { page?: number; limit?: number; per_page?: number; search?: string; role?: string; active?: 'true' | 'false' }): Promise<APIResponse<User[]>> {
    return this.request({
      method: 'GET',
      url: '/admin/users',
      params,
    });
  }

  async createUser(userData: any): Promise<APIResponse<User>> {
    return this.request({
      method: 'POST',
      url: '/admin/users',
      data: userData,
    });
  }

  async updateUser(id: string, userData: any): Promise<APIResponse<User>> {
    return this.request({
      method: 'PUT',
      url: `/admin/users/${id}`,
      data: userData,
    });
  }

  async deleteUser(id: string): Promise<APIResponse> {
    return this.request({
      method: 'DELETE',
      url: `/admin/users/${id}`,
    });
  }

  // Admin-specific product management
  async createProduct(productData: any): Promise<APIResponse<Product>> {
    return this.request({ method: 'POST', url: '/admin/products', data: productData });
  }

  async updateProduct(id: string, productData: any): Promise<APIResponse<Product>> {
    return this.request({ method: 'PUT', url: `/admin/products/${id}`, data: productData });
  }

  async deleteProduct(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/products/${id}` });
  }

  /**
   * Active stock items a product can be linked to for checkout inventory
   * tracking (migrations/034). Lives under /admin (not /store) so admin AND
   * manager can load it while editing a product.
   */
  async getStockItemOptions(): Promise<APIResponse<Array<{ id: string; name: string; unit: string; quantity_on_hand: number; supplier_id?: string | null; supplier_name?: string; unit_cost?: number | null; cost_from_receipts?: boolean }>>> {
    return this.request({ method: 'GET', url: '/admin/products/stock-item-options' });
  }

  /**
   * Costing health for the whole menu (F-RPT-N25) — for each product, the cost
   * checkout would ACTUALLY snapshot today, which rung of the precedence
   * produced it, and the signals that explain a surprise.
   *
   * Bulk by design: the per-product recipe route below would be one call per
   * row, and this drives a table that shows the entire menu at once.
   */
  async getProductCostingHealth(): Promise<APIResponse<ProductCostingHealth[]>> {
    return this.request({ method: 'GET', url: '/admin/products/costing-health' });
  }

  /**
   * A product's recipe components (migrations/035) — the stock items it draws
   * down per sale, each with its ratio. Hydrates the multi-item tracking
   * section when editing a product.
   */
  async getProductStockComponents(id: string): Promise<APIResponse<Array<{ stock_item_id: string; name: string; unit: string; deduct_qty: number; deduct_unit?: string | null; deduct_qty_entered?: number | null; order_type_scope?: 'all' | 'dine_in' | 'takeaway' }>>> {
    return this.request({ method: 'GET', url: `/admin/products/${id}/stock-components` });
  }

  // ── Recipes (F-MOD-N11) — the Costing & Recipes sheet's own API ──────────
  // A recipe is saved on its own, never as a side-effect of the product form.

  async getProductRecipe(id: string): Promise<APIResponse<ProductRecipe>> {
    return this.request({ method: 'GET', url: `/admin/products/${id}/recipe` });
  }

  async putProductRecipe(
    id: string,
    body: {
      recipe_yield: number;
      components: Array<{ stock_item_id: string; deduct_qty_entered: number; deduct_unit: string; order_type_scope: 'all' | 'dine_in' | 'takeaway' }>;
    },
  ): Promise<APIResponse<ProductRecipe>> {
    return this.request({ method: 'PUT', url: `/admin/products/${id}/recipe`, data: body });
  }

  /**
   * The option's recipe. With `productId` (F-MOD-N13): that product's own
   * recipe for the option when it has one (`own_recipe: true`), else the
   * shared recipe it inherits.
   */
  async getOptionRecipe(optionId: string, productId?: string): Promise<APIResponse<OptionRecipe>> {
    return this.request({
      method: 'GET',
      url: `/admin/modifier-options/${optionId}/recipe`,
      params: productId ? { product_id: productId } : undefined,
    });
  }

  async putOptionRecipe(
    optionId: string,
    body: {
      recipe_multiplier: number;
      components: Array<{ stock_item_id: string; deduct_qty_entered: number; deduct_unit: string; order_type_scope: 'all' | 'dine_in' | 'takeaway'; mode: 'add' | 'replace' }>;
      /** F-MOD-N14: the full swap set; omit to leave swaps as they are. Shared saves only. */
      swaps?: Array<{ from_stock_item_id: string; to_stock_item_id: string }>;
    },
    /**
     * F-MOD-N13: save THIS product's own recipe for the option. Empty
     * components drop the product back onto the shared recipe. The multiplier
     * stays shared and is ignored on a product-scoped save.
     */
    productId?: string,
  ): Promise<APIResponse<OptionRecipe>> {
    return this.request({
      method: 'PUT',
      url: `/admin/modifier-options/${optionId}/recipe`,
      data: body,
      params: productId ? { product_id: productId } : undefined,
    });
  }

  /**
   * Products and options that draw on a stock item — the Inventory drawer's
   * "Used in". Served under /store so the inventory_manager (whose only screen
   * is Inventory) can read it; admin/manager also have an /admin twin.
   */
  async getStockItemUsedIn(stockItemId: string): Promise<APIResponse<StockItemUsage[]>> {
    return this.request({ method: 'GET', url: `/store/stock-items/${stockItemId}/used-in` });
  }

  /**
   * Set ONE stock item's cost from the recipe sheet (admin + manager). Cost
   * only: a manager has no other inventory rights, so the endpoint accepts
   * nothing else (audit E-5). Must be > 0, per the item's own stock unit.
   */
  async updateStockItemCostViaAdmin(id: string, defaultUnitCost: number): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/admin/products/stock-items/${id}/cost`, data: { default_unit_cost: defaultUnitCost } });
  }

  // Admin-specific category management  
  async createCategory(categoryData: any): Promise<APIResponse<Category>> {
    return this.request({ method: 'POST', url: '/admin/categories', data: categoryData });
  }

  async updateCategory(id: string, categoryData: any): Promise<APIResponse<Category>> {
    return this.request({ method: 'PUT', url: `/admin/categories/${id}`, data: categoryData });
  }

  async deleteCategory(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/categories/${id}` });
  }

  // ── Product modifiers / variants (F-MOD-N4) ───────────────────────────────
  //
  // Configuration CRUD sits behind /admin (admin + manager, the same gate as
  // the rest of the menu). The POS-facing read is /products/modifiers, which
  // every order-taking role can reach.

  /** Every group with its options and D5 rules nested. Includes inactive ones. */
  async getModifierGroups(): Promise<APIResponse<ModifierGroup[]>> {
    return this.request({ method: 'GET', url: '/admin/modifier-groups' });
  }

  async createModifierGroup(data: Partial<ModifierGroup>): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/admin/modifier-groups', data });
  }

  async updateModifierGroup(id: string, data: Partial<ModifierGroup>): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/admin/modifier-groups/${id}`, data });
  }

  async deleteModifierGroup(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/modifier-groups/${id}` });
  }

  async createModifierOption(
    groupId: string,
    data: Partial<ModifierOption> & { group_rules?: ModifierOptionGroupRule[] },
  ): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: `/admin/modifier-groups/${groupId}/options`, data });
  }

  async updateModifierOption(
    groupId: string,
    optionId: string,
    data: Partial<ModifierOption> & { group_rules?: ModifierOptionGroupRule[] },
  ): Promise<APIResponse> {
    return this.request({
      method: 'PUT',
      url: `/admin/modifier-groups/${groupId}/options/${optionId}`,
      data,
    });
  }

  /**
   * Replace a group's whole choice list in one write (F-MOD-N12). Rows with an
   * id are updated in place, rows without are created, anything missing is
   * deleted. The grid editor saves once; nothing half-applies.
   */
  async replaceModifierOptions(
    groupId: string,
    options: Array<{
      id?: string
      name: string
      price_delta: number
      is_available: boolean
      is_default: boolean
      group_rules?: ModifierOptionGroupRule[]
    }>,
  ): Promise<APIResponse<{ option_ids: string[] }>> {
    return this.request({ method: 'PUT', url: `/admin/modifier-groups/${groupId}/options`, data: { options } });
  }

  /** Every attachment in the tenant, for the menu matrix (F-MOD-N12). */
  async getModifierAttachments(): Promise<APIResponse<ModifierAttachments>> {
    return this.request({ method: 'GET', url: '/admin/modifier-attachments' });
  }

  async deleteModifierOption(groupId: string, optionId: string): Promise<APIResponse> {
    return this.request({
      method: 'DELETE',
      url: `/admin/modifier-groups/${groupId}/options/${optionId}`,
    });
  }

  /** One product's RESOLVED groups plus the inherited groups it opts out of. */
  async getProductModifierGroups(id: string): Promise<
    APIResponse<{ groups: ResolvedModifierGroup[]; excluded_category_group_ids: string[] }>
  > {
    return this.request({ method: 'GET', url: `/admin/products/${id}/modifier-groups` });
  }

  async setProductModifierGroups(
    id: string,
    body: { group_ids: string[]; excluded_category_group_ids: string[] },
  ): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/admin/products/${id}/modifier-groups`, data: body });
  }

  /** F-MOD-N13: every option this product can be sold with, shared value beside its override. */
  async getProductOptionOverrides(id: string): Promise<APIResponse<ProductOptionOverride[]>> {
    return this.request({ method: 'GET', url: `/admin/products/${id}/option-overrides` });
  }

  /**
   * F-MOD-N13: replace this product's whole override set. A row with every
   * field unset means "use the shared value" and is not stored. Moves what
   * the product's options cost the guest — the editor confirms before saving.
   */
  async putProductOptionOverrides(
    id: string,
    overrides: Array<{ option_id: string; price_delta?: number | null; cost_delta?: number | null; is_available?: boolean | null }>,
  ): Promise<APIResponse<ProductOptionOverride[]>> {
    return this.request({ method: 'PUT', url: `/admin/products/${id}/option-overrides`, data: { overrides } });
  }

  async getCategoryModifierGroups(id: string): Promise<APIResponse<{ group_ids: string[] }>> {
    return this.request({ method: 'GET', url: `/admin/categories/${id}/modifier-groups` });
  }

  /**
   * ⭐ BULK PRICE LEVER. Attaching a size group re-bases every product in the
   * category; attaching a required group makes them all unorderable without a
   * choice. Called WITHOUT confirmPriceImpact this returns 409
   * `modifier_category_price_impact` carrying a per-product before/after
   * preview and writes nothing — show that to the owner and only re-call with
   * confirmPriceImpact once they have agreed. Never pass true on the first
   * call to "save a round trip": the round trip IS the safeguard.
   */
  async setCategoryModifierGroups(
    id: string,
    groupIds: string[],
    confirmPriceImpact = false,
  ): Promise<
    | { outcome: 'saved' }
    | { outcome: 'needs_confirmation'; impact: ModifierCategoryPriceImpact }
  > {
    try {
      await this.request({
        method: 'PUT',
        url: `/admin/categories/${id}/modifier-groups`,
        data: { group_ids: groupIds, confirm_price_impact: confirmPriceImpact },
      });
      return { outcome: 'saved' };
    } catch (err) {
      // The 409 is not a failure — it is the designed answer to "this would
      // change what customers pay". Returned as a value so a caller cannot
      // treat the safeguard as a generic error and swallow it in a toast.
      // Anything else still throws.
      if (
        err instanceof ApiClientError &&
        err.code === 'modifier_category_price_impact' &&
        err.priceImpact
      ) {
        return {
          outcome: 'needs_confirmation',
          impact: err.priceImpact as ModifierCategoryPriceImpact,
        };
      }
      throw err;
    }
  }

  /**
   * The POS configurator's whole catalogue in one call: active groups with
   * options, plus each product's already-resolved group ids.
   *
   * One request on purpose — it is a single TanStack Query key, so
   * idbPersister caches it and the configurator keeps working through an
   * outage. Do not replace this with a per-product fetch.
   */
  async getMenuModifiers(): Promise<APIResponse<ProductModifierConfig>> {
    return this.request({ method: 'GET', url: '/products/modifiers' });
  }

  // Product tags — an overlapping reporting-only label layer (product_tags).
  async getTags(activeOnly = false): Promise<APIResponse<Tag[]>> {
    return this.request({ method: 'GET', url: '/admin/tags', params: activeOnly ? { active_only: true } : {} });
  }

  async createTag(tagData: { name: string; color?: string | null; description?: string | null; sort_order?: number; show_on_storefront?: boolean }): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/admin/tags', data: tagData });
  }

  async updateTag(id: string, tagData: Partial<{ name: string; color: string | null; description: string | null; sort_order: number; is_active: boolean; show_on_storefront: boolean }>): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/admin/tags/${id}`, data: tagData });
  }

  async deleteTag(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/tags/${id}` });
  }

  // Tag-centric item assignment: read/replace the set of products carrying a tag.
  // Drives the Reports tag manager's checkbox picker (the reverse of the
  // per-product tag_ids write path).
  async getTagProducts(id: string): Promise<APIResponse<string[]>> {
    return this.request({ method: 'GET', url: `/admin/tags/${id}/products` });
  }

  async setTagProducts(id: string, productIds: string[]): Promise<APIResponse<{ product_count: number }>> {
    return this.request({ method: 'PUT', url: `/admin/tags/${id}/products`, data: { product_ids: productIds } });
  }

  // Admin products endpoint with pagination
  async getAdminProducts(params?: { page?: number, per_page?: number, limit?: number, search?: string, category_id?: string }): Promise<APIResponse<Product[]>> {
    // Normalize params (handle both per_page and limit)
    const normalizedParams = {
      page: params?.page,
      per_page: params?.per_page || params?.limit,
      search: params?.search,
      category_id: params?.category_id
    }
    
    return this.request({ 
      method: 'GET', 
      url: '/admin/products',
      params: normalizedParams
    });
  }

  // Admin categories endpoint with pagination
  async getAdminCategories(params?: { page?: number, per_page?: number, limit?: number, search?: string, active_only?: boolean }): Promise<APIResponse<Category[]>> {
    // Normalize params (handle both per_page and limit)
    const normalizedParams = {
      page: params?.page,
      per_page: params?.per_page || params?.limit,
      search: params?.search,
      active_only: params?.active_only
    }
    
    return this.request({ 
      method: 'GET', 
      url: '/admin/categories',
      params: normalizedParams
    });
  }

  // Admin tables endpoint with pagination
  async getAdminTables(params?: { page?: number, limit?: number, search?: string, status?: string }): Promise<APIResponse<DiningTable[]>> {
    return this.request({ 
      method: 'GET', 
      url: '/admin/tables',
      params 
    });
  }

  // Admin-specific table management
  async createTable(tableData: any): Promise<APIResponse<DiningTable>> {
    return this.request({ method: 'POST', url: '/admin/tables', data: tableData });
  }

  async updateTable(
    id: string,
    tableData: any,
    options?: { ifMatch?: string | null }
  ): Promise<APIResponse<DiningTable>> {
    const headers: Record<string, string> = {}
    if (options?.ifMatch) headers['If-Match'] = options.ifMatch
    return this.request({
      method: 'PUT',
      url: `/admin/tables/${id}`,
      data: tableData,
      headers: Object.keys(headers).length ? headers : undefined,
    });
  }

  async deleteTable(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/tables/${id}` });
  }

  // Per-table dine-in QR (F-BE-N52). The printed code carries the slug, never
  // the table UUID — that is what makes rotation real.

  /** Idempotent — returns the existing code if the table already has one. */
  async ensureTableQr(tableId: string): Promise<APIResponse<{ qr_slug: string }>> {
    return this.request({ method: 'POST', url: `/admin/tables/${tableId}/qr` });
  }

  /**
   * Mints a NEW slug and kills the old one immediately. The card on that table
   * stops working the moment this returns, so the caller must confirm first.
   */
  async rotateTableQr(tableId: string): Promise<APIResponse<{ qr_slug: string }>> {
    return this.request({ method: 'POST', url: `/admin/tables/${tableId}/qr/rotate` });
  }

  // Store Inventory endpoints
  async getStockCategories(): Promise<APIResponse<StockCategory[]>> {
    return this.request({ method: 'GET', url: '/store/stock-categories' });
  }

  /**
   * admin/manager variants of stock-category list + stock-item create, used by
   * the inline "add inventory item" modal in the product form. They hit /admin
   * (gated admin+manager) rather than /store (gated admin+inventory_manager) so
   * a manager can create+link an item without the store gate. Backend reuses
   * the same StockHandler methods.
   */
  async getStockCategoriesViaAdmin(): Promise<APIResponse<StockCategory[]>> {
    return this.request({ method: 'GET', url: '/admin/products/stock-categories' });
  }

  async createStockItemViaAdmin(data: { category_id?: string; name: string; unit: string; quantity_on_hand?: number; reorder_level?: number; default_unit_cost?: number; supplier_id?: string; notes?: string; tracks_expiry?: boolean; shelf_life_days?: number; opening_expiry_date?: string }): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/admin/products/stock-items', data });
  }

  async listSuppliersViaAdmin(): Promise<APIResponse<Supplier[]>> {
    return this.request({ method: 'GET', url: '/admin/products/suppliers' });
  }

  async createStockCategory(data: { name: string; description?: string; sort_order?: number }): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/store/stock-categories', data });
  }

  async updateStockCategory(id: string, data: Partial<StockCategory>): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/store/stock-categories/${id}`, data });
  }

  async deleteStockCategory(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/store/stock-categories/${id}` });
  }

  /**
   * Fetch EVERY stock item by walking the pages (F-BE-N60).
   *
   * The same trap `getAllProducts` exists for: /store/stock-items is capped at
   * 100 a page and defaults to 20, and asking for 500 in one call returned the
   * DEFAULT — so the purchasing catalogue picker was offering 20 items out of
   * however many the store carries. Clamping on the server raises that to 100;
   * only paging makes it correct.
   */
  async getAllStockItems(
    filters?: { category_id?: string; search?: string; low_stock?: string; stock_health?: 'low' | 'ok'; sort?: 'category' | 'name' | 'on_hand' | 'reorder' | 'unit_cost' | 'avg_cost' | 'expiry'; sort_dir?: 'asc' | 'desc' }
  ): Promise<StockItem[]> {
    return fetchAllPages((page) => this.getStockItems({ ...filters, page, per_page: 100 }));
  }

  async getStockItems(params?: {
    page?: number;
    per_page?: number;
    category_id?: string;
    search?: string;
    low_stock?: string;
    stock_health?: 'low' | 'ok' | 'expiring';
    sort?: 'category' | 'name' | 'on_hand' | 'reorder' | 'unit_cost' | 'avg_cost' | 'expiry';
    sort_dir?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<StockItem[]>> {
    return this.request({ method: 'GET', url: '/store/stock-items', params });
  }

  async getStockItem(id: string): Promise<APIResponse<StockItem>> {
    return this.request({ method: 'GET', url: `/store/stock-items/${id}` });
  }

  async createStockItem(data: { category_id?: string; name: string; unit: string; quantity_on_hand?: number; reorder_level?: number; default_unit_cost?: number; supplier_id?: string; notes?: string; purchase_unit_label?: string; purchase_unit_factor?: number; is_returnable?: boolean; tracks_expiry?: boolean; shelf_life_days?: number; opening_expiry_date?: string }): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/store/stock-items', data });
  }

  // ── Kitchen flow Lane A — item master ─────────────────────────────────────
  // Barcodes and the Open Food Facts lookup answer 404 feature_not_enabled
  // unless FEATURE_KITCHEN_FLOW=1; screens gate on useKitchenFlowEnabled first.
  // Lots are open to every tenant (F-CTRL-N73).

  async listStockItemBarcodes(itemId: string): Promise<APIResponse<StockItemBarcode[]>> {
    return this.request({ method: 'GET', url: `/store/stock-items/${itemId}/barcodes` });
  }

  async addStockItemBarcode(itemId: string, data: { barcode: string; pack_qty?: number; pack_label?: string }): Promise<APIResponse<StockItemBarcode>> {
    return this.request({ method: 'POST', url: `/store/stock-items/${itemId}/barcodes`, data });
  }

  async deleteStockItemBarcode(itemId: string, barcodeId: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/store/stock-items/${itemId}/barcodes/${barcodeId}` });
  }

  /** Resolves a scan. Rejects with code `barcode_not_found` for an unknown code. */
  async lookupBarcode(code: string): Promise<APIResponse<BarcodeMatch>> {
    return this.request({ method: 'GET', url: `/store/barcodes/${encodeURIComponent(code)}` });
  }

  /** Open Food Facts pre-fill for an unknown code — a suggestion, never auto-created. */
  async lookupBarcodeExternal(code: string): Promise<APIResponse<BarcodeExternalInfo>> {
    return this.request({ method: 'GET', url: `/store/barcodes/${encodeURIComponent(code)}/lookup` });
  }

  // Delivery-slip photos on a PO (kitchen flow, migration 136).
  async listPurchaseOrderAttachments(poId: string): Promise<APIResponse<PurchaseOrderAttachment[]>> {
    return this.request({ method: 'GET', url: `/store/purchase-orders/${poId}/attachments` });
  }

  async addPurchaseOrderAttachment(poId: string, data: { content_type: string; data_base64: string }): Promise<APIResponse<PurchaseOrderAttachment>> {
    return this.request({ method: 'POST', url: `/store/purchase-orders/${poId}/attachments`, data });
  }

  async deletePurchaseOrderAttachment(poId: string, attId: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/store/purchase-orders/${poId}/attachments/${attId}` });
  }

  /** The photo itself, as a Blob (an <img> cannot send the Authorization header). */
  async getPurchaseOrderAttachmentBlob(poId: string, attId: string): Promise<Blob> {
    const res = await this.client.get(`/store/purchase-orders/${poId}/attachments/${attId}`, { responseType: 'blob' });
    return res.data as Blob;
  }

  /** locationId 'all' lists the item's lots in every place. */
  async getStockItemLots(itemId: string, locationId?: string): Promise<APIResponse<StockLotsResponse>> {
    return this.request({ method: 'GET', url: `/store/stock-items/${itemId}/lots`, params: locationId ? { location_id: locationId } : undefined });
  }

  /** Correct a lot's expiry date or lot number. expiry_date '' clears it. */
  async updateStockLot(lotId: string, data: { expiry_date?: string; lot_no?: string }): Promise<APIResponse> {
    return this.request({ method: 'PATCH', url: `/store/stock-batches/${lotId}`, data });
  }

  /**
   * Throw away what is left of one lot (or `quantity` of it) as expired stock.
   * `pin` is the manager PIN, needed only when the kitchen flow is on.
   */
  async writeOffStockLot(lotId: string, data: { quantity?: number; note?: string; pin?: string }, idempotencyKey?: string): Promise<APIResponse<WriteOffLotResult>> {
    // idempotencyKey: one per intent, reused on retry, so a lost response
    // never writes the same stock off twice (Batch 5 lane stock).
    return this.request({
      method: 'POST',
      url: `/store/stock-batches/${lotId}/write-off`,
      data,
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    });
  }

  /** Open lots of tracked items that have no expiry date yet. */
  async getUndatedLots(): Promise<APIResponse<UndatedLot[]>> {
    return this.request({ method: 'GET', url: '/store/stock-batches/undated' });
  }

  /** Date many lots at once (all or nothing, 200 max). */
  async dateStockLots(lots: { id: string; expiry_date: string }[]): Promise<APIResponse<{ dated: number }>> {
    return this.request({ method: 'PATCH', url: '/store/stock-batches', data: { lots } });
  }

  async getExpirySettings(): Promise<APIResponse<ExpirySettings>> {
    return this.request({ method: 'GET', url: '/store/expiry-settings' });
  }

  async updateExpirySettings(alertDays: number): Promise<APIResponse<{ alert_days: number }>> {
    return this.request({ method: 'PUT', url: '/store/expiry-settings', data: { alert_days: alertDays } });
  }

  /** Bulk import: dryRun=true previews per-row errors; false commits in one transaction. */
  async importStockItems(fileName: string, fileBase64: string, dryRun: boolean): Promise<APIResponse<StockImportResult>> {
    return this.request({
      method: 'POST',
      url: '/store/stock-items/import',
      params: dryRun ? { dry_run: 1 } : undefined,
      data: { file_name: fileName, file_base64: fileBase64 },
    });
  }

  async downloadStockImportTemplate(): Promise<void> {
    const response = await this.client.get('/store/stock-items/import/template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stock-items-import-template.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  async updateStockItem(id: string, data: Partial<StockItem>): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/store/stock-items/${id}`, data });
  }

  async deleteStockItem(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/store/stock-items/${id}` });
  }

  async purchaseStock(
    itemId: string,
    data: {
      quantity: number;
      unit_cost?: number;
      note?: string;
      expiry_date?: string;
      supplier_id?: string;
      purchase_order_id?: string;
      purchase_order_line_id?: string;
      post_to_expense?: boolean;
    }
  ): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/stock-items/${itemId}/purchase`, data });
  }

  async issueStock(itemId: string, data: { quantity: number; unit?: string; issued_to_user_id?: string; reason?: string; note?: string }): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/stock-items/${itemId}/issue`, data });
  }

  async adjustStock(
    itemId: string,
    data: { quantity_delta: number; unit_cost?: number; reason?: string; note?: string; expiry_date?: string }
  ): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/stock-items/${itemId}/adjust`, data });
  }

  // Record a physical stock-take count. Counts are AUTHORITATIVE (migration
  // 103): on success the book quantity snaps to the counted quantity via a
  // count-linked adjustment. Send `expected_book_quantity` (the book the
  // variance preview was computed against) — if stock moved meanwhile the
  // backend 409s with code `book_quantity_changed` and the fresh
  // `book_quantity` in the error payload, so the dialog re-renders its
  // preview instead of applying a different correction than the one confirmed.
  async recordStockCount(
    itemId: string,
    data: { counted_quantity: number; note?: string; expected_book_quantity?: number }
  ): Promise<APIResponse<{
    id: string;
    stock_item_id: string;
    counted_quantity: number;
    book_quantity: number;
    variance: number;
    applied: boolean;
    adjustment_movement_id: string | null;
    created_at: string;
  }>> {
    return this.request({ method: 'POST', url: `/store/stock-items/${itemId}/count`, data });
  }

  async returnStockToSupplier(
    itemId: string,
    data: {
      supplier_id: string;
      quantity: number;
      unit_cost?: number;
      refund_amount?: number;
      refund_type: 'cash' | 'bank' | 'supplier_credit' | 'no_refund';
      reason: string;
      pin: string;
    }
  ): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/stock-items/${itemId}/supplier-return`, data });
  }

  async getStockAlerts(): Promise<APIResponse<StockAlert[]>> {
    return this.request({ method: 'GET', url: '/store/stock-alerts' });
  }

  /** `days` omitted = the tenant's expiring-soon window (expiry settings). */
  async getExpiringStockBatches(days?: number): Promise<APIResponse<ExpiringBatchAlert[]>> {
    return this.request({ method: 'GET', url: '/store/stock-alerts/expiring', params: { days } });
  }

  /**
   * The stock ledger. `type` accepts a comma list of movement types;
   * `group_sales` collapses per-recipe-component sale rows into one event per
   * order (groups stay atomic under pagination); `include_voided=false` hides
   * voided rows; `item_id` scopes to one item (the drawer's history).
   * Malformed filters are a 400 `invalid_filter`, never silently ignored.
   */
  async getStockMovements(params?: {
    page?: number; per_page?: number; category_id?: string; item_id?: string;
    type?: string; from?: string; to?: string;
    include_voided?: boolean; group_sales?: boolean;
  }): Promise<PaginatedResponse<StockMovement[]>> {
    const q: Record<string, string | number | undefined> = { ...params } as any;
    if (params?.include_voided !== undefined) q.include_voided = String(params.include_voided);
    if (params?.group_sales !== undefined) q.group_sales = String(params.group_sales);
    return this.request({ method: 'GET', url: '/store/stock-reports/movements', params: q });
  }

  /** Moves every item (active + archived) into `target_category_id`, then deletes the source category. */
  async mergeStockCategory(sourceId: string, targetCategoryId: string): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/stock-categories/${sourceId}/merge`, data: { target_category_id: targetCategoryId } });
  }

  /** Persists a full category ordering: sort_order = position in `ids`. */
  async reorderStockCategories(ids: string[]): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: '/store/stock-categories/reorder', data: { ids } });
  }

  async getStockSummary(period?: string): Promise<APIResponse<StockSummary>> {
    return this.request({ method: 'GET', url: '/store/stock-reports/summary', params: { period } });
  }

  /** The Reports tab payload for an inclusive business-date range (YYYY-MM-DD). */
  async getAdvancedStockReport(from: string, to: string): Promise<APIResponse<AdvancedStockReport>> {
    return this.request({ method: 'GET', url: '/store/stock-reports/advanced', params: { from, to } });
  }

  /**
   * Where the three views of the same stock disagree (F-CTRL-N57): the
   * quantity every report values, the movement ledger behind it, and the FIFO
   * cost layers. Read-only — it reports the gap, it does not close it.
   */
  async getStockReconciliation(): Promise<APIResponse<StockReconciliationReport>> {
    return this.request({ method: 'GET', url: '/store/stock-reports/reconciliation' });
  }

  async getInventoryActivity(params?: {
    page?: number;
    per_page?: number;
    action?: string;
    from?: string;
    to?: string;
  }): Promise<PaginatedResponse<InventoryActivityEntry[]>> {
    return this.request({ method: 'GET', url: '/store/inventory-activity', params });
  }

  async voidPurchaseMovement(movementId: string, data: { reason: string }): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/stock-movements/${movementId}/void`, data });
  }

  async correctPurchaseMovementCost(
    movementId: string,
    data: { unit_cost: number; reason: string }
  ): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/stock-movements/${movementId}/correct-purchase-cost`, data });
  }

  async getStoreUsers(): Promise<APIResponse<UserBrief[]>> {
    return this.request({ method: 'GET', url: '/store/users' });
  }

  async listSuppliers(): Promise<APIResponse<Supplier[]>> {
    return this.request({ method: 'GET', url: '/store/suppliers' });
  }

  async createSupplier(data: {
    name: string;
    contact_name?: string;
    phone?: string;
    email?: string;
    notes?: string;
    commission_percent?: number;
    is_consignment?: boolean;
  }): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/store/suppliers', data });
  }

  async updateSupplier(id: string, data: Partial<Supplier>): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/store/suppliers/${id}`, data });
  }

  /** F-MM-N24: what you already owed this vendor when you started on Bhookly.
   *  Becomes one open vendor bill (admin-only; one per vendor). */
  async createSupplierOpeningBalance(
    id: string,
    data: { amount: number; as_of: string; note?: string },
  ): Promise<APIResponse<{ id: string; amount: number; as_of: string }>> {
    return this.request({ method: 'POST', url: `/store/suppliers/${id}/opening-balance`, data });
  }

  async deleteSupplier(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/store/suppliers/${id}` });
  }

  async listPurchaseOrders(params?: { page?: number; per_page?: number; status?: string; supplier_id?: string }): Promise<PaginatedResponse<PurchaseOrderSummary[]>> {
    return this.request({ method: 'GET', url: '/store/purchase-orders', params });
  }

  /** One-step "bought and brought back" purchase: instantly-received PO + the
   *  draft vendor bill, so stock and what-you-owe always move together. */
  async quickPurchase(data: {
    supplier_id: string;
    lines: { stock_item_id: string; quantity: number; unit_cost: number; expiry_date?: string }[];
    invoice_number?: string;
    notes?: string;
    post_to_expense?: boolean;
    idempotency_key?: string;
    /** Kitchen flow lane D; ignored by the backend unless the flag is on. */
    paid_from?: 'vendor_credit' | 'petty_cash';
  }): Promise<APIResponse<QuickPurchaseResult>> {
    return this.request({ method: 'POST', url: '/store/quick-purchase', data });
  }

  // ── Lane D — kitchen flow purchasing (404 feature_not_enabled when off) ──
  async getParLevels(stockItemId: string): Promise<APIResponse<ParLevelRow[]>> {
    return this.request({ method: 'GET', url: `/store/stock-items/${stockItemId}/par-levels` });
  }

  async putParLevels(
    stockItemId: string,
    levels: { location_id: string; min_qty: number | null; max_qty: number | null }[],
  ): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/store/stock-items/${stockItemId}/par-levels`, data: { levels } });
  }

  async getPriceHistory(stockItemId: string, limit = 20): Promise<APIResponse<PriceHistoryRow[]>> {
    return this.request({ method: 'GET', url: `/store/stock-items/${stockItemId}/price-history`, params: { limit } });
  }

  // unsized_count: items that are out with no Least/Most or warn level set, so
  // nothing says how much to buy (shown on the empty "What to buy" list).
  async getOrderSuggestions(): Promise<APIResponse<OrderSuggestion[]> & { unsized_count?: number }> {
    return this.request({ method: 'GET', url: '/store/order-suggestions' });
  }

  async createPurchaseOrdersFromSuggestions(data: {
    lines: FromSuggestionsLine[];
    requisition_id?: string;
    expected_date?: string;
    notes?: string;
    idempotency_key?: string;
  }): Promise<APIResponse<FromSuggestionsResult>> {
    return this.request({ method: 'POST', url: '/store/purchase-orders/from-suggestions', data });
  }

  async getPriceAlerts(days = 30): Promise<APIResponse<{ threshold_pct: number; alerts: PriceAlert[] }>> {
    return this.request({ method: 'GET', url: '/store/price-alerts', params: { days } });
  }

  async listVendorItems(supplierId: string): Promise<APIResponse<VendorItem[]>> {
    return this.request({ method: 'GET', url: `/store/suppliers/${supplierId}/items` });
  }

  async upsertVendorItem(supplierId: string, data: VendorItemInput): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'PUT', url: `/store/suppliers/${supplierId}/items`, data });
  }

  async deleteVendorItem(vendorItemId: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/store/vendor-items/${vendorItemId}` });
  }

  async getPettyCashReport(params?: { from?: string; to?: string }): Promise<APIResponse<PettyCashReport>> {
    return this.request({ method: 'GET', url: '/store/petty-cash', params });
  }

  async getPurchaseOrder(id: string): Promise<APIResponse<PurchaseOrderDetail>> {
    return this.request({ method: 'GET', url: `/store/purchase-orders/${id}` });
  }

  async createPurchaseOrder(data: {
    supplier_id: string;
    expected_date?: string;
    notes?: string;
    lines: { stock_item_id: string; quantity_ordered: number; unit_cost?: number }[];
  }): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/store/purchase-orders', data });
  }

  async approvePurchaseOrder(id: string): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/purchase-orders/${id}/approve` });
  }

  async sendPurchaseOrderWhatsApp(id: string): Promise<APIResponse<{ wa_url: string }>> {
    return this.request({ method: 'POST', url: `/store/purchase-orders/${id}/whatsapp` });
  }

  // Vendor accounts-payable
  async listVendorBills(params?: { supplier_id?: string; status?: string }): Promise<APIResponse<VendorBill[]>> {
    return this.request({ method: 'GET', url: '/store/vendor-bills', params });
  }

  async updateVendorBill(
    id: string,
    data: { invoice_number?: string; bill_date?: string; due_date?: string; amount?: number; notes?: string; void?: boolean },
  ): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/store/vendor-bills/${id}`, data });
  }

  async createVendorPayment(data: {
    supplier_id?: string;
    vendor_bill_id?: string;
    amount: number;
    method?: 'cash' | 'bank';
    note?: string;
    /** B-P1: stable key per submit intent so a retry can't double-pay. */
    idempotency_key?: string;
  }): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/store/vendor-payments', data });
  }

  // #6: sale-time stock deduction failures (dead-letter) — list + resolve.
  async getStockDeductionFailures(): Promise<
    APIResponse<{ id: string; order_number?: string; product_name?: string; reason?: string; created_at: string }[]>
  > {
    return this.request({ method: 'GET', url: '/store/stock-deduction-failures' });
  }

  async resolveStockDeductionFailure(id: string): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/stock-deduction-failures/${id}/resolve` });
  }

  async getVendorLedger(supplierId: string): Promise<APIResponse<VendorLedgerEntry[]>> {
    return this.request({ method: 'GET', url: '/store/vendor-ledger', params: { supplier_id: supplierId } });
  }

  // Multi-sheet .xlsx inventory reports export (Stock, Purchase Orders, Sales,
  // Consumption Variance, Vendor Aging, Vendor Ledger). Streams a blob.
  /**
   * Inventory .xlsx export. Omit `sheet` (or pass 'all') for the full multi-sheet
   * workbook; pass a single report slug (stock | purchase_orders | sales |
   * consumption_variance | vendor_aging | vendor_ledger) to download that one
   * report on its own.
   */
  async exportInventoryReportsXlsx(from: string, to: string, sheet?: string): Promise<void> {
    return this.downloadReportXlsx('/store/inventory-reports/export', from, to, sheet);
  }

  /** Kitchen flow sheets (money included) — admin and the accountant. */
  async exportFlowReportsXlsx(from: string, to: string): Promise<void> {
    return this.downloadReportXlsx('/inventory-flow/reports/export', from, to);
  }

  private async downloadReportXlsx(path: string, from: string, to: string, sheet?: string): Promise<void> {
    const params: Record<string, string> = { from, to };
    if (sheet && sheet !== 'all') params.sheet = sheet;
    let response;
    try {
      response = await this.client.get(path, {
        params,
        responseType: 'blob',
      });
    } catch (error) {
      // The error body arrives as a Blob because of responseType — read it so
      // the real backend message ("range is too large…") reaches the toast
      // instead of a generic "Export failed".
      if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
        try {
          const parsed = JSON.parse(await error.response.data.text()) as { message?: string; error?: string };
          const msg = [parsed.message, parsed.error].filter(Boolean).join(' — ');
          if (msg) throw new ApiClientError(msg);
        } catch (e) {
          if (e instanceof ApiClientError) throw e;
        }
      }
      throw error;
    }
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = response.headers['content-disposition'] as string | undefined;
    const match = cd && /filename="?([^"]+)"?/i.exec(cd);
    a.download = match ? match[1] : `inventory_${sheet ?? 'reports'}_${from}_to_${to}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  // ── Vendor-wise (consignment) reports ────────────────────────────────────
  async getVendorSalesReport(supplierId: string, from: string, to: string): Promise<APIResponse<VendorSalesReport>> {
    return this.request({ method: 'GET', url: '/store/vendor-reports/sales', params: { supplier_id: supplierId, from, to } });
  }

  async getVendorStockReport(supplierId: string): Promise<APIResponse<VendorStockReport>> {
    return this.request({ method: 'GET', url: '/store/vendor-reports/stock', params: { supplier_id: supplierId } });
  }

  /** Generate (idempotently) a consignment settlement bill = the vendor's payout for the period. */
  async createVendorSettlement(supplierId: string, from: string, to: string): Promise<APIResponse<{ settlement: VendorSettlement; already_existed?: boolean }>> {
    return this.request({ method: 'POST', url: '/store/vendor-reports/settlement', params: { supplier_id: supplierId, from, to } });
  }

  /** Streams a single-report .xlsx (sales or stock) so the two download separately. */
  async exportVendorReportXlsx(report: 'sales' | 'stock', supplierId: string, from?: string, to?: string): Promise<void> {
    const params: Record<string, string> = { report, supplier_id: supplierId };
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await this.client.get('/store/vendor-reports/export', { params, responseType: 'blob' });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = response.headers['content-disposition'] as string | undefined;
    const match = cd && /filename="?([^"]+)"?/i.exec(cd);
    a.download = match ? match[1] : `vendor_${report}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  // Per-vendor .xlsx statement for a date range: running-balance Ledger, Items
  // Purchased, and Payments. Streams a blob.
  async exportVendorLedgerXlsx(supplierId: string, from: string, to: string): Promise<void> {
    const response = await this.client.get('/store/vendor-ledger/export', {
      params: { supplier_id: supplierId, from, to },
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = response.headers['content-disposition'] as string | undefined;
    const match = cd && /filename="?([^"]+)"?/i.exec(cd);
    a.download = match ? match[1] : `vendor_ledger_${from}_to_${to}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  // Branded vendor statement PDF (F-BE-N33). Admin-only on the backend —
  // callers hide the buttons for other roles. Streams a blob download.
  async exportVendorStatementPdf(supplierId: string, from: string, to: string): Promise<void> {
    const response = await this.client.get('/store/vendor-statement/pdf', {
      params: { supplier_id: supplierId, from, to },
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = response.headers['content-disposition'] as string | undefined;
    const match = cd && /filename="?([^"]+)"?/i.exec(cd);
    a.download = match ? match[1] : `vendor_statement_${from}_to_${to}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * wa.me deeplink with a text summary of the same figures as the statement
   * PDF. wa.me cannot attach a file — the caller downloads the PDF and the
   * owner attaches it manually in WhatsApp.
   */
  async sendVendorStatementWhatsApp(supplierId: string, from: string, to: string): Promise<APIResponse<{ wa_url: string }>> {
    return this.request({ method: 'POST', url: '/store/vendor-statement/whatsapp', params: { supplier_id: supplierId, from, to } });
  }

  async submitPurchaseOrder(id: string): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/purchase-orders/${id}/submit` });
  }

  async cancelPurchaseOrder(id: string): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/purchase-orders/${id}/cancel` });
  }

  async deletePurchaseOrder(id: string, data: { pin: string; reason: string }): Promise<APIResponse<{ authorized_by?: string }>> {
    return this.request({ method: 'DELETE', url: `/store/purchase-orders/${id}`, data });
  }

  async receivePurchaseOrder(
    id: string,
    data: { lines: { line_id: string; quantity_received: number; unit_cost?: number; expiry_date?: string; variance_reason?: string; lot_no?: string }[]; finalize?: boolean; idempotency_key?: string; post_to_expense?: boolean }
  ): Promise<APIResponse<PoReceiveResult>> {
    return this.request({ method: 'POST', url: `/store/purchase-orders/${id}/receive`, data });
  }

  async closePurchaseOrder(id: string, reason: string): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/purchase-orders/${id}/close`, data: { reason } });
  }

  async returnPurchaseOrderLine(
    id: string,
    data: {
      purchase_order_line_id: string;
      quantity: number;
      unit_cost?: number;
      refund_amount?: number;
      refund_type: 'cash' | 'bank' | 'supplier_credit' | 'no_refund';
      reason: string;
      pin: string;
    }
  ): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/purchase-orders/${id}/returns`, data });
  }

  // Receipt scans (F-RPT-N43), behind FEATURE_RECEIPT_SCAN.
  async uploadReceiptScan(data: { content_type: string; data_base64: string; note?: string }): Promise<APIResponse<{ id: string; status: string; possible_duplicates: ReceiptDuplicate[] }>> {
    return this.request({ method: 'POST', url: '/receipt-scans', data, timeout: 60_000 });
  }

  async listReceiptScans(params?: { status?: string }): Promise<APIResponse<ReceiptScanList>> {
    return this.request({ method: 'GET', url: '/receipt-scans', params });
  }

  async getReceiptScan(id: string): Promise<APIResponse<{ scan: ReceiptScan; possible_duplicates: ReceiptDuplicate[] }>> {
    return this.request({ method: 'GET', url: `/receipt-scans/${id}` });
  }

  /** How many receipts wait on this user (nav badge), and how many are being read. */
  async getReceiptScanSummary(): Promise<APIResponse<{ waiting: number; reading: number }>> {
    return this.request({ method: 'GET', url: '/receipt-scans/summary' });
  }

  async getReceiptScanOptions(): Promise<APIResponse<ReceiptScanOptions>> {
    return this.request({ method: 'GET', url: '/receipt-scans/options' });
  }

  /** The receipt photo as a blob URL (the route needs the auth header). */
  async getReceiptScanImageUrl(id: string): Promise<string> {
    const res = await this.client.get(`/receipt-scans/${id}/image`, { responseType: 'blob' });
    return URL.createObjectURL(res.data as Blob);
  }

  async approveReceiptExpenses(id: string, data: ApproveReceiptExpensesInput): Promise<APIResponse<{ expense_ids: string[]; status: string }>> {
    return this.request({ method: 'POST', url: `/receipt-scans/${id}/approve-expenses`, data });
  }

  async approveReceiptStock(id: string, data: ApproveReceiptStockInput): Promise<APIResponse<{ purchase_order_id: string; status: string }>> {
    return this.request({ method: 'POST', url: `/receipt-scans/${id}/approve-stock`, data });
  }

  async discardReceiptScan(id: string): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/receipt-scans/${id}/discard` });
  }

  async retryReceiptScan(id: string): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/receipt-scans/${id}/retry` });
  }

  // Expense endpoints
  async getExpenses(params?: {
    page?: number;
    per_page?: number;
    category?: string;
    from?: string;
    to?: string;
    search?: string;
    payment_method?: string;
    vendor_id?: string;
    sort_by?: 'expense_date' | 'amount' | 'category' | 'description' | 'created_by' | 'type' | 'created_at';
    sort_dir?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Expense[]>> {
    return this.request({ method: 'GET', url: '/admin/expenses', params });
  }

  async createExpense(data: {
    category: string;
    amount: number;
    description?: string;
    expense_date?: string;
    recorded_at?: string;
    vendor_id?: string;
    payee?: string;
    payment_method?: string;
    reference_no?: string;
    notes?: string;
  }): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/admin/expenses', data });
  }

  async updateExpense(id: string, data: {
    category?: string;
    amount?: number;
    description?: string;
    expense_date?: string;
    recorded_at?: string;
    /** "" clears the column; undefined leaves it untouched. */
    vendor_id?: string;
    payee?: string;
    payment_method?: string;
    reference_no?: string;
    notes?: string;
  }): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/admin/expenses/${id}`, data });
  }

  /**
   * Date-ranged, unpaginated expense ledger export (F-RPT-N35). Honours the
   * same filters as getExpenses. Streams .xlsx by default, .csv on request —
   * the server names the file after the brand and the window.
   */
  async exportExpenses(params: {
    from?: string;
    to?: string;
    category?: string;
    search?: string;
    payment_method?: string;
    vendor_id?: string;
    format?: 'xlsx' | 'csv';
  }): Promise<void> {
    await this.downloadAttachment('/admin/expenses/export', params, `expenses.${params.format ?? 'xlsx'}`);
  }

  /** P&L statement workbook (F-RPT-N35): statement vs prior period, by-period
   *  table, opex by category, and a Basis sheet defining every line. */
  async exportPnLXlsx(params: { from?: string; to?: string; period?: string }): Promise<void> {
    await this.downloadAttachment('/admin/reports/pnl/export', params, 'pnl.xlsx');
  }

  /** Shared attachment download: honours the server's Content-Disposition. */
  private async downloadAttachment(url: string, params: Record<string, string | undefined>, fallback: string): Promise<void> {
    const response = await this.client.get(url, { params, responseType: 'blob' });
    const blob = response.data as Blob;
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    const cd = response.headers['content-disposition'] as string | undefined;
    const match = cd && /filename="?([^"]+)"?/i.exec(cd);
    a.download = match ? match[1] : fallback;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  }

  async deleteExpense(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/expenses/${id}` });
  }

  /** F-RPT-N37: totals for exactly the ledger filter — same predicate as getExpenses / exportExpenses. */
  /** F-RPT-N46: stock items whose purchase price moved in the period. */
  async getPriceChanges(params?: { from?: string; to?: string }): Promise<APIResponse<PriceChangesReport>> {
    return this.request({ method: 'GET', url: '/admin/expenses/price-changes', params });
  }

  async getExpenseSummary(params?: {
    from?: string;
    to?: string;
    category?: string;
    search?: string;
    payment_method?: string;
    vendor_id?: string;
  }): Promise<APIResponse<ExpenseSummary>> {
    return this.request({ method: 'GET', url: '/admin/expenses/summary', params });
  }

  async getExpenseCategories(): Promise<APIResponse<{ category: string; count: number; total: number }[]>> {
    return this.request({ method: 'GET', url: '/admin/expenses/categories' });
  }

  async getExpenseCategoryDefinitions(): Promise<APIResponse<ExpenseCategoryDefinition[]>> {
    return this.request({ method: 'GET', url: '/admin/expense-category-definitions' });
  }

  async createExpenseCategoryDefinition(data: { label: string; color?: string; sort_order?: number }): Promise<APIResponse<{ id: string; slug: string }>> {
    return this.request({ method: 'POST', url: '/admin/expense-category-definitions', data });
  }

  async updateExpenseCategoryDefinition(
    id: string,
    data: { label?: string; color?: string; sort_order?: number; is_active?: boolean; is_inventory?: boolean }
  ): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/admin/expense-category-definitions/${id}`, data });
  }

  async deleteExpenseCategoryDefinition(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/expense-category-definitions/${id}` });
  }

  // Daily Closing endpoints (read-only — the legacy daily_closings ledger
  // is now mirrored from the new dayops lifecycle on lock-close).
  async getDailyClosings(params?: { page?: number; per_page?: number }): Promise<PaginatedResponse<DailyClosing[]>> {
    return this.request({ method: 'GET', url: '/admin/daily-closings', params });
  }

  async getCurrentDayStatus(): Promise<APIResponse<CurrentDayStatus>> {
    return this.request({ method: 'GET', url: '/admin/daily-closings/current' });
  }

  async getDailyClosingByDate(date: string): Promise<APIResponse<DailyClosing>> {
    return this.request({ method: 'GET', url: `/admin/daily-closings/${date}` });
  }

  // ── Day Operations (business_days lifecycle) ────────────────────────
  // The Day Close page uses these. Counter (`/day-ops/current`) reads
  // the same status pill via a counter-scoped route mounted in routes.go.

  /** Returns the currently-open or staged business day, or null when none. */
  async getDayOpsCurrent(): Promise<APIResponse<BusinessDay | null>> {
    return this.request({ method: 'GET', url: '/admin/day-ops/current' });
  }

  /** Counter-scope read of the same data — accessible to counter staff. */
  async getDayOpsCurrentCounter(): Promise<APIResponse<BusinessDay | null>> {
    return this.request({ method: 'GET', url: '/day-ops/current' });
  }

  /** Real-value open orders sitting open longer than `hours` (default 24) — the aging alert. */
  async getAgingOpenOrders(hours = 24): Promise<APIResponse<AgingOpenOrdersResult>> {
    return this.request({ method: 'GET', url: `/admin/day-ops/aging-open-orders?hours=${hours}` });
  }

  /** Card/online payments swiped on a day but booked earlier — day-close tender reconciliation. */
  async getTenderReconciliation(dayId: string): Promise<APIResponse<TenderReconResult>> {
    return this.request({ method: 'GET', url: `/admin/day-ops/days/${dayId}/tender-reconciliation` });
  }

  async openBusinessDay(data: {
    opening_cash: number;
    notes?: string;
  }): Promise<APIResponse<BusinessDay>> {
    return this.request({ method: 'POST', url: '/admin/day-ops/open', data });
  }

  async getBusinessDay(id: string): Promise<APIResponse<BusinessDay>> {
    return this.request({ method: 'GET', url: `/admin/day-ops/days/${id}` });
  }

  async listDayCashMovements(id: string): Promise<APIResponse<CashDrawerMovement[]>> {
    return this.request({
      method: 'GET',
      url: `/admin/day-ops/days/${id}/cash-movements`,
    });
  }

  /** F-FE-N188: the reasons this restaurant has used before, most used first. */
  async getCashMovementReasons(): Promise<APIResponse<{
    movement_type: 'paid_in' | 'paid_out';
    reason: string;
    expense_category: string;
    uses: number;
  }[]>> {
    return this.request({ method: 'GET', url: '/admin/day-ops/cash-movements/reasons' });
  }

  async addDayCashMovement(data: {
    movement_type: 'paid_in' | 'paid_out';
    amount: number;
    reason: string;
    notes?: string;
    expense_category?: string;
    /** F-CTRL-N75: what a stock cash-out bought. The amount must be its total. */
    purchase?: StockCashOutPurchase;
  }, key?: string): Promise<APIResponse<CashDrawerMovement | { movement_id: string; purchase: MarketPurchase }>> {
    if (data.purchase) {
      return this.inventoryFlowMutate('POST', '/admin/day-ops/cash-movements', data, key);
    }
    return this.request({ method: 'POST', url: '/admin/day-ops/cash-movements', data });
  }

  /** F-CTRL-N75: add the items a stock cash-out bought. Writes stock, no money. */
  async addCashMovementItems(
    id: string,
    data: { purchase: StockCashOutPurchase; accept_difference?: boolean; difference_note?: string },
    key?: string,
  ): Promise<APIResponse<MarketPurchase>> {
    return this.inventoryFlowMutate('POST', `/admin/day-ops/cash-movements/${id}/items`, data, key);
  }

  /** F-CTRL-N75: stock cash-outs whose items were never added, across days. */
  async getStockCashOutsNeedingItems(): Promise<APIResponse<CashDrawerMovement[]>> {
    return this.request({ method: 'GET', url: '/admin/day-ops/stock-cash-outs/needing-items' });
  }

  async updateCashMovement(
    id: string,
    data: { amount: number; reason: string; notes?: string },
  ): Promise<APIResponse<CashDrawerMovement>> {
    return this.request({ method: 'PUT', url: `/admin/day-ops/cash-movements/${id}`, data });
  }

  async deleteCashMovement(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/day-ops/cash-movements/${id}` });
  }

  async listDayAudit(id: string): Promise<APIResponse<DayCloseAuditEntry[]>> {
    return this.request({ method: 'GET', url: `/admin/day-ops/days/${id}/audit` });
  }

  async stageCloseDay(
    id: string,
    data: {
      counted_cash: number;
      // Required (F-CTRL-N33). Every tender is counted by a human — enter 0 if
      // it took nothing. The backend rejects an omitted field with 422
      // `tender_count_required`; making them non-optional here means a caller
      // that forgets fails at compile time instead of at the till.
      counted_card: number;
      counted_online: number;
      /**
       * F-CTRL-N67: the partner-app (Foodpanda) total. Optional — omit when the
       * day had no marketplace sales; the backend stores a NULL variance ("not
       * counted"), never grades it, and never blocks the lock on it.
       */
      counted_marketplace?: number;
      notes?: string;
    },
  ): Promise<APIResponse<BusinessDay>> {
    return this.request({
      method: 'POST',
      url: `/admin/day-ops/days/${id}/stage-close`,
      data,
    });
  }

  async lockCloseDay(id: string): Promise<APIResponse<BusinessDay>> {
    return this.request({
      method: 'POST',
      url: `/admin/day-ops/days/${id}/lock-close`,
    });
  }

  /**
   * Close a shift mid-day (F-CTRL-N29) — a drawer reconciliation at handover.
   * The day stays open. counted_card / counted_online are optional (skippable);
   * cash_drop is the amount moved to the safe (0 = none, next shift opens on the
   * full counted amount). Attributed to the logged-in user, no PIN.
   */
  async shiftCloseDay(
    id: string,
    data: {
      counted_cash: number;
      // Required — same rule as the day close (F-CTRL-N33).
      counted_card: number;
      counted_online: number;
      cash_drop?: number;
      notes?: string;
    },
  ): Promise<APIResponse<ShiftClosing>> {
    return this.request({
      method: 'POST',
      url: `/admin/day-ops/days/${id}/shift-close`,
      data,
    });
  }

  async getShiftClosings(id: string): Promise<APIResponse<ShiftClosing[]>> {
    return this.request({
      method: 'GET',
      url: `/admin/day-ops/days/${id}/shift-closings`,
    });
  }

  /** Undo the most-recent shift close (reverses its cash drop) while the day is open. */
  async undoLastShiftClose(id: string): Promise<APIResponse<null>> {
    return this.request({
      method: 'DELETE',
      url: `/admin/day-ops/days/${id}/shift-closings/last`,
    });
  }

  /**
   * Build the printable Z report payload for a staged or closed day.
   * On first call for a closed day, a monotonic Z sequence is assigned;
   * subsequent calls return the same sequence (idempotent reprints).
   */
  async getZReport(id: string): Promise<APIResponse<ZReportPayload>> {
    return this.request({
      method: 'GET',
      url: `/admin/day-ops/days/${id}/z-report`,
    });
  }

  async reopenBusinessDay(
    id: string,
    data: { pin: string },
  ): Promise<APIResponse<BusinessDay>> {
    return this.request({
      method: 'POST',
      url: `/admin/day-ops/days/${id}/reopen`,
      data,
    });
  }

  /**
   * Force-close a stale prior-date day WITHOUT recording a cash count.
   * Use this only when a count is genuinely impossible (shop was closed,
   * drawer emptied, leftover setup-time data). Backend constraints:
   * business_date must be strictly < today, reason must be 4–500 chars
   * after trim, and the PIN must belong to an active admin or manager.
   * On success the day's business_days row flips to status='closed' with
   * counted_cash / cash_variance left NULL — reports use that NULL
   * signature to distinguish "no count" from "counted zero."
   */
  async forceCloseBusinessDay(
    id: string,
    data: { pin: string; reason: string },
  ): Promise<APIResponse<BusinessDay>> {
    return this.request({
      method: 'POST',
      url: `/admin/day-ops/days/${id}/force-close`,
      data,
    });
  }

  /**
   * Daily Closing handoff. The current session (typically a cashier) sends
   * a manager/admin PIN; on success the response carries a fresh JWT for
   * that manager user. Call setAuth(token, user) with the response to swap
   * the device into the manager's session, then navigate to /admin/day-close.
   */
  async elevateSession(pin: string): Promise<APIResponse<LoginResponse>> {
    return this.request({
      method: 'POST',
      url: '/admin/day-ops/elevate-session',
      data: { pin },
    });
  }

  async listDayOpsHistory(params?: {
    limit?: number;
    offset?: number;
    /** YYYY-MM-DD — when both from & to are set, the backend returns all
     *  rows in that inclusive range and ignores limit/offset. */
    from?: string;
    to?: string;
  }): Promise<PaginatedResponse<BusinessDay[]>> {
    return this.request({ method: 'GET', url: '/admin/day-ops/history', params });
  }

  async getDayOpsHistoryBounds(): Promise<
    APIResponse<{ earliest?: string | null; latest?: string | null }>
  > {
    return this.request({ method: 'GET', url: '/admin/day-ops/history/bounds' });
  }

  // P&L Report endpoint
  async getPnLReport(params?: { period?: string; from?: string; to?: string }): Promise<APIResponse<PnLReport>> {
    return this.request({ method: 'GET', url: '/admin/reports/pnl', params });
  }

  /** F-RPT-N41: the cost-of-goods basis in force today, with its history. */
  async getCogsBasis(): Promise<APIResponse<CogsBasisState>> {
    return this.request({ method: 'GET', url: '/admin/finance/cogs-basis' });
  }

  /** F-RPT-N41: admin only. effective_from defaults to the current business date.
   *  F-RPT-N42: stock_categories (slugs) are counted as stock from effective_from on a switch to purchases. */
  async setCogsBasis(data: { basis: CogsBasis; effective_from?: string; stock_categories?: string[] }): Promise<APIResponse<CogsBasisState>> {
    return this.request({ method: 'POST', url: '/admin/finance/cogs-basis', data });
  }

  /** F-RPT-N42: admin only. Count more categories as stock on a purchases-basis tenant, from `from` (default: the current period's start). */
  async classifyStockCategories(data: { slugs: string[]; from?: string }): Promise<APIResponse<CogsBasisState>> {
    return this.request({ method: 'POST', url: '/admin/finance/cogs-basis/stock-categories', data });
  }

  /** F-RPT-N37: an explicit from/to (YYYY-MM-DD) wins over period_days, so the charts share the page's period bar. */
  async getExpenseIntelligence(params?: { period_days?: number; from?: string; to?: string }): Promise<APIResponse<ExpenseIntelligenceReport>> {
    return this.request({ method: 'GET', url: '/admin/reports/expense-intelligence', params });
  }

  // KOT endpoints
  /**
   * Fire a KOT. Pass `course` to fire only that course (hold-and-fire); omit to
   * fire all unsent lines.
   *
   * `relayCounterPrint` asks the backend to queue counter-bound slips for the
   * counter terminal to print instead of returning them for THIS client to
   * print (F-FE-N60). Set it when the client cannot reach the counter's printer
   * — a tablet or a PWA. Leave it off on the desktop counter, which prints its
   * own paper exactly as it always has.
   */
  async fireKOT(orderId: string, course?: number, relayCounterPrint?: boolean): Promise<APIResponse<FireKOTResponse>> {
    return this.request({
      method: 'POST',
      url: `/orders/${orderId}/fire-kot`,
      params: course && course > 0 ? { course } : undefined,
      data: relayCounterPrint ? { relay_counter_print: true } : undefined,
    });
  }

  // ── Counter print relay (F-FE-N60) ───────────────────────────────────────

  /**
   * Take a batch of relayed slips to print. Only the counter terminal calls
   * this, and only when its print agent is switched on.
   */
  async claimPrintJobs(terminalId: string, limit = 10): Promise<APIResponse<{ jobs: PrintJob[] }>> {
    return this.request({
      method: 'POST',
      url: '/print-jobs/claim',
      data: { terminal_id: terminalId, limit },
    });
  }

  /**
   * Report what became of one claimed slip. `terminalId` must be the value
   * passed to claimPrintJobs: the backend only applies the ack while this
   * terminal still holds the claim (F-BE-N79).
   */
  async ackPrintJob(id: string, ok: boolean, error?: string, terminalId?: string): Promise<APIResponse> {
    return this.request({
      method: 'POST',
      url: '/print-jobs/ack',
      data: { id, ok, error, terminal_id: terminalId },
    });
  }

  /** Poll the fate of slips this client relayed, so the waiter can be told. */
  async getPrintJobStatus(ids: string[]): Promise<APIResponse<PrintJobStatusResponse>> {
    return this.request({
      method: 'GET',
      url: '/print-jobs/status',
      params: { ids: ids.join(',') },
    });
  }

  /** Admin audit of the relay for the open business day. */
  async getPrintJobsAudit(params?: { status?: string; limit?: number }): Promise<APIResponse<{ jobs: PrintJobAudit[] }>> {
    return this.request({ method: 'GET', url: '/admin/print-jobs', params });
  }

  /**
   * Append items to an existing order.
   *
   * `offlineOpId` makes the call retry-safe for offline replay (F-CTRL-N37):
   * the backend claims it inside the same transaction as the insert, so a
   * retried replay is rejected with `offline_order_duplicate` rather than
   * appending the same items — and the food being made — twice. Omitted on
   * every live path.
   */
  async addItemsToOrder(
    orderId: string,
    items: {
      product_id: string;
      quantity: number;
      special_instructions?: string;
      course_number?: number;
      /**
       * Chosen options. Required groups are enforced server-side on this path
       * (`resolveStrict`), so a line that omits them is rejected outright with
       * `modifier_required_group_missing` — not silently mispriced.
       */
      modifiers?: CreateOrderItemModifier[];
    }[],
    offlineOpId?: string,
  ): Promise<APIResponse> {
    return this.request({
      method: 'POST',
      url: `/orders/${orderId}/items`,
      data: offlineOpId ? { items, offline_op_id: offlineOpId } : { items },
    });
  }

  /** Order-level kitchen note (printed on KOT). Floor + counter. */
  async updateOrderNotes(orderId: string, notes: string): Promise<APIResponse> {
    return this.request({ method: 'PATCH', url: `/orders/${orderId}/notes`, data: { notes } });
  }

  async removeDraftItem(orderId: string, itemId: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/orders/${orderId}/items/${itemId}` });
  }

  async voidItem(
    orderId: string,
    itemId: string,
    data: { pin: string; reason: string; quantity?: number; relay_counter_print?: boolean; outcome?: 'not_made' | 'wasted'; reason_code?: string },
  ): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/orders/${orderId}/items/${itemId}/void`, data });
  }

  /**
   * Rebuild the fired kitchen ticket(s) for an order (every line already sent
   * to the kitchen, grouped by the station it was fired to). Read-only on the
   * backend — the caller decides whether to print here or resend to kitchen.
   * Errors: order_not_found, order_not_editable (cancelled), no_fired_items.
   */
  async reprintKOT(orderId: string, relayCounterPrint?: boolean): Promise<APIResponse<FireKOTResponse & { order_number?: string }>> {
    return this.request({
      method: 'POST',
      url: `/orders/${orderId}/reprint-kot`,
      data: relayCounterPrint ? { relay_counter_print: true } : undefined,
    });
  }

  // PIN verification
  async verifyPin(pin: string): Promise<APIResponse<{ valid: boolean; user_name?: string }>> {
    return this.request({ method: 'POST', url: '/verify-pin', data: { pin } });
  }

  // Kitchen Station management (admin)
  async getStations(): Promise<APIResponse<KitchenStation[]>> {
    return this.request({ method: 'GET', url: '/admin/stations' });
  }

  async createStation(data: {
    name: string;
    output_type: string;
    sort_order?: number;
    print_location?: 'kitchen' | 'counter';
    printer_target?: string;
  }): Promise<APIResponse<KitchenStation>> {
    return this.request({ method: 'POST', url: '/admin/stations', data });
  }

  async updateStation(id: string, data: Partial<KitchenStation>): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/admin/stations/${id}`, data });
  }

  async deleteStation(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/stations/${id}` });
  }

  async setStationCategories(stationId: string, categoryIds: string[]): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/admin/stations/${stationId}/categories`, data: { category_ids: categoryIds } });
  }

  async getStationCategories(stationId: string): Promise<APIResponse<string[]>> {
    return this.request({ method: 'GET', url: `/admin/stations/${stationId}/categories` });
  }

  /** Generate a canned KOT for a station so admins can verify wiring. */
  async testStationKOT(stationId: string): Promise<APIResponse<FireKOTResponse>> {
    return this.request({ method: 'POST', url: `/admin/stations/${stationId}/test-kot` });
  }

  /** Assign category to exactly one kitchen station (or clear with null). */
  async setCategoryKitchenStation(categoryId: string, stationId: string | null): Promise<APIResponse> {
    return this.request({
      method: 'PUT',
      url: `/admin/categories/${categoryId}/station`,
      data: { station_id: stationId },
    });
  }

  // PIN management (admin)
  async setUserPin(userId: string, pin: string): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/admin/users/${userId}/pin`, data: { pin } });
  }

  // Void log (admin)
  async getVoidLog(params?: {
    page?: number
    per_page?: number
    from?: string
    to?: string
    /** Legacy combined filter — matches voider OR authorizer. Prefer voided_by/authorized_by. */
    user_id?: string
    voided_by?: string
    authorized_by?: string
    reason?: string
    order_number?: string
    /** Free-text search: order number OR item name. */
    q?: string
    min_value?: number
    /** F-CTRL-N74: 'not_made' | 'wasted' | 'unclassified'. */
    outcome?: string
    reason_code?: string
  }): Promise<PaginatedResponse<VoidLogEntry[]>> {
    return this.request({ method: 'GET', url: '/admin/void-log', params });
  }

  /** Same filters as getVoidLog; describes the rows that call would return. */
  async getVoidLogSummary(params?: Parameters<APIClient['getVoidLog']>[0]): Promise<APIResponse<VoidLogSummary>> {
    return this.request({ method: 'GET', url: '/admin/void-log/summary', params });
  }

  // App Settings
  async getSetting(key: string): Promise<APIResponse<any>> {
    return this.request({ method: 'GET', url: `/settings/${key}` });
  }

  async updateSetting(key: string, value: any): Promise<APIResponse<any>> {
    return this.request({ method: 'PUT', url: `/admin/settings/${key}`, data: value });
  }

  async getAllSettings(): Promise<APIResponse<Record<string, any>>> {
    return this.request({ method: 'GET', url: '/settings' });
  }

  // WhatsApp Commerce catalog
  async getCatalogStatus(): Promise<
    APIResponse<{
      configured: boolean;
      catalog_id: string;
      auto_sync_enabled: boolean;
      available_products: number;
      catalog_ready: number;
      missing_image: number;
      feed_url: string;
    }>
  > {
    return this.request({ method: 'GET', url: '/admin/whatsapp/catalog/status' });
  }

  async syncCatalogNow(): Promise<APIResponse<{ pushed: number; skipped: number }>> {
    return this.request({ method: 'POST', url: '/admin/whatsapp/catalog/sync' });
  }

  async backfillProductImages(): Promise<
    APIResponse<{ migrated: number; failed: number; remaining: number; total: number }>
  > {
    return this.request({ method: 'POST', url: '/admin/products/migrate-images' });
  }

  async exportCatalogCsv(): Promise<void> {
    const response = await this.client.get('/admin/whatsapp/catalog/export.csv', {
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'whatsapp_catalog.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  // Configurable online payment sub-methods (Easypaisa/JazzCash/…), migration 050.
  async getOnlineSubMethods(): Promise<APIResponse<OnlineSubMethodConfig>> {
    return this.request({ method: 'GET', url: '/admin/payment-submethods' });
  }

  async updateOnlineSubMethods(
    config: OnlineSubMethodConfig,
  ): Promise<APIResponse<OnlineSubMethodConfig>> {
    return this.request({ method: 'PUT', url: '/admin/payment-submethods', data: config });
  }

  // Dynamic marketing QR codes — each row owns an immutable slug that is
  // encoded in the printed PNG. Editing target_url retroactively retargets
  // every QR already in circulation; scan analytics come from /q/:slug hits.
  async listMarketingQR(): Promise<APIResponse<MarketingQRListResponse>> {
    return this.request({ method: 'GET', url: '/admin/marketing/qr' });
  }

  async bootstrapMarketingQR(): Promise<APIResponse> {
    return this.request({ method: 'POST', url: '/admin/marketing/qr/bootstrap' });
  }

  // Optional label seeds the new row; empty body lets the backend pick
  // a "QR N" default. Returns the new slug + id; the caller refetches
  // the list to get the full row shape with style + stats.
  async createMarketingQR(
    label?: string,
  ): Promise<APIResponse<import('@/types').CreateMarketingQRResponse>> {
    return this.request({
      method: 'POST',
      url: '/admin/marketing/qr',
      data: label && label.trim() !== '' ? { label: label.trim() } : {},
    });
  }

  async deleteMarketingQR(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/marketing/qr/${id}` });
  }

  async updateMarketingQR(id: string, data: UpdateMarketingQRRequest): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/admin/marketing/qr/${id}`, data });
  }

  async getMarketingQRTimeseries(
    id: string,
    days = 30,
  ): Promise<APIResponse<MarketingQRTimeseriesResponse>> {
    return this.request({
      method: 'GET',
      url: `/admin/marketing/qr/${id}/timeseries`,
      params: { days },
    });
  }

  // Pass redirect_base = '' to clear the override and let the backend
  // fall through to MARKETING_QR_REDIRECT_BASE env / request auto-detect.
  async updateMarketingQRConfig(redirectBase: string): Promise<APIResponse> {
    return this.request({
      method: 'PUT',
      url: '/admin/marketing/qr/config',
      data: { redirect_base: redirectBase },
    });
  }

  // Fiscal (FBR/PRA) — admin only
  async getFiscalConfig(): Promise<APIResponse<FiscalConfigPublic>> {
    return this.request({ method: 'GET', url: '/admin/fiscal/config' });
  }

  async putFiscalConfig(data: {
    authority: string
    pos_id: string
    ntn: string
    is_sandbox: boolean
    strn?: string
    pntn?: string
    pos_registration_number?: string
    sfd_proxy_url?: string
    pos_post_url?: string
    fbr_validate_url?: string
    fbr_post_url?: string
    seller_business_name?: string
    seller_province?: string
    seller_address?: string
    buyer_registration_default?: string
    api_key?: string
  }): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: '/admin/fiscal/config', data });
  }

  async postFiscalTestConnection(body: { authority?: string }): Promise<APIResponse<FiscalTestConnectionResult>> {
    return this.request({ method: 'POST', url: '/admin/fiscal/test-connection', data: body });
  }

  async postFiscalDiSn002SandboxValidate(): Promise<APIResponse<FiscalDiSn002SandboxValidateResult>> {
    return this.request({
      method: 'POST',
      url: '/admin/fiscal/sn002/validate',
    });
  }

  // Fiscal credit notes. The schema retains the credit/debit union for
  // forward compatibility, but the application only exposes credit notes
  // (the FBR-approved payload uses InvoiceType=3 for credit cancellation).
  async postFiscalNote(data: CreateFiscalNoteRequest): Promise<APIResponse<FiscalNote>> {
    return this.request({ method: 'POST', url: '/admin/fiscal/notes', data });
  }

  async getFiscalNotes(params?: { order_id?: string; note_type?: string }): Promise<APIResponse<FiscalNote[]>> {
    return this.request({ method: 'GET', url: '/admin/fiscal/notes', params });
  }

  async getFiscalNote(id: string): Promise<APIResponse<FiscalNote>> {
    return this.request({ method: 'GET', url: `/admin/fiscal/notes/${id}` });
  }

  /** Order-scoped, PIN-gated full credit note. Replaces the destructive
   * "cancel a synced invoice" path: posts a credit note to FBR, marks the
   * original order cancelled_by_credit_note, and writes a fiscal_audit_event.
   *
   * Tries `/admin/orders/:id/credit-note` first (legacy path shown in older
   * bundles), then `/admin/fiscal/orders/:id/credit-note` on 404 so fiscal-
   * scoped deploys still work when there is deploy skew or CDN caching. */
  async issueOrderCreditNote(
    orderId: string,
    data: IssueCreditNoteRequest,
  ): Promise<APIResponse<FiscalNote>> {
    const urls = [
      `/admin/orders/${orderId}/credit-note`,
      `/admin/fiscal/orders/${orderId}/credit-note`,
    ] as const;
    for (let i = 0; i < urls.length; i++) {
      try {
        return await this.request<APIResponse<FiscalNote>>({
          method: 'POST',
          url: urls[i],
          data,
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : '';
        const looksLike404 =
          msg.includes('404') ||
          /\bnot\s*found\b/i.test(msg);
        if (looksLike404 && i < urls.length - 1) {
          continue;
        }
        throw e;
      }
    }
    throw new Error('Credit note request failed');
  }

  async getFiscalAuditEvents(params?: {
    order_id?: string;
    phase?: string;
    outcome?: string;
    workflow?: string;
  }): Promise<APIResponse<FiscalAuditEvent[]>> {
    return this.request({ method: 'GET', url: '/admin/fiscal/audit-events', params });
  }

  /** Live fiscal_outbound_jobs queue (submission state). status: '' / 'all' / 'dead' / 'pending' / … */
  async getFiscalJobs(status?: string): Promise<APIResponse<FiscalJob[]>> {
    return this.request({
      method: 'GET',
      url: '/admin/fiscal/jobs',
      params: status && status !== 'all' ? { status } : undefined,
    });
  }

  /** Re-queue a single dead/cancelled fiscal job (same USIN; no payload change). */
  async retryFiscalJob(id: string): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/admin/fiscal/jobs/${id}/retry` });
  }

  /** Re-queue every dead fiscal job that has no competing active job. */
  async retryAllFiscalJobs(): Promise<APIResponse<{ retried_count: number }>> {
    return this.request({ method: 'POST', url: '/admin/fiscal/jobs/retry-all' });
  }

  /**
   * PRA claims stuck in-flight — the reconciliation worklist.
   *
   * PRA is not idempotent and offers no query-by-USIN endpoint, so when a POST
   * fails ambiguously the system cannot tell whether PRA filed the invoice. It
   * parks the claim and stops rather than risk a duplicate filing on a tax
   * record. These are the orders waiting on a human to check the PRA portal.
   */
  async getPRAUnresolved(): Promise<APIResponse<PRAUnresolvedClaim[]>> {
    return this.request({ method: 'GET', url: '/admin/fiscal/pra/unresolved' });
  }

  /**
   * Close one in-flight PRA claim with an operator's verdict.
   *
   * 'found'  — the invoice IS in the PRA portal. Records its number and marks
   *            the order filed. Never resubmits.
   * 'absent' — PRA never filed it, so resubmission is safe. Releases the claim
   *            and re-queues the order (or the credit note, while it is still
   *            pending) in the same transaction — a failed re-queue leaves the
   *            claim in place and returns `requeue_failed` (F-FBR-N5).
   *            `data.requeued` says whether a job was written;
   *            `data.worker_enabled` whether this server drains the queue.
   *
   * This is the only path that turns an unknown into a resubmit, and it
   * requires someone who has actually looked.
   */
  async resolvePRAClaim(
    id: string,
    data: { action: 'found' | 'absent'; invoice_number?: string; note?: string },
  ): Promise<APIResponse<{ requeued?: boolean; worker_enabled?: boolean }>> {
    return this.request({ method: 'POST', url: `/admin/fiscal/pra/${id}/resolve`, data });
  }

  /**
   * F-CTRL-N66: "Not in portal — resubmit" for admin AND manager. Same atomic
   * release-and-requeue as resolve(absent); the backend forces the action, so
   * this route can never record a "found" declaration.
   */
  async resubmitPRAClaim(
    id: string,
    data?: { note?: string },
  ): Promise<APIResponse<{ requeued?: boolean; worker_enabled?: boolean }>> {
    return this.request({ method: 'POST', url: `/admin/fiscal/pra/${id}/resubmit`, data: data ?? {} });
  }

  /** LOCAL_BRIDGE: send the IMSFiscal localhost response back to the backend for persistence. */
  async postLocalFiscalResult(orderId: string, data: {
    invoice_number: string
    qr_code: string
    raw_response: string
    error: string
  }): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/counter/orders/${orderId}/local-fiscal-result`, data });
  }

  // ── Kitchen flow lane B: prep items, production batches, demand sheet ──
  // Every route 404s feature_not_enabled unless FEATURE_KITCHEN_FLOW=1;
  // screens call these only when useKitchenFlowEnabled() is true.

  async getPrepRecipe(stockItemId: string): Promise<APIResponse<PrepRecipe>> {
    return this.request({ method: 'GET', url: `/store/stock-items/${stockItemId}/prep-recipe` });
  }

  async putPrepRecipe(stockItemId: string, data: PrepRecipePayload): Promise<APIResponse<PrepRecipe>> {
    return this.request({ method: 'PUT', url: `/store/stock-items/${stockItemId}/prep-recipe`, data });
  }

  async getPrepIngredientOptions(): Promise<APIResponse<PrepIngredientOption[]>> {
    return this.request({ method: 'GET', url: '/store/prep-ingredient-options' });
  }

  async listProductionBatches(params?: { status?: ProductionBatchStatus[]; prep_item_id?: string; limit?: number }): Promise<APIResponse<ProductionBatch[]>> {
    return this.request({
      method: 'GET',
      url: '/store/production-batches',
      params: { ...params, status: params?.status?.join(',') || undefined },
    });
  }

  async getProductionBatch(id: string): Promise<APIResponse<{ batch: ProductionBatch; movements: ProductionBatchMovement[] }>> {
    return this.request({ method: 'GET', url: `/store/production-batches/${id}` });
  }

  /** idempotencyKey: one per tap, reused on retry, so a weak connection never plans two batches. */
  async createProductionBatch(
    data: { prep_item_id: string; planned_qty: number; location_id?: string; production_plan_id?: string; notes?: string },
    idempotencyKey: string,
  ): Promise<APIResponse<ProductionBatch>> {
    return this.request({ method: 'POST', url: '/store/production-batches', data, headers: { 'Idempotency-Key': idempotencyKey } });
  }

  async startProductionBatch(id: string): Promise<APIResponse<ProductionBatch>> {
    return this.request({ method: 'POST', url: `/store/production-batches/${id}/start`, data: {} });
  }

  async completeProductionBatch(id: string, data: { actual_yield_qty: number; expiry_date?: string; notes?: string }): Promise<APIResponse<CompleteBatchResult>> {
    return this.request({ method: 'POST', url: `/store/production-batches/${id}/complete`, data });
  }

  async cancelProductionBatch(id: string, reason?: string): Promise<APIResponse<ProductionBatch>> {
    return this.request({ method: 'POST', url: `/store/production-batches/${id}/cancel`, data: { reason } });
  }

  /** What the kitchen can make — batch size, ingredients, shelf stock; no costs (kitchen_manager's view). */
  async listKitchenPrepItems(): Promise<APIResponse<KitchenPrepItem[]>> {
    return this.request({ method: 'GET', url: '/store/production/prep-items' });
  }

  async previewDemand(lines: DemandLineInput[], locationId?: string): Promise<APIResponse<DemandPreview>> {
    return this.request({ method: 'POST', url: '/store/demand/preview', data: { lines, location_id: locationId } });
  }
  // ── end lane B ──

  // ── Kitchen flow: recipe cards on the kitchen screens (migration 148) ──
  // Reads 404 without FEATURE_KITCHEN_FLOW=1 and 403 recipe_cards_off until
  // the tenant switches them on; screens call these only when
  // useRecipeCardsEnabled() is true. A card carries no price and no cost.

  async getRecipeCardIndex(): Promise<APIResponse<RecipeCardIndex>> {
    return this.request({ method: 'GET', url: '/recipe-cards/index' });
  }

  async getRecipeCardForOrderItem(orderItemId: string): Promise<APIResponse<RecipeCard>> {
    return this.request({ method: 'GET', url: `/recipe-cards/order-items/${orderItemId}` });
  }

  /** `options` is "<option id>[:quantity],…" — see optionsQuery in lib/recipeCard/recipeScale.ts. */
  async getRecipeCardForProduct(productId: string, params?: { order_type?: string; options?: string }): Promise<APIResponse<RecipeCard>> {
    return this.request({
      method: 'GET',
      url: `/recipe-cards/products/${productId}`,
      params: { order_type: params?.order_type || undefined, options: params?.options || undefined },
    });
  }

  async getRecipeCardForPrepItem(prepItemId: string): Promise<APIResponse<RecipeCard>> {
    return this.request({ method: 'GET', url: `/recipe-cards/prep-items/${prepItemId}` });
  }

  /** A dish's method and its yield with a unit — saved on their own, never through the recipe save. */
  async getRecipeMethod(productId: string): Promise<APIResponse<RecipeMethod>> {
    return this.request({ method: 'GET', url: `/admin/products/${productId}/recipe-method` });
  }

  async putRecipeMethod(productId: string, data: RecipeMethodPayload): Promise<APIResponse<RecipeMethod>> {
    return this.request({ method: 'PUT', url: `/admin/products/${productId}/recipe-method`, data });
  }
  // ── end recipe cards ──

  // ── Kitchen flow: catering on the Kitchen Display (migration 149) ───────
  // 404 without FEATURE_KITCHEN_FLOW=1, 403 catering_on_kds_off until the
  // tenant switches it on. Display state only: never changes the plan or stock.

  async getKitchenCateringTickets(stationId?: string): Promise<APIResponse<CateringTicket[]>> {
    return this.request({ method: 'GET', url: '/kitchen/catering/tickets', params: { station_id: stationId && stationId !== 'all' ? stationId : undefined } });
  }

  async setCateringLineStatus(planId: string, lineKey: string, status: CateringLineStatus): Promise<APIResponse<CateringTicket>> {
    return this.request({ method: 'PATCH', url: `/kitchen/catering/tickets/${planId}/lines/${lineKey}/status`, data: { status } });
  }

  async bumpCateringTicket(planId: string): Promise<APIResponse<CateringTicket>> {
    return this.request({ method: 'POST', url: `/kitchen/catering/tickets/${planId}/bump`, data: {} });
  }

  async recallCateringTicket(planId: string): Promise<APIResponse<CateringTicket>> {
    return this.request({ method: 'POST', url: `/kitchen/catering/tickets/${planId}/recall`, data: {} });
  }
  // ── end catering on the KDS ──

  // ─── Kitchen flow, lane C (/inventory-flow) ─────────────────────────────
  // Every mutation takes an optional Idempotency-Key: the kitchen tablet
  // retries on a weak connection, and the server replays the first response
  // for a repeated key instead of moving stock twice.

  /** Generic mutation used by the typed wrappers and the offline queue replay. */
  async inventoryFlowMutate<T = unknown>(
    method: 'POST' | 'PUT' | 'PATCH',
    url: string,
    data?: unknown,
    idempotencyKey?: string,
  ): Promise<APIResponse<T>> {
    return this.request({
      method,
      url,
      data: data ?? {},
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    });
  }

  async getFlowLocations(): Promise<APIResponse<FlowLocation[]>> {
    return this.request({ method: 'GET', url: '/inventory-flow/locations' });
  }

  async createFlowLocation(data: { name: string; kind: string; sort_order?: number }, key?: string): Promise<APIResponse<FlowLocation>> {
    return this.inventoryFlowMutate('POST', '/inventory-flow/locations', data, key);
  }

  async updateFlowLocation(
    id: string,
    data: Partial<{ name: string; kind: string; sort_order: number; is_active: boolean; is_receiving_default: boolean; is_sale_source: boolean }>,
    key?: string,
  ): Promise<APIResponse<FlowLocation>> {
    return this.inventoryFlowMutate('PATCH', `/inventory-flow/locations/${id}`, data, key);
  }

  async getFlowItems(): Promise<APIResponse<FlowItem[]>> {
    return this.request({ method: 'GET', url: '/inventory-flow/items' });
  }

  // ── Production plans (kitchen flow, Stage 2A) ─────────────────────────────
  async getProductionPlans(params?: { status?: string; from?: string; to?: string; limit?: number }): Promise<APIResponse<ProductionPlan[]>> {
    return this.request({ method: 'GET', url: '/inventory-flow/production-plans', params });
  }

  async getProductionPlan(id: string): Promise<APIResponse<ProductionPlan>> {
    return this.request({ method: 'GET', url: `/inventory-flow/production-plans/${id}` });
  }

  /** The live sheet for dishes not yet saved. Writes nothing; no costs. */
  async previewProductionPlan(data: PlanInput): Promise<APIResponse<PlanPreview>> {
    return this.request({ method: 'POST', url: '/inventory-flow/production-plans/preview', data });
  }

  async saveProductionPlan(data: PlanInput, id: string | null, key?: string): Promise<APIResponse<ProductionPlan>> {
    return id
      ? this.inventoryFlowMutate('PUT', `/inventory-flow/production-plans/${id}`, data, key)
      : this.inventoryFlowMutate('POST', '/inventory-flow/production-plans', data, key);
  }

  async productionPlanAction(id: string, action: PlanAction, data?: unknown, key?: string): Promise<APIResponse<ProductionPlan>> {
    return this.inventoryFlowMutate('POST', `/inventory-flow/production-plans/${id}/${action}`, data, key);
  }

  /** Stage 2B: change a plan after it was made (full new dish / extras list). */
  async reviseProductionPlan(id: string, data: Pick<PlanInput, 'items' | 'extras'>, key?: string): Promise<APIResponse<{ plan: ProductionPlan; revision: PlanRevision }>> {
    return this.inventoryFlowMutate('POST', `/inventory-flow/production-plans/${id}/revise`, data, key);
  }

  /** Stage 2B: send stock the plan no longer needs back to the store. */
  async returnPlanStock(id: string, lines: { stock_item_id: string; qty: number }[], notes?: string, key?: string): Promise<APIResponse<ProductionPlan>> {
    return this.inventoryFlowMutate('POST', `/inventory-flow/production-plans/${id}/return-stock`, { lines, notes }, key);
  }

  /** Stage 2D: the after-event wrap-up sheet (or its summary once done). */
  async getPlanWrapUp(id: string): Promise<APIResponse<WrapUpSheet>> {
    return this.request({ method: 'GET', url: `/inventory-flow/production-plans/${id}/wrap-up` });
  }

  async wrapUpPlan(id: string, data: WrapUpInput, key?: string): Promise<APIResponse<ProductionPlan>> {
    return this.inventoryFlowMutate('POST', `/inventory-flow/production-plans/${id}/wrap-up`, data, key);
  }

  // ── Already-bought purchases (kitchen flow, Stage 2D; F-CTRL-N75) ─────────
  async recordMarketPurchase(data: MarketPurchaseInput, key?: string): Promise<APIResponse<MarketPurchase>> {
    return this.inventoryFlowMutate('POST', '/inventory-flow/market-purchases', data, key);
  }

  async getMarketPurchases(params?: { location_id?: string; limit?: number }): Promise<APIResponse<MarketPurchase[]>> {
    return this.request({ method: 'GET', url: '/inventory-flow/market-purchases', params });
  }

  async getMarketPurchasePhotoBlob(id: string, photoId: string): Promise<Blob> {
    const res = await this.client.get(`/inventory-flow/market-purchases/${id}/photos/${photoId}`, { responseType: 'blob' });
    return res.data as Blob;
  }

  // ── Barcodes for the whole flow (kitchen flow, Stage 2D) ─────────────────
  /** Same answer as lookupBarcode, open to the kitchen manager too. */
  async flowLookupBarcode(code: string): Promise<APIResponse<BarcodeMatch>> {
    return this.request({ method: 'GET', url: `/inventory-flow/barcodes/${encodeURIComponent(code)}` });
  }

  async linkBarcode(data: { barcode: string; stock_item_id: string; pack_qty: number; pack_label?: string }, key?: string): Promise<APIResponse<BarcodeMatch>> {
    return this.inventoryFlowMutate('POST', '/inventory-flow/barcodes', data, key);
  }

  // ── Item merges (kitchen flow, go-live; admin) ───────────────────────────
  /** Runs the whole merge and rolls it back: exactly what Merge will do. */
  async previewItemMerge(data: MergeInput): Promise<APIResponse<MergeResult>> {
    return this.request({ method: 'POST', url: '/inventory-flow/item-merges/preview', data });
  }

  async mergeItems(data: MergeInput, key?: string): Promise<APIResponse<MergeResult>> {
    return this.inventoryFlowMutate('POST', '/inventory-flow/item-merges', data, key);
  }

  async listItemMerges(limit = 100): Promise<APIResponse<ItemMergeLogRow[]>> {
    return this.request({ method: 'GET', url: '/inventory-flow/item-merges', params: { limit } });
  }

  /** The client's brand_merge_review.csv: dry run first, then apply. */
  async bulkMergeItems(file: File, dryRun: boolean, key?: string): Promise<APIResponse<MergeBulkSummary>> {
    const form = new FormData();
    form.append('file', file);
    return this.request({
      method: 'POST',
      url: '/inventory-flow/item-merges/bulk',
      params: dryRun ? { dry_run: 1 } : undefined,
      data: form,
      // The instance default is application/json, under which axios turns a
      // FormData into JSON; multipart makes it send the file (the browser
      // then sets the boundary).
      headers: { 'Content-Type': 'multipart/form-data', ...(key ? { 'Idempotency-Key': key } : {}) },
    });
  }

  // ── Catering menu + recipes import (kitchen flow, go-live; admin) ───────
  /** Dry run: every dish checked, nothing written. Import: all or nothing. */
  async importMenuRecipes(file: File, dryRun: boolean, key?: string): Promise<APIResponse<MenuImportResult>> {
    const form = new FormData();
    form.append('file', file);
    return this.request({
      method: 'POST',
      url: '/inventory-flow/menu-import',
      params: dryRun ? { dry_run: 1 } : undefined,
      data: form,
      // The instance default is application/json, under which axios turns a
      // FormData into JSON; multipart makes it send the file (the browser
      // then sets the boundary).
      headers: { 'Content-Type': 'multipart/form-data', ...(key ? { 'Idempotency-Key': key } : {}) },
    });
  }

  async downloadMenuImportTemplate(): Promise<void> {
    const response = await this.client.get('/inventory-flow/menu-import/template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'menu_recipes_template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  // ── The chef's Today screen (kitchen flow, Batch 4) ──────────────────────
  async getKitchenToday(): Promise<APIResponse<KitchenToday>> {
    return this.request({ method: 'GET', url: '/inventory-flow/kitchen-today' });
  }

  // ── Kitchen basics (kitchen flow, Stage 2D) ──────────────────────────────
  async getKitchenBasics(): Promise<APIResponse<KitchenBasic[]>> {
    return this.request({ method: 'GET', url: '/inventory-flow/kitchen-basics' });
  }

  async setKitchenBasic(itemId: string, data: { is_kitchen_basic: boolean; min_qty: number; max_qty?: number | null }, key?: string): Promise<APIResponse<KitchenBasic[]>> {
    return this.inventoryFlowMutate('PUT', `/inventory-flow/kitchen-basics/${itemId}`, data, key);
  }

  async requestKitchenBasics(lines: { stock_item_id: string; qty: number }[], key?: string): Promise<APIResponse<Requisition>> {
    return this.inventoryFlowMutate('POST', '/inventory-flow/kitchen-basics/request', { lines }, key);
  }

  async pushKitchenBasics(lines: { stock_item_id: string; qty: number }[], key?: string): Promise<APIResponse<Requisition>> {
    return this.inventoryFlowMutate('POST', '/inventory-flow/kitchen-basics/push', { lines }, key);
  }

  // ── Catering bookings (kitchen flow, Stage 2B) ───────────────────────────
  async getCateringBookings(params?: { from?: string; to?: string; status?: string; unpaid?: 1; limit?: number }): Promise<APIResponse<CateringBooking[]>> {
    return this.request({ method: 'GET', url: '/inventory-flow/catering/bookings', params });
  }

  async getCateringBooking(id: string): Promise<APIResponse<CateringBooking>> {
    return this.request({ method: 'GET', url: `/inventory-flow/catering/bookings/${id}` });
  }

  async createCateringBooking(data: BookingInput, key?: string): Promise<APIResponse<CateringBooking>> {
    return this.inventoryFlowMutate('POST', '/inventory-flow/catering/bookings', data, key);
  }

  async updateCateringBooking(id: string, data: BookingInput, key?: string): Promise<APIResponse<BookingUpdateResult>> {
    return this.inventoryFlowMutate('PUT', `/inventory-flow/catering/bookings/${id}`, data, key);
  }

  async cateringBookingAction(id: string, action: BookingAction, reason?: string, key?: string): Promise<APIResponse<CateringBooking>> {
    return this.inventoryFlowMutate('POST', `/inventory-flow/catering/bookings/${id}/${action}`, reason ? { reason } : {}, key);
  }

  async addBookingPayment(id: string, data: PaymentInput, key?: string): Promise<APIResponse<CateringBooking>> {
    return this.inventoryFlowMutate('POST', `/inventory-flow/catering/bookings/${id}/payments`, data, key);
  }

  async voidBookingPayment(id: string, paymentId: string, reason: string, key?: string): Promise<APIResponse<CateringBooking>> {
    return this.inventoryFlowMutate('POST', `/inventory-flow/catering/bookings/${id}/payments/${paymentId}/void`, { reason }, key);
  }

  /** Batch 5: the equipment double-booking warning for a booking being
   *  written (nothing is saved). */
  async checkCateringEquipment(data: { event_date: string; booking_id?: string; extras: { stock_item_id: string; qty: number }[] }): Promise<APIResponse<EquipmentClash[]>> {
    return this.request({ method: 'POST', url: '/inventory-flow/catering/equipment-check', data });
  }

  async sendBookingToKitchen(id: string, key?: string): Promise<APIResponse<ProductionPlan>> {
    return this.inventoryFlowMutate('POST', `/inventory-flow/catering/bookings/${id}/plan`, {}, key);
  }

  async getCateringPaymentsReport(params?: { from?: string; to?: string }): Promise<APIResponse<CateringPaymentsReport>> {
    return this.request({ method: 'GET', url: '/inventory-flow/catering/payments-report', params });
  }

  /** Downloads the branded invoice (a receipt once fully paid). */
  async downloadCateringInvoice(id: string): Promise<void> {
    const response = await this.client.get(`/inventory-flow/catering/bookings/${id}/invoice.pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = response.headers['content-disposition'] as string | undefined;
    const match = cd && /filename="?([^"]+)"?/i.exec(cd);
    a.download = match ? match[1] : 'catering_invoice.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  }

  // ── Waste log (kitchen flow, Stage 2C) ───────────────────────────────────
  async recordWaste(data: WasteInput, key?: string): Promise<APIResponse<WasteEntry>> {
    return this.inventoryFlowMutate('POST', '/inventory-flow/waste', data, key);
  }

  async getWasteLog(params?: { from?: string; to?: string; location_id?: string; reason?: string; limit?: number }): Promise<APIResponse<WasteLog>> {
    return this.request({ method: 'GET', url: '/inventory-flow/waste', params });
  }

  async getWastePhotoBlob(id: string): Promise<Blob> {
    const res = await this.client.get(`/inventory-flow/waste/${id}/photo`, { responseType: 'blob' });
    return res.data as Blob;
  }

  /** Open batches of an item at a place (kitchen by default), earliest expiry first. */
  async getItemLotsAt(itemId: string, locationId?: string): Promise<APIResponse<StockLotsResponse>> {
    return this.request({ method: 'GET', url: `/inventory-flow/items/${itemId}/lots`, params: locationId ? { location_id: locationId } : undefined });
  }

  /** include_open: the newest `limit` plus every request still open, however old (Batch 5). */
  async getRequisitions(params?: { status?: string; kind?: 'issue' | 'return'; limit?: number; include_open?: boolean }): Promise<APIResponse<Requisition[]>> {
    const { include_open, ...rest } = params ?? {};
    return this.request({ method: 'GET', url: '/inventory-flow/requisitions', params: include_open ? { ...rest, include_open: 1 } : rest });
  }

  async getRequisitionSummary(): Promise<APIResponse<RequisitionSummary>> {
    return this.request({ method: 'GET', url: '/inventory-flow/requisitions/summary' });
  }

  async getRequisition(id: string): Promise<APIResponse<Requisition>> {
    return this.request({ method: 'GET', url: `/inventory-flow/requisitions/${id}` });
  }

  async getRequisitionShortfall(id: string): Promise<APIResponse<{ requisition_id: string; kind: string; suppliers: ShortfallGroup[] }>> {
    return this.request({ method: 'GET', url: `/inventory-flow/requisitions/${id}/shortfall` });
  }

  async createRequisition(data: CreateRequisitionBody, key?: string): Promise<APIResponse<Requisition>> {
    return this.inventoryFlowMutate('POST', '/inventory-flow/requisitions', data, key);
  }

  async requisitionAction(
    id: string,
    action: 'submit' | 'cancel' | 'approve' | 'reject' | 'issue' | 'receive' | 'approve-and-issue',
    data?: unknown,
    key?: string,
  ): Promise<APIResponse<Requisition>> {
    return this.inventoryFlowMutate('POST', `/inventory-flow/requisitions/${id}/${action}`, data, key);
  }

  async requisitionLineAction(
    id: string,
    lineId: string,
    action: 'close-short' | 'write-off',
    data: unknown,
    key?: string,
  ): Promise<APIResponse<Requisition>> {
    return this.inventoryFlowMutate('POST', `/inventory-flow/requisitions/${id}/lines/${lineId}/${action}`, data, key);
  }

  // ── Kitchen flow Stage 3 — owner visibility ──────────────────────────────
  // Count sessions per location, owner reports, document timelines and the
  // flow dashboard. Every route is behind FEATURE_KITCHEN_FLOW.

  async getCountSessions(params?: { location_id?: string; status?: string; limit?: number }): Promise<APIResponse<CountSession[]>> {
    return this.request({ method: 'GET', url: '/inventory-flow/count-sessions', params });
  }

  async getCountSession(id: string): Promise<APIResponse<CountSession>> {
    return this.request({ method: 'GET', url: `/inventory-flow/count-sessions/${id}` });
  }

  async createCountSession(data: { location_id: string; all_items?: boolean; notes?: string }, key?: string): Promise<APIResponse<CountSession>> {
    return this.inventoryFlowMutate('POST', '/inventory-flow/count-sessions', data, key);
  }

  /** counted_seconds_ago: how long before this call the line was entered, so a
   *  late (queued) autosave keeps the moment the shelf was counted. */
  async saveCountLines(
    id: string,
    lines: { stock_item_id: string; counted_qty: number | null; counted_seconds_ago?: number }[],
    key?: string,
  ): Promise<APIResponse<CountSession>> {
    return this.inventoryFlowMutate('PUT', `/inventory-flow/count-sessions/${id}/lines`, { lines }, key);
  }

  async countSessionAction(
    id: string,
    action: 'submit' | 'cancel' | 'approve' | 'reopen',
    key?: string,
    body?: { note?: string },
  ): Promise<APIResponse<CountSession>> {
    return this.inventoryFlowMutate('POST', `/inventory-flow/count-sessions/${id}/${action}`, body ?? {}, key);
  }

  /**
   * Single-item count at a named location (kitchen flow on). The book the
   * preview was computed against must be that location's balance, or the
   * stale-preview guard answers 409 book_quantity_changed.
   */
  async recordStockCountAt(
    itemId: string,
    data: { counted_quantity: number; note?: string; expected_book_quantity: number; location_id: string },
  ): Promise<APIResponse<{ variance: number; applied: boolean }>> {
    return this.request({ method: 'POST', url: `/store/stock-items/${itemId}/count`, data });
  }

  // Owner reports. from/to are business dates (YYYY-MM-DD).
  async getFlowFillRate(from: string, to: string): Promise<APIResponse<FillRateReport>> {
    return this.request({ method: 'GET', url: '/inventory-flow/reports/fill-rate', params: { from, to } });
  }

  async getFlowTransitVariance(from: string, to: string): Promise<APIResponse<TransitVarianceReport>> {
    return this.request({ method: 'GET', url: '/inventory-flow/reports/transit-variance', params: { from, to } });
  }

  async getFlowAvT(from: string, to: string, locationId?: string): Promise<APIResponse<AvTReport>> {
    return this.request({ method: 'GET', url: '/inventory-flow/reports/avt', params: { from, to, location_id: locationId } });
  }

  async getFlowYield(from: string, to: string): Promise<APIResponse<YieldReport>> {
    return this.request({ method: 'GET', url: '/inventory-flow/reports/yield', params: { from, to } });
  }

  async getFlowStockByLocation(params?: { usage_days?: number; slow_days?: number; expiry_days?: number }): Promise<APIResponse<StockByLocationReport>> {
    return this.request({ method: 'GET', url: '/inventory-flow/reports/stock-by-location', params });
  }

  async getFlowPurchasing(from: string, to: string): Promise<APIResponse<PurchasingReport>> {
    return this.request({ method: 'GET', url: '/inventory-flow/reports/purchasing', params: { from, to } });
  }

  async getProductionPlanTimeline(id: string): Promise<APIResponse<DocumentTimeline>> {
    return this.request({ method: 'GET', url: `/inventory-flow/production-plans/${id}/timeline` });
  }

  async getPurchaseOrderTimeline(id: string): Promise<APIResponse<DocumentTimeline>> {
    return this.request({ method: 'GET', url: `/inventory-flow/purchase-orders/${id}/timeline` });
  }

  async getFlowSummary(): Promise<APIResponse<FlowSummary>> {
    return this.request({ method: 'GET', url: '/inventory-flow/flow-summary' });
  }

  // Utility methods
  setAuthToken(token: string): void {
    localStorage.setItem('pos_token', token);
  }

  /** Writes both the JWT and the user object — mirrors what /login does. */
  setAuth(token: string, user: User): void {
    localStorage.setItem('pos_token', token);
    localStorage.setItem('pos_user', JSON.stringify(user));
  }

  clearAuth(): void {
    localStorage.removeItem('pos_token');
    localStorage.removeItem('pos_user');
    // A rider signing out on the Android app must stop sharing location
    // (F-DLV-N3). No-op everywhere else.
    nativeStopRiderTracking();
  }

  getAuthToken(): string | null {
    return localStorage.getItem('pos_token');
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }
}

// Create and export a singleton instance
export const apiClient = new APIClient();
export default apiClient;

